// ─── Practice Data ───
export type PracticeType = "GP Practice" | "Dentist" | "Pharmacy";
export type AcceptingStatus = "yes" | "no" | "limited";
export type NhsStatus = "nhs" | "private" | "mixed";

export interface Practice {
  id: string;
  name: string;
  type: PracticeType;
  nhsStatus: NhsStatus;
  odsCode?: string;
  services: string[];
  borough: string;
  postcode: string;
  address: string;
  phone: string;
  acceptingNewPatients: AcceptingStatus;
  description: string;
  openingHours: string;
  rating: number;
  reviews: number;
  distance: number;
  featured?: boolean;
  eveningWeekend?: boolean;
  wheelchairAccessible?: boolean;
  privatePay?: boolean;
  outOfAreaRegistrations?: boolean;
  languages?: string[];
  restricted?: boolean;
}

export const LANGUAGE_OPTIONS = [
  "English",
  "Polish",
  "Urdu",
  "Bengali",
  "Gujarati",
  "Punjabi",
  "Arabic",
  "Somali",
  "Tamil",
  "French",
];

export const PRACTICES: Practice[] = [
  // ─── LONDON PHARMACIES (59) ───
  {
    id: "40",
    name: "111 Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Harrow",
    postcode: "HA2 8SW",
    address: "136 Carlyon Avenue, South Harrow, London, HA2 8SW",
    phone: "020 3793 8417",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in South Harrow offering prescription services, health advice and over-the-counter medicines.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.2,
    reviews: 18,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Urdu"],
  },
  {
    id: "41",
    name: "A Moore & Co",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Homeopathic Medicines", "Health Advice", "Private Services"],
    borough: "Westminster",
    postcode: "SW1X 9JF",
    address: "25E Lowndes Street, London, SW1X 9JF",
    phone: "020 7235 5887",
    acceptingNewPatients: "yes",
    description: "Established pharmacy in Belgravia providing prescription dispensing, homeopathic preparations and private health services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:30-13:00, Sun closed",
    rating: 4.5,
    reviews: 24,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French"],
  },
  {
    id: "42",
    name: "A R Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Southwark",
    postcode: "SE1 5NA",
    address: "199 Old Kent Road, London, SE1 5NA",
    phone: "020 7703 4097",
    acceptingNewPatients: "yes",
    description: "Friendly pharmacy on Old Kent Road serving the local Southwark community with prescription and health services.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.9,
    reviews: 12,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali"],
  },
  {
    id: "43",
    name: "Access Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Flu Vaccinations", "Health Advice"],
    borough: "Greenwich",
    postcode: "SE2 9BB",
    address: "38 Grovebury Road, Abbey Wood, London, SE2 9BB",
    phone: "020 8310 6482",
    acceptingNewPatients: "yes",
    description: "Local pharmacy in Abbey Wood offering NHS prescriptions, seasonal vaccinations and general health advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 9,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "44",
    name: "Acheason Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Blood Pressure Monitoring"],
    borough: "Waltham Forest",
    postcode: "E11 4HH",
    address: "273 High Road, Leytonstone, London, E11 4HH",
    phone: "020 8534 3154",
    acceptingNewPatients: "yes",
    description: "Well-established pharmacy on Leytonstone High Road providing prescription services and health monitoring.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:30, Sun closed",
    rating: 4.1,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
  },
  {
    id: "45",
    name: "Acorn Pharmacy Mill Hill",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Flu Vaccinations", "Health Advice"],
    borough: "Barnet",
    postcode: "NW7 3JR",
    address: "641 Watford Way, Mill Hill, London, NW7 3JR",
    phone: "020 8959 3520",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Mill Hill offering comprehensive prescription services, flu vaccinations and wellbeing advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.3,
    reviews: 21,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "46",
    name: "Adarshi Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Lambeth",
    postcode: "SE27 9DJ",
    address: "485 Norwood Road, London, SE27 9DJ",
    phone: "020 8670 0973",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in West Norwood providing prescription dispensing and health advice services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 3.8,
    reviews: 8,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Tamil"],
  },
  {
    id: "47",
    name: "Abbey Pharmacy Merton",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Merton",
    postcode: "SW19 1DG",
    address: "12A Abbey Parade, Merton High Street, London, SW19 1DG",
    phone: "020 8542 3713",
    acceptingNewPatients: "yes",
    description: "Established pharmacy on Merton High Street providing NHS prescription services and seasonal flu jabs.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-17:00, Sun closed",
    rating: 4.4,
    reviews: 27,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Urdu"],
  },
  {
    id: "48",
    name: "Abbott Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Haringey",
    postcode: "N10 1LR",
    address: "101 Colney Hatch Lane, Muswell Hill, London, N10 1LR",
    phone: "020 8883 3960",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Muswell Hill offering prescription dispensing and over-the-counter remedies.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 14,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "49",
    name: "Ainsworth Homeopathic Pharmacy",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Homeopathic Medicines", "Health Advice", "Private Services"],
    borough: "Westminster",
    postcode: "W1G 8UF",
    address: "36 New Cavendish Street, London, W1G 8UF",
    phone: "020 7935 5330",
    acceptingNewPatients: "yes",
    description: "Specialist homeopathic pharmacy in Marylebone offering traditional and homeopathic medicines alongside standard prescriptions.",
    openingHours: "Mon-Fri 09:30-17:30, Sat 10:00-14:00, Sun closed",
    rating: 4.6,
    reviews: 42,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French", "Arabic"],
  },
  {
    id: "50",
    name: "Akro Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Newham",
    postcode: "E7 8NP",
    address: "404 Katherine Road, Forest Gate, London, E7 8NP",
    phone: "020 8472 0461",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Forest Gate serving a diverse population with prescription and health services.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.9,
    reviews: 11,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Urdu"],
  },
  {
    id: "51",
    name: "Akshar Pharmacies Walthamstow",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Blood Pressure Monitoring"],
    borough: "Waltham Forest",
    postcode: "E17 5BZ",
    address: "8A Rushbrook Crescent, Walthamstow, London, E17 5BZ",
    phone: "020 8527 5443",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Walthamstow offering prescription dispensing, blood pressure checks and general health guidance.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.1,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati"],
  },
  {
    id: "52",
    name: "Ali's Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Tower Hamlets",
    postcode: "E1 2QE",
    address: "93 Watney Street, Stepney, London, E1 2QE",
    phone: "020 7790 9150",
    acceptingNewPatients: "yes",
    description: "Friendly neighbourhood pharmacy in Stepney providing prescription services and health advice to the local community.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Somali"],
  },
  {
    id: "53",
    name: "Alisha Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Flu Vaccinations", "Health Advice"],
    borough: "Hounslow",
    postcode: "W4 5DG",
    address: "257 Acton Lane, Chiswick, London, W4 5DG",
    phone: "020 8994 5177",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Chiswick offering NHS prescriptions, seasonal vaccinations and wellness consultations.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-14:00, Sun closed",
    rating: 4.3,
    reviews: 19,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi"],
  },
  {
    id: "54",
    name: "Allchins & Co Chemist",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Private Services"],
    borough: "Camden",
    postcode: "NW3 4UE",
    address: "28 Englands Lane, London, NW3 4UE",
    phone: "020 7722 1095",
    acceptingNewPatients: "yes",
    description: "Traditional pharmacy in Belsize Park providing prescription dispensing, health advice and private healthcare products.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-17:00, Sun closed",
    rating: 4.5,
    reviews: 33,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French"],
  },
  {
    id: "55",
    name: "Allcures Pharmacy Tottenham",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Haringey",
    postcode: "N15 3PB",
    address: "331 West Green Road, Tottenham, London, N15 3PB",
    phone: "020 8888 3040",
    acceptingNewPatients: "yes",
    description: "Community pharmacy on West Green Road offering prescription services and health advice.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.8,
    reviews: 7,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali", "Arabic"],
  },
  {
    id: "56",
    name: "Allen Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Blood Pressure Monitoring", "Health Advice"],
    borough: "Hackney",
    postcode: "N16 9PA",
    address: "150 Albion Road, Stoke Newington, London, N16 9PA",
    phone: "020 7254 2500",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Stoke Newington providing prescription services, blood pressure monitoring and wellness advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.2,
    reviews: 20,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "57",
    name: "Allens Pharmacy South Woodford",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Redbridge",
    postcode: "E18 2LY",
    address: "19 Electric Parade, George Lane, South Woodford, London, E18 2LY",
    phone: "020 8989 3353",
    acceptingNewPatients: "yes",
    description: "Friendly pharmacy in South Woodford offering prescription dispensing and health advice services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.4,
    reviews: 25,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "58",
    name: "Allen's Pharmacy Isle of Dogs",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Tower Hamlets",
    postcode: "E14 3PQ",
    address: "19 Castalia Square, Isle of Dogs, London, E14 3PQ",
    phone: "020 7987 1362",
    acceptingNewPatients: "yes",
    description: "Local pharmacy on the Isle of Dogs providing essential prescription and health services to residents.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 13,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali"],
  },
  {
    id: "59",
    name: "Atkins Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Islington",
    postcode: "N7 8JE",
    address: "124 Holloway Road, London, N7 8JE",
    phone: "020 7609 0281",
    acceptingNewPatients: "yes",
    description: "Pharmacy on Holloway Road providing prescription dispensing and health services to the Islington community.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.9,
    reviews: 11,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic"],
  },
  {
    id: "60",
    name: "Atkinsons Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Enfield",
    postcode: "N21 3RE",
    address: "750 Green Lanes, Winchmore Hill, London, N21 3RE",
    phone: "020 8360 1037",
    acceptingNewPatients: "yes",
    description: "Traditional chemist in Winchmore Hill offering prescription services, seasonal flu jabs and health products.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.5,
    reviews: 31,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "61",
    name: "Ashburton Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Wandsworth",
    postcode: "SW15 6HG",
    address: "30 Chartfield Avenue, Roehampton, London, SW15 6HG",
    phone: "020 8785 1083",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Roehampton providing prescription dispensing and health advice services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.1,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "62",
    name: "Asvacare Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Hackney",
    postcode: "E5 9BU",
    address: "97 Upper Clapton Road, Upper Clapton, London, E5 9BU",
    phone: "020 8806 5140",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Upper Clapton serving the local Hackney community with prescription and health services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 3.7,
    reviews: 6,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali"],
  },
  {
    id: "63",
    name: "Bonamy Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Southwark",
    postcode: "SE16 3HF",
    address: "355 Rotherhithe New Road, Bermondsey, London, SE16 3HF",
    phone: "020 7740 2523",
    acceptingNewPatients: "yes",
    description: "Bermondsey pharmacy providing prescription dispensing, flu vaccinations and health consultations.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.2,
    reviews: 17,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "71",
    name: "Oakleigh Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Barnet",
    postcode: "N20 0TX",
    address: "253 Oakleigh Road North, Whetstone, London, N20 0TX",
    phone: "020 8445 3276",
    acceptingNewPatients: "yes",
    description: "Local independent pharmacy in Whetstone providing prescription dispensing and health products.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.2,
    reviews: 18,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "76",
    name: "John Bell & Croyden",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations", "Blood Pressure Monitoring"],
    borough: "Westminster",
    postcode: "W1G 9PF",
    address: "50-54 Wigmore Street, London, W1G 9PF",
    phone: "020 7935 5555",
    acceptingNewPatients: "yes",
    description: "Historic independent pharmacy since 1798, offering premium prescriptions, health screenings and specialist advice.",
    openingHours: "Mon-Fri 08:30-19:00, Sat 09:30-18:00, Sun closed",
    rating: 4.7,
    reviews: 58,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French", "Arabic"],
  },
  {
    id: "79",
    name: "Boutalls Pharmacy",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Private Services"],
    borough: "Camden",
    postcode: "WC1N 3LW",
    address: "60 Lambs Conduit Street, London, WC1N 3LW",
    phone: "020 7405 1039",
    acceptingNewPatients: "yes",
    description: "Charming independent pharmacy on Lambs Conduit Street offering prescriptions and personalised health advice.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:30-14:00, Sun closed",
    rating: 4.6,
    reviews: 39,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "80",
    name: "Boss Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Lambeth",
    postcode: "SW4 7RD",
    address: "85 Bedford Road, London, SW4 7RD",
    phone: "020 7326 7470",
    acceptingNewPatients: "yes",
    description: "Local pharmacy in Clapham providing prescription services and health products.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.0,
    reviews: 12,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "81",
    name: "Bower Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Waltham Forest",
    postcode: "E11 4EA",
    address: "340 Grove Green Road, Leytonstone, London, E11 4EA",
    phone: "020 8539 1922",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Leytonstone offering prescription dispensing and essential health services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.1,
    reviews: 14,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
  },
  {
    id: "82",
    name: "Bramley Pharmacy North Kensington",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Kensington and Chelsea",
    postcode: "W10 6TJ",
    address: "132 Bramley Road, London, W10 6TJ",
    phone: "020 8969 0053",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in North Kensington offering prescription services and community health support.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic"],
  },
  {
    id: "83",
    name: "Bramley Pharmacy Ealing",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Flu Vaccinations", "Health Advice"],
    borough: "Ealing",
    postcode: "W5 4UA",
    address: "261 Northfield Avenue, Ealing, London, W5 4UA",
    phone: "020 8840 4464",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Ealing providing prescription services, seasonal vaccinations and health products.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.3,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi"],
  },
  {
    id: "84",
    name: "Brentmead Chemists",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Brent",
    postcode: "NW10 9NH",
    address: "136-138 Church Road, Willesden, London, NW10 9NH",
    phone: "020 8459 3147",
    acceptingNewPatients: "yes",
    description: "Long-standing pharmacy in Willesden providing prescription dispensing and health advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-17:00, Sun closed",
    rating: 4.0,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Urdu"],
  },
  {
    id: "85",
    name: "Britannia Pharmacy Poplar",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Tower Hamlets",
    postcode: "E14 0XA",
    address: "10 Aberfeldy Square, Poplar, London, E14 0XA",
    phone: "020 7537 3319",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Poplar serving local residents with prescription and health services.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.8,
    reviews: 9,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Somali"],
  },
  {
    id: "86",
    name: "Broadwick Pharmacy",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Private Services"],
    borough: "Westminster",
    postcode: "W1F 1FG",
    address: "18 Broadwick Street, London, W1F 1FG",
    phone: "020 7437 3846",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in the heart of Soho offering prescription services and private health consultations.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 10:00-16:00, Sun closed",
    rating: 4.3,
    reviews: 26,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "87",
    name: "Broadway Pharmacy Muswell Hill",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Haringey",
    postcode: "N10 3RS",
    address: "185 Muswell Hill Broadway, Muswell Hill, London, N10 3RS",
    phone: "020 8883 5564",
    acceptingNewPatients: "yes",
    description: "Pharmacy on Muswell Hill Broadway providing prescriptions, vaccinations and health advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-17:00, Sun closed",
    rating: 4.4,
    reviews: 29,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "88",
    name: "Clockwork Pharmacy Caledonian Road",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Islington",
    postcode: "N1 0SL",
    address: "161 Caledonian Road, Islington, London, N1 0SL",
    phone: "020 7837 5753",
    acceptingNewPatients: "yes",
    description: "Modern pharmacy on Caledonian Road offering prescription services and health products.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 4.0,
    reviews: 13,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "89",
    name: "Clan Pharmacy Islington",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Blood Pressure Monitoring"],
    borough: "Islington",
    postcode: "N1 1RA",
    address: "150 Upper Street, Islington, London, N1 1RA",
    phone: "020 7359 7595",
    acceptingNewPatients: "yes",
    description: "Pharmacy on Upper Street providing prescription dispensing, health monitoring and wellness advice.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-18:00, Sun closed",
    rating: 4.2,
    reviews: 21,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },

  {
    id: "691",
    name: "Wainwright Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Camden",
    postcode: "WC1N 3LW",
    address: "64 Lamb's Conduit Street, London, WC1N 3LW",
    phone: "020 7405 6351",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "692",
    name: "Holborn Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Camden",
    postcode: "WC1R 4PD",
    address: "60-62 Red Lion Street, London, WC1R 4PD",
    phone: "020 7405 1688",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "693",
    name: "Day Lewis Pharmacy Whitechapel",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Tower Hamlets",
    postcode: "E1 1BJ",
    address: "174 Whitechapel Road, London, E1 1BJ",
    phone: "020 7247 1879",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "694",
    name: "Penton Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Islington",
    postcode: "N1 9PS",
    address: "14 Penton Street, London, N1 9PS",
    phone: "020 7837 0552",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "695",
    name: "Warman Freed Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Barnet",
    postcode: "NW11 8EL",
    address: "45 Golders Green Road, London, NW11 8EL",
    phone: "020 8455 4351",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "696",
    name: "Portobello Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Kensington and Chelsea",
    postcode: "W11 2QB",
    address: "79 Portobello Road, London, W11 2QB",
    phone: "020 7727 8080",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "697",
    name: "Clapham Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Lambeth",
    postcode: "SW4 7SS",
    address: "143 Clapham High Street, London, SW4 7SS",
    phone: "020 7622 1371",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "698",
    name: "Greenlight Pharmacy Bermondsey",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Southwark",
    postcode: "SE16 3RS",
    address: "12 Southwark Park Road, London, SE16 3RS",
    phone: "020 7237 1659",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "699",
    name: "Streatham Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Lambeth",
    postcode: "SW16 1HJ",
    address: "95 Streatham High Road, London, SW16 1HJ",
    phone: "020 8769 1131",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "700",
    name: "Finchley Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Barnet",
    postcode: "N3 2BG",
    address: "2 Ballards Lane, London, N3 2BG",
    phone: "020 8346 0494",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "701",
    name: "Lewisham Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Lewisham",
    postcode: "SE13 6BG",
    address: "25 Lewis Grove, London, SE13 6BG",
    phone: "020 8852 2673",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "702",
    name: "Willesden Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Brent",
    postcode: "NW10 2SB",
    address: "117 High Road, London, NW10 2SB",
    phone: "020 8459 1370",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "703",
    name: "Ealing Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Ealing",
    postcode: "W5 2NP",
    address: "34 The Broadway, London, W5 2NP",
    phone: "020 8567 1234",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "704",
    name: "Wood Green Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Haringey",
    postcode: "N22 6BB",
    address: "125 High Road, Wood Green, London, N22 6BB",
    phone: "020 8888 2022",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "705",
    name: "Hackney Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Hackney",
    postcode: "E8 3QE",
    address: "215 Mare Street, London, E8 3QE",
    phone: "020 8985 1132",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "706",
    name: "Bethnal Green Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Tower Hamlets",
    postcode: "E2 6LG",
    address: "357 Bethnal Green Road, London, E2 6LG",
    phone: "020 7739 1369",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "707",
    name: "Tooting Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Wandsworth",
    postcode: "SW17 7TR",
    address: "35 Upper Tooting Road, London, SW17 7TR",
    phone: "020 8672 1479",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "708",
    name: "Dalston Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Hackney",
    postcode: "E8 1NG",
    address: "98 Dalston Lane, London, E8 1NG",
    phone: "020 7254 3547",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "709",
    name: "Fulham Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Hammersmith and Fulham",
    postcode: "SW6 5RP",
    address: "600 Fulham Road, London, SW6 5RP",
    phone: "020 7736 8551",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "710",
    name: "Walthamstow Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Waltham Forest",
    postcode: "E17 9PT",
    address: "247 Hoe Street, London, E17 9PT",
    phone: "020 8520 5462",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "711",
    name: "Brixton Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Lambeth",
    postcode: "SW9 8HA",
    address: "439 Brixton Road, London, SW9 8HA",
    phone: "020 7733 5116",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "712",
    name: "Islington Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Islington",
    postcode: "N1 2TZ",
    address: "289 Upper Street, London, N1 2TZ",
    phone: "020 7226 1081",
    acceptingNewPatients: "yes",
    description: "Community pharmacy providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  // ─── BIRMINGHAM PHARMACIES (25) ───
  {
    id: "90",
    name: "ARK Healthcare",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Sparkhill",
    postcode: "B11 4AN",
    address: "566-568 Stratford Road, Sparkhill, Birmingham, B11 4AN",
    phone: "0121 772 7381",
    acceptingNewPatients: "yes",
    description: "Pharmacy on Stratford Road in Sparkhill serving the local community with prescription and health services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.0,
    reviews: 14,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "91",
    name: "AG Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Acocks Green",
    postcode: "B27 6BP",
    address: "Units 5-6, 1160 Warwick Road, Acocks Green, Birmingham, B27 6BP",
    phone: "0121 706 0374",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Acocks Green providing prescriptions, vaccinations and health advice.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.1,
    reviews: 11,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
  },
  {
    id: "92",
    name: "A+ Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Stechford",
    postcode: "B33 8QF",
    address: "311 Bordesley Green East, Stechford, Birmingham, B33 8QF",
    phone: "0121 624 6010",
    acceptingNewPatients: "yes",
    description: "Pharmacy in Stechford offering prescription dispensing and essential health services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 3.9,
    reviews: 8,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
  },
  {
    id: "93",
    name: "Allcare Pharmacy Small Heath",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Small Heath",
    postcode: "B10 0UU",
    address: "674 Coventry Road, Small Heath, Birmingham, B10 0UU",
    phone: "0121 772 3108",
    acceptingNewPatients: "yes",
    description: "Pharmacy on Coventry Road in Small Heath serving the local community with prescription services.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.8,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Urdu"],
  },
  {
    id: "94",
    name: "Ashtree Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Stirchley",
    postcode: "B30 2NW",
    address: "1534 Pershore Road, Stirchley, Birmingham, B30 2NW",
    phone: "0121 459 6023",
    acceptingNewPatients: "yes",
    description: "Pharmacy on Pershore Road in Stirchley providing prescriptions, seasonal vaccinations and health products.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.2,
    reviews: 19,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "95",
    name: "Asif's Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Alum Rock",
    postcode: "B8 1JA",
    address: "Alvi House, 38D Alum Rock Road, Birmingham, B8 1JA",
    phone: "0121 328 2320",
    acceptingNewPatients: "yes",
    description: "Community pharmacy on Alum Rock Road offering prescription dispensing and health advice.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.9,
    reviews: 7,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "96",
    name: "Askers Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Kings Heath",
    postcode: "B14 6AA",
    address: "146 Alcester Road South, Kings Heath, Birmingham, B14 6AA",
    phone: "0121 444 6234",
    acceptingNewPatients: "yes",
    description: "Traditional chemist in Kings Heath providing prescription services and health products.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.3,
    reviews: 24,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "97",
    name: "Aston Chemist",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Selly Oak",
    postcode: "B29 4HH",
    address: "4 Shenley Green, Shenley Lane, Selly Oak, Birmingham, B29 4HH",
    phone: "0121 475 4293",
    acceptingNewPatients: "yes",
    description: "Neighbourhood pharmacy in Selly Oak serving students and residents with prescription services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
  },
  {
    id: "99",
    name: "Click4Prescriptions",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Delivery Service"],
    borough: "Handsworth",
    postcode: "B21 9NH",
    address: "2 Trafalgar Road, Handsworth, Birmingham, B21 9NH",
    phone: "0121 554 3150",
    acceptingNewPatients: "yes",
    description: "Modern pharmacy in Handsworth offering prescription services with delivery options.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 3.8,
    reviews: 9,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "100",
    name: "Co-Chem Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Handsworth",
    postcode: "B19 1HJ",
    address: "136 Heathfield Road, Handsworth, Birmingham, B19 1HJ",
    phone: "0121 554 8125",
    acceptingNewPatients: "yes",
    description: "Local pharmacy in Handsworth providing prescription dispensing and health advice services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 3.7,
    reviews: 6,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi"],
  },
  {
    id: "101",
    name: "Clock Pharmacy Northfield",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Northfield",
    postcode: "B31 2PA",
    address: "891 Bristol Road South, Northfield, Birmingham, B31 2PA",
    phone: "0121 477 4646",
    acceptingNewPatients: "yes",
    description: "Pharmacy on Bristol Road South in Northfield offering prescriptions, vaccinations and health services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.1,
    reviews: 18,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "102",
    name: "Great Barr Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Great Barr",
    postcode: "B43 7EX",
    address: "624 Walsall Road, Great Barr, Birmingham, B43 7EX",
    phone: "0121 357 1462",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in Great Barr serving the local community with prescriptions and vaccinations.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.2,
    reviews: 19,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "103",
    name: "Perry Barr Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Perry Barr",
    postcode: "B42 1TQ",
    address: "750 Walsall Road, Perry Barr, Birmingham, B42 1TQ",
    phone: "0121 356 2087",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Perry Barr providing prescription services and health advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 14,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "104",
    name: "Sycamore Pharmacy Northfield",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Northfield",
    postcode: "B31 2JT",
    address: "37 Church Road, Northfield, Birmingham, B31 2JT",
    phone: "0121 475 1928",
    acceptingNewPatients: "yes",
    description: "Family-run pharmacy in Northfield providing prescriptions, vaccinations and health products.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.3,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "105",
    name: "Greenhill Pharmacy Kings Norton",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Kings Norton",
    postcode: "B38 8RU",
    address: "6 The Green, Kings Norton, Birmingham, B38 8RU",
    phone: "0121 459 3215",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy on the historic Kings Norton Green providing prescription services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.1,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "106",
    name: "Highfield Pharmacy Kings Heath",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Kings Heath",
    postcode: "B14 7JG",
    address: "186 Alcester Road, Kings Heath, Birmingham, B14 7JG",
    phone: "0121 444 3782",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy on Alcester Road in Kings Heath offering prescriptions and health advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.4,
    reviews: 25,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "107",
    name: "Ashfurlong Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Sutton Coldfield",
    postcode: "B75 6DX",
    address: "233 Tamworth Road, Sutton Coldfield, B75 6DX",
    phone: "0121 323 4300",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Sutton Coldfield serving local residents with prescription and health services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.4,
    reviews: 23,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "108",
    name: "Walmley Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Sutton Coldfield",
    postcode: "B76 1NR",
    address: "1 Walmley Road, Walmley, Sutton Coldfield, B76 1NR",
    phone: "0121 351 2740",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in Walmley serving local families with prescription and health services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.3,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "109",
    name: "Mere Green Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Sutton Coldfield",
    postcode: "B75 5BL",
    address: "41 Mere Green Road, Sutton Coldfield, B75 5BL",
    phone: "0121 308 1254",
    acceptingNewPatients: "yes",
    description: "Friendly independent pharmacy at Mere Green serving the Sutton Coldfield community.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.4,
    reviews: 21,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "110",
    name: "Parade Pharmacy Sutton Coldfield",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Sutton Coldfield",
    postcode: "B72 1PH",
    address: "2 The Parade, Sutton Coldfield, B72 1PH",
    phone: "0121 354 3087",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in Sutton Coldfield town centre offering prescriptions and vaccinations.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-17:00, Sun closed",
    rating: 4.2,
    reviews: 27,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "111",
    name: "Boldmere Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Sutton Coldfield",
    postcode: "B73 5UY",
    address: "52 Boldmere Road, Sutton Coldfield, B73 5UY",
    phone: "0121 354 1893",
    acceptingNewPatients: "yes",
    description: "Community pharmacy on Boldmere Road providing prescriptions and health products.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.1,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "112",
    name: "Cape Hill Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Smethwick",
    postcode: "B66 4SH",
    address: "153 Cape Hill, Smethwick, B66 4SH",
    phone: "0121 558 0724",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy on Cape Hill in Smethwick offering prescriptions and health advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.0,
    reviews: 13,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi"],
  },
  {
    id: "113",
    name: "Ashworth Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Smethwick",
    postcode: "B67 6NX",
    address: "211A Queens Road, Smethwick, B67 6NX",
    phone: "0121 552 1132",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Smethwick providing prescription dispensing and health services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
  },
  {
    id: "114",
    name: "Brandhall Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Blood Pressure Monitoring"],
    borough: "Oldbury",
    postcode: "B68 0RT",
    address: "Warley Medical Centre, Ambrose House, Kingsway, Oldbury, B68 0RT",
    phone: "0121 423 3713",
    acceptingNewPatients: "yes",
    description: "Pharmacy within Warley Medical Centre in Oldbury offering prescriptions and health monitoring.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.1,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi"],
  },

  // ─── MANCHESTER PHARMACIES (10) ───
  {
    id: "115",
    name: "A & A Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Rusholme",
    postcode: "M14 5AL",
    address: "58 Wilmslow Road, Rusholme, Manchester, M14 5AL",
    phone: "0161 224 8501",
    acceptingNewPatients: "yes",
    description: "Pharmacy on the famous Wilmslow Road in Rusholme serving students and residents.",
    openingHours: "Mon-Fri 09:00-19:00, Sat 09:00-17:00, Sun closed",
    rating: 3.9,
    reviews: 11,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Arabic"],
  },
  {
    id: "116",
    name: "Allied Pharmacy Five Oaks",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Beswick",
    postcode: "M11 3BE",
    address: "400 Alan Turing Way, Beswick, Manchester, M11 3BE",
    phone: "0161 223 0112",
    acceptingNewPatients: "yes",
    description: "Modern pharmacy near the Etihad Stadium offering prescriptions, vaccinations and health services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.1,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "117",
    name: "Harpurhey Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Harpurhey",
    postcode: "M9 4DH",
    address: "197 Rochdale Road, Harpurhey, Manchester, M9 4DH",
    phone: "0161 205 3920",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in Harpurhey providing prescriptions and health advice to the local community.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.0,
    reviews: 12,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "118",
    name: "Longsight Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Longsight",
    postcode: "M12 4QH",
    address: "564 Stockport Road, Longsight, Manchester, M12 4QH",
    phone: "0161 224 4190",
    acceptingNewPatients: "yes",
    description: "Community pharmacy on Stockport Road in Longsight providing prescriptions and health services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.1,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
  },
  {
    id: "119",
    name: "Whitworth Pharmacy",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations", "Blood Pressure Monitoring"],
    borough: "Manchester City Centre",
    postcode: "M1 3WS",
    address: "93 Oxford Road, Manchester, M1 3WS",
    phone: "0161 236 1445",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy on Oxford Road serving students and city centre residents with prescriptions and health services.",
    openingHours: "Mon-Fri 08:30-19:00, Sat 09:00-17:00, Sun closed",
    rating: 4.2,
    reviews: 36,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "120",
    name: "Chorlton Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Chorlton",
    postcode: "M21 9NJ",
    address: "509 Barlow Moor Road, Chorlton, Manchester, M21 9NJ",
    phone: "0161 861 0292",
    acceptingNewPatients: "yes",
    description: "Well-loved independent pharmacy in Chorlton offering prescriptions, vaccinations and personalised health advice.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.5,
    reviews: 28,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "121",
    name: "Didsbury Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Didsbury",
    postcode: "M20 6RD",
    address: "742 Wilmslow Road, Didsbury, Manchester, M20 6RD",
    phone: "0161 445 2312",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in Didsbury village providing prescriptions and health products.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.3,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "122",
    name: "Bowland Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Baguley",
    postcode: "M23 1JX",
    address: "52 Bowland Road, Baguley, Manchester, M23 1JX",
    phone: "0161 998 7114",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Baguley providing prescription services and health advice.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.2,
    reviews: 14,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "123",
    name: "Bowness Pharmacy Little Hulton",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Little Hulton",
    postcode: "M38 9RQ",
    address: "210 Cleggs Lane, Little Hulton, Manchester, M38 9RQ",
    phone: "0161 703 7575",
    acceptingNewPatients: "yes",
    description: "Local pharmacy in Little Hulton serving the community with prescription and health services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "124",
    name: "Cohens Chemist Collyhurst",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Collyhurst",
    postcode: "M40 7QH",
    address: "1 Whitley Road, Collyhurst, Manchester, M40 7QH",
    phone: "0161 205 1451",
    acceptingNewPatients: "yes",
    description: "Cohens chemist in Collyhurst providing prescription services, vaccinations and health products.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 3.9,
    reviews: 8,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },

  // ─── BRISTOL PHARMACIES (5) ───
  {
    id: "125",
    name: "Air Balloon Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "St George",
    postcode: "BS5 7PD",
    address: "Air Balloon Surgery, St George, Bristol, BS5 7PD",
    phone: "0117 955 549",
    acceptingNewPatients: "yes",
    description: "Pharmacy located within Air Balloon Surgery in St George, providing prescriptions and vaccinations.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.3,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "126",
    name: "Ashgrove Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Blood Pressure Monitoring"],
    borough: "Horfield",
    postcode: "BS7 9JT",
    address: "97-99 Ashley Down Road, Horfield, Bristol, BS7 9JT",
    phone: "0117 924 8510",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy in Horfield offering prescriptions, health monitoring and wellness services.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-14:00, Sun closed",
    rating: 4.4,
    reviews: 28,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "127",
    name: "Kingswood Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Kingswood",
    postcode: "BS15 4AB",
    address: "78 High Street, Kingswood, Bristol, BS15 4AB",
    phone: "0117 967 2450",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy on Kingswood High Street providing prescriptions, vaccinations and health services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.2,
    reviews: 20,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "128",
    name: "Bristol Pharmacy Stockwood",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Stockwood",
    postcode: "BS14 8PG",
    address: "74-76 Hollway Road, Stockwood, Bristol, BS14 8PG",
    phone: "01275 833862",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Stockwood providing prescription dispensing and health products.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "129",
    name: "Clifton Village Pharmacy",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Private Services"],
    borough: "Clifton",
    postcode: "BS8 4BP",
    address: "10 Princess Victoria Street, Clifton, Bristol, BS8 4BP",
    phone: "0117 973 8087",
    acceptingNewPatients: "yes",
    description: "Elegant pharmacy in Clifton Village offering prescription services and private health consultations.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-14:00, Sun closed",
    rating: 4.6,
    reviews: 35,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },

  // ─── NEWCASTLE PHARMACIES (5) ───
  {
    id: "130",
    name: "Ashchem Chemists",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Slatyford",
    postcode: "NE5 2UY",
    address: "Denton Turret Medical Centre, 10 Kenley Road, Slatyford, Newcastle upon Tyne, NE5 2UY",
    phone: "0191 274 5653",
    acceptingNewPatients: "yes",
    description: "Pharmacy within Denton Turret Medical Centre in Slatyford, Newcastle, providing prescription services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 12,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "131",
    name: "Byker Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Byker",
    postcode: "NE6 2HQ",
    address: "253 Shields Road, Byker, Newcastle upon Tyne, NE6 2HQ",
    phone: "0191 265 7320",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy on Shields Road in Byker serving the local community with prescriptions and vaccinations.",
    openingHours: "Mon-Fri 09:00-18:30, Sat 09:00-13:00, Sun closed",
    rating: 4.1,
    reviews: 14,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "132",
    name: "Gosforth Pharmacy",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations"],
    borough: "Gosforth",
    postcode: "NE3 1HB",
    address: "106 High Street, Gosforth, Newcastle upon Tyne, NE3 1HB",
    phone: "0191 285 3847",
    acceptingNewPatients: "yes",
    description: "Independent pharmacy on Gosforth High Street offering prescriptions and health services.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.4,
    reviews: 24,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "133",
    name: "Pilgrim Street Pharmacy",
    type: "Pharmacy",
    nhsStatus: "mixed",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice", "Flu Vaccinations", "Blood Pressure Monitoring"],
    borough: "Newcastle City Centre",
    postcode: "NE1 6QE",
    address: "55 Pilgrim Street, Newcastle upon Tyne, NE1 6QE",
    phone: "0191 232 4781",
    acceptingNewPatients: "yes",
    description: "City centre independent pharmacy providing prescriptions, health monitoring and vaccinations.",
    openingHours: "Mon-Fri 08:30-18:30, Sat 09:00-17:00, Sun closed",
    rating: 4.2,
    reviews: 31,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: true,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "134",
    name: "Cockerton Pharmacy Fenham",
    type: "Pharmacy",
    nhsStatus: "nhs",
    services: ["Prescriptions", "Over-the-Counter Medicines", "Health Advice"],
    borough: "Fenham",
    postcode: "NE4 5JP",
    address: "141 Beaconsfield Street, Newcastle upon Tyne, NE4 5JP",
    phone: "0191 273 3030",
    acceptingNewPatients: "yes",
    description: "Community pharmacy in Fenham, Newcastle, offering prescription dispensing and health advice.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.0,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  // ─── GP Practices (from NHS data) ───
  {
    id: "135",
    name: "The Practice Albert Road",
    type: "GP Practice",
    nhsStatus: "nhs",
        services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Health Checks", "Referrals"],
    borough: "Newham",
    postcode: "E16 2DY",
    address: "Kennard Street Health Centre, 76 Albert Road, London, E16 2DY",
    phone: "020 81042222",
    acceptingNewPatients: "yes",
    description: "The Practice Albert Road is a medical facility located within the Kennard Street Health Centre, providing general medical services to the local community in east London.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 11,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Bengali", "Romanian"],
  },
  {
    id: "136",
    name: "Church Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E84067",
    services: ["General Practice", "Immunisations", "Minor Illness Treatment", "Repeat Prescriptions", "Family Planning"],
    borough: "Brent",
    postcode: "NW9 8LU",
    address: "282 Church Lane, Kingsbury, London, NW9 8LU",
    phone: "020 82000077",
    acceptingNewPatients: "yes",
    description: "Church Lane Surgery provides primary healthcare services to patients in the Kingsbury area of London, focusing on accessible and comprehensive medical care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 39,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Hindi"],
  },
  {
    id: "137",
    name: "Arcadian Gardens Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F85034",
    services: ["General Practice", "Maternity Services", "Child Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Haringey",
    postcode: "N22 5AB",
    address: "1 Arcadian Gardens, Wood Green, London, N22 5AB",
    phone: "020 88884142",
    acceptingNewPatients: "yes",
    description: "Arcadian Gardens Surgery serves the Wood Green community, offering a range of general practice services in a convenient London location.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 21,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish", "Albanian"],
  },
  {
    id: "138",
    name: "Wimbledon Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H85028",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Minor Surgery", "Health & Wellbeing Advice"],
    borough: "Merton",
    postcode: "SW19 1NX",
    address: "Patrick Doody Clinic Health Centre, 79 Pelham Road, Wimbledon, London, SW19 1NX",
    phone: "020 85422827",
    acceptingNewPatients: "yes",
    description: "Wimbledon Medical Practice is situated within the Patrick Doody Clinic Health Centre, providing essential healthcare services to residents of Wimbledon.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 77,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish", "Tamil"],
  },
  {
    id: "139",
    name: "Holland Park Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87016",
    services: ["General Practice", "Medical Examinations", "Prescription Services", "Immunizations", "Preventative Care"],
    borough: "Kensington and Chelsea",
    postcode: "W8 7RX",
    address: "Lower Ground Floor, 12 Phillimore Walk, London, W8 7RX",
    phone: "020 72214334",
    acceptingNewPatients: "yes",
    description: "Holland Park Surgery offers general medical services from its discreet location on Phillimore Walk, catering to the local community in Holland Park.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 62,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "French", "Spanish"],
  },
  {
    id: "140",
    name: "Handsworth Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F86004",
    services: ["General Practice", "Women's Health", "Men's Health", "Antenatal Care", "Smoking Cessation"],
    borough: "Waltham Forest",
    postcode: "E4 9PD",
    address: "Handsworth Avenue, Highams Park, London, E4 9PD",
    phone: "020 30069216",
    acceptingNewPatients: "yes",
    description: "Handsworth Medical Practice is located in Highams Park and delivers primary healthcare to its patients, focusing on community health and well-being.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.5,
    reviews: 19,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Bengali"],
  },
  {
    id: "141",
    name: "Gray's Inn Medical Group Chancery Lane",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83042",
    services: ["General Practice", "Travel Advice", "Sexual Health", "Occupational Health", "Cryotherapy"],
    borough: "Camden",
    postcode: "WC1X 8TS",
    address: "77 Gray's Inn Road, London, WC1X 8TS",
    phone: "020 74059360",
    acceptingNewPatients: "yes",
    description: "Gray's Inn Medical Group Chancery Lane provides comprehensive general practice services in a central London location, serving residents and workers.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 72,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Italian", "French"],
  },
  {
    id: "142",
    name: "Crawford Street Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87070",
    services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Family Planning", "Referrals"],
    borough: "Westminster",
    postcode: "W1H 2HJ",
    address: "95-97 Crawford Street, London, W1H 2HJ",
    phone: "020 77236324",
    acceptingNewPatients: "yes",
    description: "Crawford Street Surgery offers general medical services in the Marylebone area of London, committed to providing accessible patient care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 79,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Arabic", "Somali"],
  },
  {
    id: "143",
    name: "Willesden Green Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E84702",
    services: ["General Practice", "Adult Immunisations", "Child Health Surveillance", "Minor Injuries", "Health Promotion"],
    borough: "Brent",
    postcode: "NW2 3UY",
    address: "59 Anson Road, London, NW2 3UY",
    phone: "020 82084141",
    acceptingNewPatients: "yes",
    description: "Willesden Green Surgery provides primary healthcare services to the Willesden Green community, focusing on personalised and proactive care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 77,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Romanian"],
  },
  {
    id: "144",
    name: "Chiswick Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85130",
    services: ["General Practice", "Child Immunisation", "Women's Health", "Elderly Care", "Mental Health Support"],
    borough: "Ealing",
    postcode: "W3 8TF",
    address: "Unit A, Kelton House, Corbet Gardens, London, W3 8TF",
    phone: "020 89956707",
    acceptingNewPatients: "yes",
    description: "Chiswick Family Practice is located within Kelton House, offering a range of family-focused GP services to the Chiswick community in London.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 65,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Hindi"],
  },
  {
    id: "145",
    name: "The Vale Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85635",
    services: ["General Practice", "Long Term Conditions", "Preventative Medicine", "Smear Tests", "Online Consultations"],
    borough: "Ealing",
    postcode: "W3 7RG",
    address: "97 the Vale, Acton, London, W3 7RG",
    phone: "020 87434086",
    acceptingNewPatients: "yes",
    description: "The Vale Surgery serves the Acton area, providing general medical care and a commitment to patient well-being.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 8,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali", "Urdu"],
  },
  {
    id: "146",
    name: "The Corner Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85127",
    services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Health Checks", "Minor Ailments"],
    borough: "Southwark",
    postcode: "SE5 9NS",
    address: "99 Coldharbour Lane, London, SE5 9NS",
    phone: "020 72744507",
    acceptingNewPatients: "yes",
    description: "The Corner Surgery is a local general practice providing essential healthcare services to the community in the Camberwell area.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 28,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Yoruba"],
  },
  {
    id: "147",
    name: "Canberra Old Oak Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02906",
    services: ["General Practice", "Child Immunisations", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Hammersmith and Fulham",
    postcode: "W12 7FG",
    address: "Parkview Cfh, Cranston Ct, 56 Bloemfontein Road, White City, London, W12 7FG",
    phone: "020 33139010",
    acceptingNewPatients: "yes",
    description: "Canberra Old Oak Surgery, located within Parkview CFH, offers primary medical care to residents in the White City area of West London.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 51,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Arabic", "Polish"],
  },
  {
    id: "148",
    name: "The Lawson Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84096",
    services: ["General Practice", "Chronic Disease Management", "Antenatal Care", "Contraception", "Minor Surgery"],
    borough: "Hackney",
    postcode: "N1 5HZ",
    address: "St Leonards, 85 Nuttall St, London, N1 5HZ",
    phone: "020 35386044",
    acceptingNewPatients: "yes",
    description: "The Lawson Practice, based at St Leonards, delivers comprehensive general practitioner services to patients in the N1 area of London.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 35,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish", "Bengali"],
  },
  {
    id: "149",
    name: "Robin Hood Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G84029",
    services: ["General Practice", "Vaccinations", "Health Screening", "Referrals", "Repeat Prescriptions"],
    borough: "Bromley",
    postcode: "SE20 7AB",
    address: "94 Croydon Road, Anerley, London, SE20 7AB",
    phone: "020 87788651",
    acceptingNewPatients: "yes",
    description: "Robin Hood Surgery provides general medical care to the residents of Anerley, focusing on accessible and patient-centred services.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 51,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Yoruba"],
  },
  {
    id: "150",
    name: "Auckland Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H83037",
    services: ["General Practice", "Child Health", "Immunisations", "Chronic Disease Management", "Travel Vaccinations"],
    borough: "Croydon",
    postcode: "SE19 2DF",
    address: "84a Auckland Road, Upper Norwood, London, SE19 2DF",
    phone: "020 86535146",
    acceptingNewPatients: "yes",
    description: "Auckland Surgery serves the Upper Norwood community, offering a range of primary healthcare services from its local premises.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 56,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Romanian", "Arabic"],
  },
  {
    id: "151",
    name: "Ashville Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85719",
    services: ["General Practice", "Vaccinations", "Health Checks", "Women's Health", "Mental Health Support"],
    borough: "Hammersmith and Fulham",
    postcode: "SW6 4HS",
    address: "Swan House, Parsons Green Lane, Fulham, London, SW6 4HS",
    phone: "020 73717171",
    acceptingNewPatients: "yes",
    description: "Ashville Surgery is located within Swan House in Fulham, providing general medical services to the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 52,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "French", "Spanish"],
  },
  {
    id: "152",
    name: "Chalfont Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F85682",
    services: ["General Practice", "Chronic Disease Management", "Child Health Surveillance", "Immunisations", "Health Promotion"],
    borough: "Enfield",
    postcode: "N9 0TW",
    address: "Evergreen Primary Care Centre, 1 Smythe Close, London, N9 0TW",
    phone: "020 88074505",
    acceptingNewPatients: "yes",
    description: "Chalfont Surgery, operating from Evergreen Primary Care Centre, delivers comprehensive general practice services to the Enfield N9 area.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 13,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish", "Polish"],
  },
  {
    id: "153",
    name: "Grafton Medical Partners",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02423",
    services: ["General Practice", "Antenatal Care", "Childhood Immunisations", "Travel Vaccinations", "Minor Surgery"],
    borough: "Wandsworth",
    postcode: "SW17 0RT",
    address: "Trevelyan House, 160 Tooting High Street, London, SW17 0RT",
    phone: "02038 835700",
    acceptingNewPatients: "yes",
    description: "Grafton Medical Partners is situated at Trevelyan House on Tooting High Street, offering a partnership of general practitioners for integrated patient care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 76,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Tamil", "Polish"],
  },
  {
    id: "154",
    name: "Grove Park Terrace Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85746",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screening", "Women's Health"],
    borough: "Hounslow",
    postcode: "W4 3JL",
    address: "25 Grove Park Terrace, Chiswick, London, W4 3JL",
    phone: "020 86301680",
    acceptingNewPatients: "yes",
    description: "Grove Park Terrace Surgery serves the Chiswick area, providing general medical care with a focus on family health.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.7,
    reviews: 56,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Italian", "German"],
  },
  {
    id: "155",
    name: "Swiss Cottage Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83665",
    services: ["General Practice", "Travel Vaccinations", "Sexual Health Services", "Mental Health Support", "Health Assessments"],
    borough: "Camden",
    postcode: "NW3 3NP",
    address: "2 Winchester Mews, Swiss Cottage, London, NW3 3NP",
    phone: "020 77222772",
    acceptingNewPatients: "yes",
    description: "Swiss Cottage Surgery offers general medical services in the Swiss Cottage area, providing convenient healthcare to the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 45,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "French"],
  },
  {
    id: "156",
    name: "The Lyndhurst Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F86088",
    services: ["General Practice", "Child Health", "Immunisations", "Diabetes Management", "Asthma Reviews"],
    borough: "Waltham Forest",
    postcode: "E10 6JB",
    address: "53 Lyndhurst Drive, Leyton, London, E10 6JB",
    phone: "020 85391663",
    acceptingNewPatients: "yes",
    description: "The Lyndhurst Surgery provides general practitioner services to residents in Leyton, committed to local community healthcare.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 54,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Bengali"],
  },
  {
    id: "157",
    name: "Arnos Grove Medical Centr",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F85700",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Checks", "Family Planning"],
    borough: "Enfield",
    postcode: "N11 1BD",
    address: "269 Bowes Road, New Southgate, London, N11 1BD",
    phone: "020 83684455",
    acceptingNewPatients: "yes",
    description: "Arnos Grove Medical Centre provides comprehensive primary healthcare services to the New Southgate area.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Greek", "Turkish"],
  },
  {
    id: "158",
    name: "The Florence Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85122",
    services: ["General Practice", "Travel Vaccinations", "Women's Health", "Minor Injuries", "Health Advice"],
    borough: "Ealing",
    postcode: "W5 3TX",
    address: "26 Florence Road, Ealing, London, W5 3TX",
    phone: "020 85672111",
    acceptingNewPatients: "yes",
    description: "The Florence Road Surgery offers general medical services to the Ealing community, providing patient-focused care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 37,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali", "Polish"],
  },
  {
    id: "159",
    name: "Fountayne Road Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84080",
    services: ["General Practice", "Child Health", "Chronic Disease Management", "Immunisations", "Mental Health Support"],
    borough: "Hackney",
    postcode: "N16 7EA",
    address: "1a Fountayne Road, London, N16 7EA",
    phone: "020 39280777",
    acceptingNewPatients: "yes",
    description: "Fountayne Road Medical Practice serves the N16 area of London, offering general medical services within the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 18,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Yiddish", "Polish"],
  },
  {
    id: "160",
    name: "Vesta Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85105",
    services: ["General Practice", "Vaccinations", "Health Checks", "Chronic Disease Management", "Family Planning"],
    borough: "Lewisham",
    postcode: "SE4 2NH",
    address: "58 Vesta Road, London, SE4 2NH",
    phone: "020 76390654",
    acceptingNewPatients: "yes",
    description: "Vesta Road Surgery provides general medical care to the Brockley area, focusing on accessible and quality healthcare services.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 20,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Yoruba"],
  },
  {
    id: "161",
    name: "The Ridgeway Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F86078",
    services: ["General Practice", "Women's Health", "Child Health", "Travel Vaccinations", "Asthma Management"],
    borough: "Waltham Forest",
    postcode: "E4 7JX",
    address: "1 Mount Echo Avenue, Chingford, London, E4 7JX",
    phone: "020 85237722",
    acceptingNewPatients: "yes",
    description: "The Ridgeway Surgery offers primary healthcare services to the Chingford area, committed to supporting the health of its patients.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 66,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish", "Albanian"],
  },
  {
    id: "162",
    name: "Triangle Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85120",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Antenatal & Postnatal Care", "Minor Surgery"],
    borough: "Lewisham",
    postcode: "SE13 6DQ",
    address: "2 Morley Road, Lewisham, London, SE13 6DQ",
    phone: "020 83187272",
    acceptingNewPatients: "yes",
    description: "Triangle Group Practice provides a range of general medical services to the Lewisham community, focusing on comprehensive patient care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 54,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Yoruba", "Portuguese"],
  },
  {
    id: "163",
    name: "Ritchie Street Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83021",
    services: ["General Practice", "Vaccinations", "Health Checks", "Family Planning", "Mental Health Support"],
    borough: "Islington",
    postcode: "N1 0DG",
    address: "34 Ritchie Street, London, N1 0DG",
    phone: "020 78371663",
    acceptingNewPatients: "yes",
    description: "Ritchie Street Group Practice offers general medical services in the Islington N1 area, dedicated to community health.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.7,
    reviews: 53,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Spanish", "Turkish"],
  },
  {
    id: "164",
    name: "Grafton Square Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85674",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Women's Health", "Child Health"],
    borough: "Lambeth",
    postcode: "SW4 0DE",
    address: "8b Grafton Square, London, SW4 0DE",
    phone: "020 76225642",
    acceptingNewPatients: "yes",
    description: "Grafton Square Surgery provides primary healthcare services to the Clapham community, focusing on accessible and friendly care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 42,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Portuguese", "French"],
  },
  {
    id: "165",
    name: "Shoreditch Park Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84635",
    services: ["General Practice", "Immunisations", "Health Checks", "Chronic Disease Management", "Mental Health Support"],
    borough: "Hackney",
    postcode: "N1 5DR",
    address: "6-10 Rushton Street, London, N1 5DR",
    phone: "020 77398525",
    acceptingNewPatients: "yes",
    description: "Shoreditch Park Surgery offers comprehensive general practice services to the community near Shoreditch Park.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 17,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Turkish"],
  },
  {
    id: "166",
    name: "The Law Medical Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E84006",
    services: ["General Practice", "Child Health", "Vaccinations", "Women's Health", "Travel Advice"],
    borough: "Brent",
    postcode: "NW10 5UY",
    address: "Wrottesley Road Surgery, 9-11 Wrottesley Road, London, NW10 5UY",
    phone: "020 38188441",
    acceptingNewPatients: "yes",
    description: "The Law Medical Group Practice, located at Wrottesley Road Surgery, provides general medical care to patients in Harlesden.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 29,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Urdu"],
  },
  {
    id: "167",
    name: "Hurley and Riverside Practices",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85053",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Mental Health Support", "Antenatal Care"],
    borough: "Lambeth",
    postcode: "SE11 4HJ",
    address: "Ebenezer House, Kennington Lane, London, SE11 4HJ",
    phone: "020 77357918",
    acceptingNewPatients: "yes",
    description: "Hurley and Riverside Practices, based at Ebenezer House, serve the Kennington Lane area with a focus on integrated healthcare.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 39,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Yoruba"],
  },
  {
    id: "168",
    name: "Addison Road Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F86607",
    services: ["General Practice", "Immunisations", "Chronic Disease Management", "Family Planning", "Health Checks"],
    borough: "Waltham Forest",
    postcode: "E17 9LY",
    address: "46 Ravenswood Road, Walthamstow, London, E17 9LY",
    phone: "02081 769110",
    acceptingNewPatients: "yes",
    description: "Addison Road Medical Practice provides primary healthcare services to the Walthamstow area, with a commitment to patient well-being.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.7,
    reviews: 56,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Bengali"],
  },
  {
    id: "169",
    name: "Dr Cm Patel's Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84660",
    services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Blood Tests", "Referrals"],
    borough: "Newham",
    postcode: "E7 8LZ",
    address: "2 Jephson Road, Forest Gate, London, E7 8LZ",
    phone: "020 84706429",
    acceptingNewPatients: "yes",
    description: "Dr C M Patel's Surgery offers general medical services in Forest Gate, serving the local diverse community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 79,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Hindi"],
  },
  {
    id: "171",
    name: "Newham Transitional Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84740",
    services: ["General Practice", "Mental Health Support", "Substance Misuse Support", "Homeless Healthcare", "Referrals"],
    borough: "Newham",
    postcode: "E12 6AQ",
    address: "30 Church Road, the Centre, Manor Park, London, E12 6AQ",
    phone: "020 85537460",
    acceptingNewPatients: "yes",
    description: "Newham Transitional Practice, located at The Centre in Manor Park, provides specific medical services for transitional patient needs.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Romanian"],
  },
  {
    id: "172",
    name: "De Beauvoir Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84072",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Women's Health", "Health Checks"],
    borough: "Hackney",
    postcode: "N1 5QT",
    address: "30 Hertford Road, London, N1 5QT",
    phone: "020 79233684",
    acceptingNewPatients: "yes",
    description: "De Beauvoir Surgery offers general medical services in the De Beauvoir Town area, serving the local community with primary care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 12,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish", "Portuguese"],
  },
  {
    id: "173",
    name: "Gower Street Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83005",
    services: ["General Practice", "Travel Vaccinations", "Sexual Health", "Mental Health Support", "Workplace Health"],
    borough: "Camden",
    postcode: "WC1E 6DP",
    address: "20 Gower Street, London, WC1E 6DP",
    phone: "020 76367628",
    acceptingNewPatients: "yes",
    description: "Gower Street Practice provides comprehensive general medical services in central London, catering to residents and professionals.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 59,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Mandarin", "French"],
  },
  {
    id: "174",
    name: "Paxton Green Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85039",
    services: ["General Practice", "Chronic Disease Management", "Child Health", "Mental Health Support", "Minor Surgery"],
    borough: "Lambeth",
    postcode: "SE21 8AU",
    address: "Paxton Green Health Centre, 1 Alleyn Park, London, SE21 8AU",
    phone: "020 86706878",
    acceptingNewPatients: "yes",
    description: "Paxton Green Group Practice is based at Paxton Green Health Centre, offering a wide range of primary care services to the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 35,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Yoruba"],
  },
  {
    id: "175",
    name: "Cheshire Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F85640",
    services: ["General Practice", "Vaccinations", "Health Checks", "Repeat Prescriptions", "Referrals"],
    borough: "Haringey",
    postcode: "N22 8JJ",
    address: "22 Cheshire Road, Wood Green, London, N22 8JJ",
    phone: "020 88888378",
    acceptingNewPatients: "yes",
    description: "Cheshire Road Surgery serves the Wood Green area, offering general medical services with a community focus.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 80,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish", "Albanian"],
  },
  {
    id: "176",
    name: "Northfields Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85014",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Women's Health", "Travel Advice"],
    borough: "Ealing",
    postcode: "W13 9QP",
    address: "61 Northfield Avenue, Ealing, London, W13 9QP",
    phone: "020 85671612",
    acceptingNewPatients: "yes",
    description: "Northfields Surgery provides primary healthcare services to the Ealing community, offering accessible and patient-centred care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 48,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Punjabi"],
  },
  {
    id: "177",
    name: "Nexus Health Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85034",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Sexual Health"],
    borough: "Southwark",
    postcode: "SE1 6JP",
    address: "Princess Street Group Practice, 2 Princess Street, London, SE1 6JP",
    phone: "02079 283299",
    acceptingNewPatients: "yes",
    description: "Nexus Health Group, including Princess Street Group Practice, provides comprehensive general medical services in the Borough area of London.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 71,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Spanish"],
  },
  {
    id: "178",
    name: "The Manor Park Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84729",
    services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Child Health", "Women's Health"],
    borough: "Newham",
    postcode: "E12 5JG",
    address: "778 Romford Road, London, E12 5JG",
    phone: "020 84780533",
    acceptingNewPatients: "yes",
    description: "The Manor Park Practice offers general medical services on Romford Road, serving the diverse community of Manor Park.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 66,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Urdu"],
  },
  {
    id: "179",
    name: "Highbury Grange Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83660",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Family Planning", "Mental Health Support"],
    borough: "Islington",
    postcode: "N5 2QB",
    address: "1-5 Highbury Grange, London, N5 2QB",
    phone: "020 72262462",
    acceptingNewPatients: "yes",
    description: "Highbury Grange Medical Practice provides primary healthcare services in the Highbury area, committed to patient well-being.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.7,
    reviews: 25,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Portuguese"],
  },
  {
    id: "180",
    name: "Barton House Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84008",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Chronic Disease Management"],
    borough: "Hackney",
    postcode: "N16 9JT",
    address: "233 Albion Road, Stoke Newington, London, N16 9JT",
    phone: "020 72495511",
    acceptingNewPatients: "yes",
    description: "Barton House Group Practice serves the Stoke Newington area, offering a range of general medical services to the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 79,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish", "Yiddish"],
  },
  {
    id: "181",
    name: "Wells Park Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85114",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Checks", "Minor Illness Treatment"],
    borough: "Lewisham",
    postcode: "SE26 6JQ",
    address: "1 Wells Park Road, Sydenham, London, SE26 6JQ",
    phone: "020 86992840",
    acceptingNewPatients: "yes",
    description: "Wells Park Practice provides general medical care in Sydenham, focusing on preventative medicine and patient support.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 82,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Yoruba", "Portuguese"],
  },
  {
    id: "182",
    name: "Southdene Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F86066",
    services: ["General Practice", "Immunisations", "Child Health", "Women's Health", "Diabetes Care"],
    borough: "Redbridge",
    postcode: "E18 1BD",
    address: "The Shrubberies, George Lane, South Woodford, London, E18 1BD",
    phone: "020 85303731",
    acceptingNewPatients: "yes",
    description: "Southdene Surgery, located at The Shrubberies, offers general medical services to the South Woodford community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 82,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Bengali"],
  },
  {
    id: "183",
    name: "The Bush Doctors",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85055",
    services: ["General Practice", "Walk-in Appointments", "Minor Injuries", "Prescription Services", "Health Advice"],
    borough: "Hammersmith and Fulham",
    postcode: "W12 8PP",
    address: "16-17 West 12 Shopping Centre, Shepherds Bush Green, London, W12 8PP",
    phone: "020 87491882",
    acceptingNewPatients: "yes",
    description: "The Bush Doctors provides general medical services from within the West 12 Shopping Centre, offering convenient care in Shepherds Bush Green.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 36,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Arabic", "Portuguese"],
  },
  {
    id: "184",
    name: "Clement Road Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M87034",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Women's Health", "Travel Advice"],
    borough: "Birmingham",
    postcode: "B62 9LR",
    address: "4a Clement Road, Halesowen, B62 9LR",
    phone: "0121 5592044",
    acceptingNewPatients: "yes",
    description: "Clement Road Medical Practice offers general medical services in Halesowen, serving the local Dudley borough community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 25,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "185",
    name: "Pear Tree Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M84006",
    services: ["General Practice", "Immunisations", "Health Checks", "Family Planning", "Minor Illnesses"],
    borough: "Tamworth",
    postcode: "B78 2NR",
    address: "28 Meadow Close, Kingsbury, Tamworth, B78 2NR",
    phone: "01827 872755",
    acceptingNewPatients: "yes",
    description: "Pear Tree Surgery in Kingsbury, Tamworth, provides comprehensive general medical services to its local patient population.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 19,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "186",
    name: "Bloomsbury Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85732",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Child Health", "Drug & Alcohol Support"],
    borough: "Birmingham",
    postcode: "B7 5BT",
    address: "30 Bloomsbury Street, Nechells, Birmingham, B7 5BT",
    phone: "0121 3591539",
    acceptingNewPatients: "yes",
    description: "Bloomsbury Surgery is located in Nechells, Birmingham, offering a range of primary healthcare services to the diverse local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "187",
    name: "Wychall Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85071",
    services: ["General Practice", "Family Planning", "Child Immunisations", "Diabetes Management", "Asthma Reviews"],
    borough: "Birmingham",
    postcode: "B38 8TE",
    address: "11 Wychall Lane, Kings Norton, Birmingham, B38 8TE",
    phone: "0121 6282345",
    acceptingNewPatients: "yes",
    description: "Wychall Lane Surgery in Kings Norton provides general medical services, primarily focusing on family health and preventative care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.7,
    reviews: 28,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "188",
    name: "Kingstanding Circle Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85155",
    services: ["General Practice", "Vaccinations", "Health Checks", "Chronic Disease Management", "Repeat Prescriptions"],
    borough: "Birmingham",
    postcode: "B44 0UY",
    address: "26 Rough Road, Birmingham, B44 0UY",
    phone: "01216 471385",
    acceptingNewPatients: "yes",
    description: "Kingstanding Circle Surgery offers primary healthcare services to the Kingstanding area of Birmingham, committed to patient well-being.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 62,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "189",
    name: "Newport Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85164",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Women's Health", "Minor Surgery"],
    borough: "Birmingham",
    postcode: "B12 8QE",
    address: "Newport Medical Practice, 1 Newport Road, Birmingham, B12 8QE",
    phone: "0121 4491327",
    acceptingNewPatients: "yes",
    description: "Newport Medical Group, located at Newport Medical Practice, provides comprehensive general medical care in Birmingham B12.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 57,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "190",
    name: "Dr Kulshrestha Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85686",
    services: ["General Practice", "Child Health", "Antenatal Care", "Chronic Disease Management", "Travel Vaccinations"],
    borough: "Birmingham",
    postcode: "B18 7AL",
    address: "Summerfield Prim Care Ctr, 134 Heath Street, Winson, Birmingham, B18 7AL",
    phone: "0845 0730392",
    acceptingNewPatients: "yes",
    description: "Dr Kulshrestha Family Practice, located at Summerfield Primary Care Centre, offers family-focused general medical services in Winson Green.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 67,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "Punjabi"],
  },
  
  {
    id: "193",
    name: "Green Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M89027",
    services: ["General Practice", "Immunisations", "Chronic Disease Management", "Women's Health", "Health Checks"],
    borough: "Birmingham",
    postcode: "B36 0SZ",
    address: "Smiths Wood Medical Ctr, Burtons Way, Smiths Wood, Birmingham, B36 0SZ",
    phone: "0121 7495115",
    acceptingNewPatients: "yes",
    description: "Green Lane Surgery is part of Smiths Wood Medical Centre, providing general medical care to Smiths Wood and the surrounding area.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "194",
    name: "Kingshurst Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M89002",
    services: ["General Practice", "Child Health", "Family Planning", "Chronic Disease Management", "Vaccinations"],
    borough: "Birmingham",
    postcode: "B37 6BE",
    address: "40 Gilson Way, Kingshurst, Birmingham, B37 6BE",
    phone: "01217 175350",
    acceptingNewPatients: "yes",
    description: "Kingshurst Medical Practice offers comprehensive general medical services to the Kingshurst community, focusing on local patient needs.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 76,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Romanian", "Polish"],
  },
  {
    id: "195",
    name: "Green Ridge Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85006",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Mental Health Support"],
    borough: "Birmingham",
    postcode: "B13 0HN",
    address: "671 Yardley Wood Road, Birmingham, B13 0HN",
    phone: "0121 4658230",
    acceptingNewPatients: "yes",
    description: "Green Ridge Surgery provides primary healthcare services on Yardley Wood Road, serving the local Birmingham community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 51,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "196",
    name: "Causeway Green Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M88620",
    services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Minor Illness Treatment", "Referrals"],
    borough: "Birmingham",
    postcode: "B68 8LJ",
    address: "158 Causeway Green Road, Warley, Oldbury, B68 8LJ",
    phone: "0121 5521968",
    acceptingNewPatients: "yes",
    description: "Causeway Green Road Surgery in Warley, Oldbury, offers general medical services to the local residents.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 63,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "197",
    name: "Perry Park Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85624",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Smoking Cessation"],
    borough: "Birmingham",
    postcode: "B42 1TY",
    address: "291-293 Walsall Road, Perry Barr, Birmingham, B42 1TY",
    phone: "0121 3564131",
    acceptingNewPatients: "yes",
    description: "Perry Park Surgery provides general medical care to the Perry Barr area, committed to community health and patient support.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.7,
    reviews: 8,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "198",
    name: "Hillcrest Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85016",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Prescription Services"],
    borough: "Birmingham",
    postcode: "B44 0NN",
    address: "9 Twickenham Road, Kingstanding, Birmingham, B44 0NN",
    phone: "01213 730916",
    acceptingNewPatients: "yes",
    description: "Hillcrest Surgery offers primary healthcare services in Kingstanding, providing general medical care to the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 41,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "199",
    name: "Radcliffe Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P83007",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Minor Surgery", "Health Checks"],
    borough: "Bury",
    postcode: "M26 2SP",
    address: "69 Church Street West, Radcliffe, Manchester, M26 2SP",
    phone: "0161 7242062",
    acceptingNewPatients: "yes",
    description: "Radcliffe Medical Practice provides general medical services in Radcliffe, Manchester, committed to integrated patient care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 30,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Chinese"],
  },
  {
    id: "200",
    name: "The Willows Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P87658",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Salford",
    postcode: "M5 5JR",
    address: "The Willows, Lords Avenue, Salford, M5 5JR",
    phone: "0161 7362356",
    acceptingNewPatients: "yes",
    description: "The Willows Medical Practice in Salford offers primary healthcare services, focusing on comprehensive and accessible patient care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 21,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Arabic"],
  },
  {
    id: "201",
    name: "North Trafford Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P91629",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Family Planning", "Minor Disorders"],
    borough: "Trafford",
    postcode: "M32 0PA",
    address: "864-866 Chester Road, Stretford, Manchester, M32 0PA",
    phone: "0161 8655556",
    acceptingNewPatients: "yes",
    description: "North Trafford Group Practice provides general medical services on Chester Road, Stretford, serving its local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 46,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "202",
    name: "Cornishway Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P84043",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Checks", "Antenatal Care"],
    borough: "Manchester",
    postcode: "M22 5RX",
    address: "Forum Health, Simonsway, Wythenshawe, Manchester, M22 5RX",
    phone: "0161 4902293",
    acceptingNewPatients: "yes",
    description: "Cornishway Group Practice is situated at Forum Health in Wythenshawe, offering a range of primary care services.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 72,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "203",
    name: "Northenden Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P84012",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Manchester",
    postcode: "M22 4DH",
    address: "489 Palatine Road, Northenden, Manchester, M22 4DH",
    phone: "0161 9983206",
    acceptingNewPatients: "yes",
    description: "Northenden Group Practice offers comprehensive general medical services to the Northenden area, dedicated to patient health.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 27,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish", "Arabic"],
  },
  {
    id: "204",
    name: "Newbury Green Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P87026",
    services: ["General Practice", "Immunisations", "Health Checks", "Chronic Disease Management", "Smoking Cessation"],
    borough: "Salford",
    postcode: "M7 4NX",
    address: "55 Rigby Street, Salford, M7 4NX",
    phone: "01619 830800",
    acceptingNewPatients: "yes",
    description: "Newbury Green Medical Practice provides general medical care in Salford, committed to accessible healthcare for its community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 28,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "205",
    name: "Droylsden Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02663",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Family Planning", "Minor Surgery"],
    borough: "Tameside",
    postcode: "M43 7NP",
    address: "1-3 Albion Drive, Droylsden, Manchester, M43 7NP",
    phone: "0161 3427777",
    acceptingNewPatients: "yes",
    description: "Droylsden Medical Practice offers a range of primary healthcare services to the Droylsden community in Manchester.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 75,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish", "Urdu"],
  },
  {
    id: "206",
    name: "West Gorton Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P84052",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Manchester",
    postcode: "M12 5JE",
    address: "2 Clowes St, West Gorton, Manchester, M12 5JE",
    phone: "0161 2235226",
    acceptingNewPatients: "yes",
    description: "West Gorton Medical Practice provides general medical services in West Gorton, dedicated to supporting the health of the local population.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 84,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Bengali"],
  },
  {
    id: "207",
    name: "Kingsway Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P84022",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Minor Illness Treatment"],
    borough: "Manchester",
    postcode: "M19 1UG",
    address: "720 Burnage Lane, Manchester, M19 1UG",
    phone: "0161 8049899",
    acceptingNewPatients: "yes",
    description: "Kingsway Medical Practice offers general medical services on Burnage Lane, serving the local community in Burnage.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "208",
    name: "Windermere Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P86620",
    services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Family Planning", "Repeat Prescriptions"],
    borough: "Rochdale",
    postcode: "M24 5WF",
    address: "109-111 Windermere Road, Middleton, Manchester, M24 5WF",
    phone: "0844 4778558",
    acceptingNewPatients: "yes",
    description: "Windermere Surgery provides primary healthcare services in Middleton, Manchester, focusing on patient-centred care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 54,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "209",
    name: "Roseheath Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N83031",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Knowsley",
    postcode: "L26 9UH",
    address: "Roseheath Drive, Halewood, Liverpool, L26 9UH",
    phone: "0151 2443255",
    acceptingNewPatients: "yes",
    description: "Roseheath Surgery in Halewood, Liverpool, offers compassionate general medical services to its local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 47,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Urdu"],
  },
  {
    id: "210",
    name: "Rainhill Village Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N83010",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Minor Surgery"],
    borough: "St Helens",
    postcode: "L35 4LP",
    address: "529 Warrington Road, Rainhill, Prescot, L35 4LP",
    phone: "0151 5115672",
    acceptingNewPatients: "yes",
    description: "Rainhill Village Surgery provides general medical care in Rainhill, Prescot, dedicated to serving its community's health needs.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 38,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Lithuanian"],
  },
  {
    id: "211",
    name: "Ashworth (secured) Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A99920",
    services: ["General Practice", "Mental Health Support", "Long-term Conditions", "Physical Health Checks", "Forensic Healthcare"],
    borough: "Sefton",
    postcode: "L31 1HW",
    address: "Ashworth Hospital, Parkbourn, Maghull, Liverpool, L31 1HW",
    phone: "",
    acceptingNewPatients: "yes",
    description: "Ashworth (Secured) Practice, operating within Ashworth Hospital, provides specialized medical care in a secure environment.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 18,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Urdu"],
  },
  {
    id: "212",
    name: "Park Street Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N84034",
    services: ["General Practice", "Vaccinations", "Chronic Disease Management", "Child Health", "Women's Health"],
    borough: "Sefton",
    postcode: "L20 3DF",
    address: "Park Street, Bootle, L20 3DF",
    phone: "0151 9441090",
    acceptingNewPatients: "yes",
    description: "Park Street Surgery provides general medical services in Bootle, committed to accessible and quality care for its patients.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.6,
    reviews: 70,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish", "Arabic"],
  },
  {
    id: "213",
    name: "Penny Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N82026",
    services: ["General Practice", "Child Immunisations", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Liverpool",
    postcode: "L15 9EH",
    address: "7 Smithdown Place, Wavertree, Liverpool, L15 9EH",
    phone: "0151 7332800",
    acceptingNewPatients: "yes",
    description: "Penny Lane Surgery in Wavertree, Liverpool, offers general medical services with a focus on family health.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 76,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Filipino"],
  },
  {
    id: "214",
    name: "Burscough Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P81138",
    services: ["General Practice", "Chronic Disease Management", "Antenatal Care", "Child Health", "Immunisations"],
    borough: "West Lancashire",
    postcode: "L40 4LA",
    address: "Burscough Health Centre, Stanley Court, Lord Street, Burscough, Ormskirk, L40 4LA",
    phone: "01704 396020",
    acceptingNewPatients: "yes",
    description: "Burscough Family Practice is located within Burscough Health Centre, providing comprehensive family medical care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 24,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "215",
    name: "Mere Lane Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N82099",
    services: ["General Practice", "Chronic Disease Management", "Health Checks", "Vaccinations", "Mental Health Support"],
    borough: "Liverpool",
    postcode: "L5 0QW",
    address: "Mere Lane N'hood Hc, Mere Lane, Liverpool, L5 0QW",
    phone: "0151 2959620",
    acceptingNewPatients: "yes",
    description: "Mere Lane Group Practice, at Mere Lane N'hood HC, delivers general medical services to the local Liverpool community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 78,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Somali"],
  },
  {
    id: "216",
    name: "Stopgate Lane Medical Ctr",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N82678",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Chronic Disease Management"],
    borough: "Liverpool",
    postcode: "L9 6AP",
    address: "6 Stopgate Lane, Walton, Liverpool, L9 6AP",
    phone: "0151 5251298",
    acceptingNewPatients: "yes",
    description: "Stopgate Lane Medical Centre offers general medical services in Walton, Liverpool, dedicated to patient health and well-being.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.7,
    reviews: 75,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Lithuanian"],
  },
  {
    id: "217",
    name: "Diamond Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86019",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Minor Surgery"],
    borough: "Leeds",
    postcode: "LS7 3RA",
    address: "Rutland Lodge, Scott Hall Road, Leeds, LS7 3RA",
    phone: "0113 2007474",
    acceptingNewPatients: "yes",
    description: "Diamond Medical Group, located at Rutland Lodge, provides general medical services in Leeds.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 62,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "218",
    name: "Chevin Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86032",
    services: ["General Practice", "Family Planning", "Child Immunisations", "Travel Vaccinations", "Minor Injuries"],
    borough: "Leeds",
    postcode: "LS21 1BQ",
    address: "3 Bridge Street, Otley, LS21 1BQ",
    phone: "01943 858300",
    acceptingNewPatients: "yes",
    description: "Chevin Medical Practice in Otley offers comprehensive general medical services to the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 77,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "220",
    name: "Hillfoot Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86011",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Checks", "Repeat Prescriptions"],
    borough: "Leeds",
    postcode: "LS28 7QR",
    address: "126 Owlcotes Road, Pudsey, LS28 7QR",
    phone: "0113 2574169",
    acceptingNewPatients: "yes",
    description: "Hillfoot Surgery provides general medical care in Pudsey, committed to supporting the health and well-being of its patients.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 59,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "221",
    name: "Foundry Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86106",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Diabetes Management"],
    borough: "Leeds",
    postcode: "LS14 6GG",
    address: "95 Moresdale Lane, Leeds, LS14 6GG",
    phone: "0113 2951200",
    acceptingNewPatients: "yes",
    description: "Foundry Lane Surgery in Leeds offers primary healthcare services, with a focus on family medicine and chronic condition management.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 55,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Urdu"],
  },
  {
    id: "222",
    name: "Sherburn Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B82031",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Smoking Cessation", "Minor Surgery"],
    borough: "Leeds",
    postcode: "LS25 6ED",
    address: "Beech Grove, Sherburn in Elmet, Leeds, LS25 6ED",
    phone: "01977 682208",
    acceptingNewPatients: "yes",
    description: "Sherburn Group Practice, located in Sherburn in Elmet, provides comprehensive general medical services to the rural community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 74,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "223",
    name: "St Martins Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86100",
    services: ["General Practice", "Child Health", "Women's Health", "Chronic Disease Management", "Mental Health Support"],
    borough: "Leeds",
    postcode: "LS7 4HZ",
    address: "St. Martins Surgery, 210 Chapeltown Road, Leeds, LS7 4HZ",
    phone: "0113 2211888",
    acceptingNewPatients: "yes",
    description: "St Martins Practice, also known as St. Martins Surgery, serves the Chapeltown Road area of Leeds with general medical care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 39,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "224",
    name: "Mulberry Street Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86018",
    services: ["General Practice", "Vaccinations", "Health Checks", "Chronic Disease Management", "Travel Advice"],
    borough: "Leeds",
    postcode: "LS28 7XP",
    address: "18 Mulberry Street, Pudsey, LS28 7XP",
    phone: "0113 2570711",
    acceptingNewPatients: "yes",
    description: "Mulberry Street Medical Practice provides primary healthcare services in Pudsey, Leeds, committed to local patient needs.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 51,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Punjabi", "Urdu"],
  },
  {
    id: "225",
    name: "Graham Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81016",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Family Planning"],
    borough: "North Somerset",
    postcode: "BS23 1YA",
    address: "22 Graham Road, Weston-super-mare, BS23 1YA",
    phone: "01934 628111",
    acceptingNewPatients: "yes",
    description: "Graham Road Surgery provides general medical care in Weston-super-Mare, committed to serving the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.5,
    reviews: 78,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "226",
    name: "Wellington Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81642",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Minor Surgery"],
    borough: "South Gloucestershire",
    postcode: "BS37 5UY",
    address: "Wellington Road, Yate, Bristol, BS37 5UY",
    phone: "01454 323366",
    acceptingNewPatients: "yes",
    description: "Wellington Road Surgery in Yate provides general medical services, focusing on comprehensive care for its patient population.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 36,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "227",
    name: "Hillview Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81041",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Mental Health Support", "Health Promotion"],
    borough: "Bristol",
    postcode: "BS13 0JP",
    address: "Hartcliffe Health Centre, Hareclive Road, Hartcliffe, Bristol, BS13 0JP",
    phone: "0117 3015240",
    acceptingNewPatients: "yes",
    description: "Hillview Family Practice, located at Hartcliffe Health Centre, offers family-focused general medical services in Bristol.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.5,
    reviews: 15,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Somali"],
  },
  {
    id: "228",
    name: "Axbridge Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L85055",
    services: ["General Practice", "Vaccinations", "Health Checks", "Chronic Disease Management", "Referrals"],
    borough: "Sedgemoor",
    postcode: "BS26 2BJ",
    address: "The Surgery, Houlgate Way, Axbridge, BS26 2BJ",
    phone: "01934 732464",
    acceptingNewPatients: "yes",
    description: "Axbridge Surgery provides general medical care at The Surgery, committed to serving the local Axbridge community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 12,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "229",
    name: "Almondsbury Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81127",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Chronic Disease Management"],
    borough: "South Gloucestershire",
    postcode: "BS32 4DS",
    address: "Sundays Hill, Almondsbury, Bristol, BS32 4DS",
    phone: "01454 613161",
    acceptingNewPatients: "yes",
    description: "Almondsbury Surgery on Sundays Hill offers primary healthcare services to the Almondsbury area of Bristol.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 17,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "230",
    name: "Heaton Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A86024",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Health Checks", "Family Planning"],
    borough: "Newcastle upon Tyne",
    postcode: "NE6 1SA",
    address: "17-19 Heaton Road, Newcastle Upon Tyne, NE6 1SA",
    phone: "0191 2655911",
    acceptingNewPatients: "yes",
    description: "Heaton Road Surgery provides general medical services in Newcastle upon Tyne, serving the local Heaton community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 43,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Punjabi"],
  },
  {
    id: "231",
    name: "Newburn Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A86038",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Newcastle upon Tyne",
    postcode: "NE15 8LX",
    address: "4 Newburn Road, Newburn, Newcastle Upon Tyne, NE15 8LX",
    phone: "0191 2290090",
    acceptingNewPatients: "yes",
    description: "Newburn Surgery offers primary healthcare services in Newburn, dedicated to accessible and quality care for its patients.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 35,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "232",
    name: "Gas House Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A84039",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Checks", "Minor Surgery"],
    borough: "Northumberland",
    postcode: "NE61 1JX",
    address: "Morpeth Nhs Centre, the Mount, Morpeth, NE61 1JX",
    phone: "01670 513657",
    acceptingNewPatients: "yes",
    description: "Gas House Lane Surgery, part of Morpeth NHS Centre, provides general medical services in Morpeth.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 81,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
  },
  {
    id: "233",
    name: "Betts Avenue Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A86030",
    services: ["General Practice", "Child Health", "Women's Health", "Travel Vaccinations", "Mental Health Support"],
    borough: "Newcastle upon Tyne",
    postcode: "NE15 6TQ",
    address: "2 Betts Avenue, Benwell, Newcastle Upon Tyne, NE15 6TQ",
    phone: "0191 2742767",
    acceptingNewPatients: "yes",
    description: "Betts Avenue Medical Group offers general medical services in Benwell, Newcastle upon Tyne, focused on community health.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 39,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Urdu"],
  },
  {
    id: "234",
    name: "Elsdon Avenue Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A84619",
    services: ["General Practice", "Chronic Disease Management", "Immunisations", "Family Planning", "Health Checks"],
    borough: "Northumberland",
    postcode: "NE25 0BW",
    address: "Elsdon Avenue, Seaton Delaval, Whitley Bay, NE25 0BW",
    phone: "0191 2372299",
    acceptingNewPatients: "yes",
    description: "Elsdon Avenue Surgery in Seaton Delaval, Whitley Bay, provides primary healthcare services to the local community.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 60,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish"],
  },
  
  {
    id: "236",
    name: "Orsett Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F81137",
    services: ["Ear Syringing", "Blood Tests & Pathology", "Child Health & Development", "General Consultations", "Smoking Cessation", "Vaccinations & Immunisations", "Blood Pressure Monitoring", "Contraception & Family Planning"],
    borough: "London",
    postcode: "RM16 3ET",
    address: "The Surgery, 63 Rowley Road, Orsett, Grays, RM16 3ET",
    phone: "01375 892082",
    acceptingNewPatients: "yes",
    description: "Orsett Surgery is a patient-focused GP surgery in London, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish"],
    eveningWeekend: true,
  },
  {
    id: "237",
    name: "The Broadway Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F86013",
    services: ["Travel Vaccinations", "Smoking Cessation", "Repeat Prescriptions", "Asthma & COPD Management", "Health Screenings", "Joint Injections"],
    borough: "London",
    postcode: "IG8 0HF",
    address: "3 Broadway Gardens, Monkhams Avenue, Woodford Green, IG8 0HF",
    phone: "020 85053204",
    acceptingNewPatients: "yes",
    description: "Serving the London community, The Broadway Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish"],
    eveningWeekend: true,
  },
  {
    id: "238",
    name: "Pes@Open Eye Opticians, Nwl",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08820",
    services: ["General Consultations", "Contraception & Family Planning", "Wound Care", "ECG Testing", "Cervical Screening", "Smoking Cessation", "New Patient Registration", "Blood Pressure Monitoring"],
    borough: "London",
    postcode: "NW10 2PU",
    address: "64 High Road, Willesden, London, NW10 2PU",
    phone: "02088 302727",
    acceptingNewPatients: "yes",
    description: "Pes@Open Eye Opticians, Nwl provides high-quality NHS healthcare services in London, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Portuguese", "Arabic", "French", "Polish"],
    eveningWeekend: false,
  },
  {
    id: "239",
    name: "Cricket Green Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H85038",
    services: ["Minor Surgery", "New Patient Registration", "Contraception & Family Planning", "Vaccinations & Immunisations", "Diabetes Management", "Wound Care", "Repeat Prescriptions", "Postnatal Care"],
    borough: "London",
    postcode: "CR4 3DA",
    address: "75-79 Miles Road, , Mitcham, CR4 3DA",
    phone: "020 86480822",
    acceptingNewPatients: "yes",
    description: "Cricket Green Medical Practice is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali"],
    eveningWeekend: false,
  },
  {
    id: "240",
    name: "The Chelsea Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87665",
    services: ["New Patient Registration", "Cervical Screening", "Joint Injections", "Chronic Disease Management", "ECG Testing", "Travel Vaccinations", "Asthma & COPD Management", "Blood Tests & Pathology", "Postnatal Care"],
    borough: "London",
    postcode: "SW3 5DD",
    address: "43 Chelsea Manor Street, , London, SW3 5DD",
    phone: "02073 497330",
    acceptingNewPatients: "yes",
    description: "The Chelsea Practice offers friendly and professional GP services in London, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Bengali", "French", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "241",
    name: "London Sha Php",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02385",
    services: ["Weight Management", "Health Screenings", "Smoking Cessation", "ECG Testing", "Wound Care", "General Consultations", "Women's Health", "Blood Pressure Monitoring"],
    borough: "London",
    postcode: "SW8 2JW",
    address: "Riverside Medical Centre, Hobart Hse, Wandsworth Road, London, SW8 2JW",
    phone: "020 30494500",
    acceptingNewPatients: "yes",
    description: "London Sha Php is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Romanian"],
    eveningWeekend: false,
  },
  
  {
    id: "244",
    name: "The Nightingale Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84018",
    services: ["Antenatal Care", "Repeat Prescriptions", "Contraception & Family Planning", "Smoking Cessation", "Chronic Disease Management", "Men's Health", "Diabetes Management"],
    borough: "London",
    postcode: "E5 8BY",
    address: "10 Kenninghall Road, Clapton, London, E5 8BY",
    phone: "020 89858388",
    acceptingNewPatients: "yes",
    description: "The Nightingale Practice is a patient-focused GP surgery in London, offering both routine and specialist primary care services.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "245",
    name: "Highgate Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F85014",
    services: ["Child Health & Development", "Cervical Screening", "Minor Surgery", "Antenatal Care", "Joint Injections", "New Patient Registration"],
    borough: "London",
    postcode: "N6 4QA",
    address: "44 North Hill, Highgate, London, N6 4QA",
    phone: "020 83406628",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Highgate Group Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "French"],
    eveningWeekend: false,
  },
  {
    id: "246",
    name: "Upper Norwood Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H83005",
    services: ["Blood Tests & Pathology", "Mental Health Support", "Minor Surgery", "Health Screenings", "Contraception & Family Planning", "Chronic Disease Management", "Cervical Screening", "Vaccinations & Immunisations", "Antenatal Care", "ECG Testing"],
    borough: "London",
    postcode: "SE19 2NT",
    address: "130 Church Road, Upper Norwood, London, SE19 2NT",
    phone: "020 87716050",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Upper Norwood Group Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish", "Gujarati"],
    eveningWeekend: true,
  },
  {
    id: "247",
    name: "The Allerton Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84716",
    services: ["Antenatal Care", "New Patient Registration", "Men's Health", "Asthma & COPD Management", "Joint Injections", "Contraception & Family Planning", "General Consultations"],
    borough: "London",
    postcode: "N16 5UF",
    address: "34A Allerton Road, Stoke Newington, London, N16 5UF",
    phone: "020 88022882",
    acceptingNewPatients: "yes",
    description: "Serving the London community, The Allerton Road Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Cantonese", "Arabic", "Somali"],
    eveningWeekend: false,
  },
  
  
  {
    id: "250",
    name: "Chelsfield Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G84020",
    services: ["Travel Vaccinations", "Women's Health", "Diabetes Management", "Repeat Prescriptions", "Ear Syringing", "Minor Surgery", "Blood Tests & Pathology", "Wound Care", "Health Screenings"],
    borough: "London",
    postcode: "BR6 6HD",
    address: "62 Windsor Drive, Chelsfield, Orpington, BR6 6HD",
    phone: "01689 852204",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Chelsfield Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Romanian", "Gujarati", "Cantonese", "Tamil"],
    eveningWeekend: false,
  },
  
  
  {
    id: "255",
    name: "Gillmans Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G84041",
    services: ["Cervical Screening", "Physiotherapy Referrals", "Men's Health", "Asthma & COPD Management", "Wound Care", "Mental Health Support", "Weight Management", "Blood Tests & Pathology", "Chronic Disease Management"],
    borough: "London",
    postcode: "BR5 4LA",
    address: "1 Gillmans Road, , Orpington, BR5 4LA",
    phone: "01689 822022",
    acceptingNewPatients: "yes",
    description: "Gillmans Road Surgery is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Somali", "Urdu"],
    eveningWeekend: false,
  },
  {
    id: "256",
    name: "Custom House Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84047",
    services: ["Joint Injections", "Smoking Cessation", "Ear Syringing", "Travel Vaccinations", "Child Health & Development", "Postnatal Care", "Women's Health"],
    borough: "London",
    postcode: "E16 3NA",
    address: "16 Freemasons Road, Canning Town, London, E16 3NA",
    phone: "020 74762255",
    acceptingNewPatients: "yes",
    description: "Custom House Surgery is a patient-focused GP surgery in London, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "Gujarati", "French"],
    eveningWeekend: false,
  },
  {
    id: "257",
    name: "Birdhurst Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H83627",
    services: ["Contraception & Family Planning", "New Patient Registration", "Mental Health Support", "Postnatal Care", "Vaccinations & Immunisations", "Asthma & COPD Management", "Chronic Disease Management", "Travel Vaccinations", "Men's Health"],
    borough: "London",
    postcode: "CR2 7DX",
    address: "1 Birdhurst Avenue, , South Croydon, CR2 7DX",
    phone: "020 86862070",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Birdhurst Medical Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Bengali", "Urdu", "Tamil"],
    eveningWeekend: true,
  },
  
  {
    id: "259",
    name: "Molebridge Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H81618",
    services: ["Diabetes Management", "Smoking Cessation", "Mental Health Support", "Weight Management", "Contraception & Family Planning", "Chronic Disease Management", "Joint Injections"],
    borough: "London",
    postcode: "KT22 7PZ",
    address: "North Leatherhead Medical Centre, 148-152 Kingston Road, Leatherhead, KT22 7PZ",
    phone: "01372 362099",
    acceptingNewPatients: "yes",
    description: "Molebridge Practice is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "260",
    name: "The Alexandra Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F85675",
    services: ["Postnatal Care", "Blood Tests & Pathology", "Antenatal Care", "Smoking Cessation", "Minor Surgery", "Weight Management", "Men's Health"],
    borough: "London",
    postcode: "N22 7UN",
    address: "125 Alexandra Park Road, Wood Green, London, N22 7UN",
    phone: "020 88882518",
    acceptingNewPatients: "yes",
    description: "Located in London, The Alexandra Surgery is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish"],
    eveningWeekend: true,
  },
  
  {
    id: "262",
    name: "Emperor'S Gate Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87043",
    services: ["Cervical Screening", "Wound Care", "General Consultations", "ECG Testing", "Travel Vaccinations", "Vaccinations & Immunisations", "Contraception & Family Planning", "Child Health & Development"],
    borough: "London",
    postcode: "SW7 4HJ",
    address: "First Floor, 49 Emperors Gate, London, SW7 4HJ",
    phone: "020 72445670",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Emperor'S Gate Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Mandarin", "Portuguese", "Urdu"],
    eveningWeekend: false,
  },
  {
    id: "263",
    name: "North East London Mhtr",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08898",
    services: ["Mental Health Support", "Joint Injections", "Physiotherapy Referrals", "Smoking Cessation", "Asthma & COPD Management", "Travel Vaccinations", "ECG Testing"],
    borough: "London",
    postcode: "E2 6LL",
    address: "Bethnal Green Health Centre, 60 Florida Street, London, E2 6LL",
    phone: "02077 396677",
    acceptingNewPatients: "yes",
    description: "North East London Mhtr is a patient-focused GP surgery in London, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish", "Polish"],
    eveningWeekend: true,
  },
  {
    id: "264",
    name: "Colney Hatch Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E83034",
    services: ["Physiotherapy Referrals", "Child Health & Development", "Women's Health", "Men's Health", "Ear Syringing", "Blood Pressure Monitoring", "Wound Care"],
    borough: "London",
    postcode: "N10 1ET",
    address: "192 Colney Hatch Lane, Muswell Hill, London, N10 1ET",
    phone: "020 88835555",
    acceptingNewPatients: "yes",
    description: "Colney Hatch Lane Surgery is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Mandarin", "Urdu"],
    eveningWeekend: true,
  },
  
  {
    id: "266",
    name: "The Surgery, Dr Dasgupta & Partners",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85005",
    services: ["Postnatal Care", "Physiotherapy Referrals", "Weight Management", "Child Health & Development", "Men's Health", "Contraception & Family Planning", "General Consultations", "Smoking Cessation", "Mental Health Support"],
    borough: "London",
    postcode: "W12 0PT",
    address: "13 Westway, Shepherds Bush, London, W12 0PT",
    phone: "020 87433704",
    acceptingNewPatients: "yes",
    description: "The Surgery, Dr Dasgupta & Partners provides high-quality NHS healthcare services in London, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Turkish"],
    eveningWeekend: false,
  },
  
  {
    id: "268",
    name: "Green Light Pharmacy",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08536",
    services: ["Travel Vaccinations", "Postnatal Care", "Mental Health Support", "Smoking Cessation", "Wound Care", "Asthma & COPD Management", "Chronic Disease Management"],
    borough: "London",
    postcode: "N16 6QX",
    address: "172 Stamford Hill, , London, N16 6QX",
    phone: "02088 006166",
    acceptingNewPatients: "yes",
    description: "Green Light Pharmacy provides high-quality NHS healthcare services in London, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Portuguese", "Turkish", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "269",
    name: "Box Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "J83013",
    services: ["Mental Health Support", "Contraception & Family Planning", "Repeat Prescriptions", "Ear Syringing", "Smoking Cessation", "New Patient Registration", "Physiotherapy Referrals", "Vaccinations & Immunisations", "Postnatal Care", "Blood Pressure Monitoring"],
    borough: "London",
    postcode: "SN13 8NA",
    address: "London Road, Box, Corsham, SN13 8NA",
    phone: "01225 742361",
    acceptingNewPatients: "yes",
    description: "Box Surgery provides high-quality NHS healthcare services in London, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "French", "Punjabi", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "270",
    name: "Health Finder Pro Courts",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08296",
    services: ["Postnatal Care", "Smoking Cessation", "Joint Injections", "Asthma & COPD Management", "Blood Pressure Monitoring", "Contraception & Family Planning"],
    borough: "London",
    postcode: "SE11 5JH",
    address: "Unit 316, Vox Studios, 1-45 Durham Street, London, SE11 5JH",
    phone: "02073 264268",
    acceptingNewPatients: "yes",
    description: "Health Finder Pro Courts offers friendly and professional GP services in London, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Hindi", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "271",
    name: "Oxshott Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H81107",
    services: ["Antenatal Care", "Ear Syringing", "Weight Management", "Repeat Prescriptions", "Travel Vaccinations", "Physiotherapy Referrals", "Cervical Screening"],
    borough: "London",
    postcode: "KT22 0QL",
    address: "Holtwood Road, Oxshott, Leatherhead, KT22 0QL",
    phone: "01372 844000",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Oxshott Medical Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "French", "Punjabi", "Somali", "Bengali"],
    eveningWeekend: false,
  },
  {
    id: "272",
    name: "Sutton Health And Care At Home Team",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y07125",
    services: ["Repeat Prescriptions", "Blood Pressure Monitoring", "Ear Syringing", "Men's Health", "Cervical Screening", "Antenatal Care"],
    borough: "London",
    postcode: "SM5 1AA",
    address: "St Helier Hospital, Wrythe Lane, Carshalton, SM5 1AA",
    phone: "02082 962187",
    acceptingNewPatients: "yes",
    description: "Sutton Health And Care At Home Team provides high-quality NHS healthcare services in London, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "273",
    name: "Langstone Way Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E83049",
    services: ["Physiotherapy Referrals", "ECG Testing", "Joint Injections", "Travel Vaccinations", "New Patient Registration", "Repeat Prescriptions", "Weight Management", "Minor Surgery", "Mental Health Support"],
    borough: "London",
    postcode: "NW7 1GR",
    address: "28 Langstone Way, Mill Hill, London, NW7 1GR",
    phone: "020 83432401",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Langstone Way Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Arabic"],
    eveningWeekend: false,
  },
  {
    id: "274",
    name: "The Riverside Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84619",
    services: ["Weight Management", "Blood Pressure Monitoring", "Vaccinations & Immunisations", "Blood Tests & Pathology", "Repeat Prescriptions", "Contraception & Family Planning"],
    borough: "London",
    postcode: "E5 9BQ",
    address: "14 Urban Hive, Theydon Road, London, E5 9BQ",
    phone: "020 88061928",
    acceptingNewPatients: "yes",
    description: "Serving the London community, The Riverside Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Gujarati", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "275",
    name: "Crown Street Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85019",
    services: ["Blood Pressure Monitoring", "ECG Testing", "Smoking Cessation", "Antenatal Care", "Health Screenings", "Minor Surgery", "Asthma & COPD Management", "Child Health & Development"],
    borough: "London",
    postcode: "W3 8SA",
    address: "2 Lombard Court, Crown Street, Acton, London, W3 8SA",
    phone: "020 89922010",
    acceptingNewPatients: "yes",
    description: "Crown Street Surgery is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Gujarati", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "276",
    name: "Pickhurst Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G84033",
    services: ["Men's Health", "Antenatal Care", "ECG Testing", "Child Health & Development", "Weight Management", "Ear Syringing", "Contraception & Family Planning"],
    borough: "London",
    postcode: "BR2 7JF",
    address: "56 Pickhurst Lane, Hayes, Bromley, BR2 7JF",
    phone: "020 36675550",
    acceptingNewPatients: "yes",
    description: "Pickhurst Surgery provides high-quality NHS healthcare services in London, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Tamil", "French"],
    eveningWeekend: false,
  },
  {
    id: "277",
    name: "Fairhill Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H84020",
    services: ["Physiotherapy Referrals", "Weight Management", "Cervical Screening", "ECG Testing", "Diabetes Management", "Blood Tests & Pathology", "Vaccinations & Immunisations", "Smoking Cessation"],
    borough: "London",
    postcode: "KT2 7PX",
    address: "81 Kingston Hill, , Kingston Upon Thames, KT2 7PX",
    phone: "020 85461407",
    acceptingNewPatients: "yes",
    description: "Fairhill Medical Practice offers friendly and professional GP services in London, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish", "Arabic", "Polish", "Hindi"],
    eveningWeekend: false,
  },
  {
    id: "278",
    name: "Lee Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85046",
    services: ["Ear Syringing", "Physiotherapy Referrals", "Antenatal Care", "Minor Surgery", "Blood Tests & Pathology", "New Patient Registration"],
    borough: "London",
    postcode: "SE3 9RT",
    address: "Doctors Surgery, 20 Lee Road, London, SE3 9RT",
    phone: "020 88521235",
    acceptingNewPatients: "yes",
    description: "Lee Road Surgery provides high-quality NHS healthcare services in London, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "Cantonese", "Gujarati", "Polish"],
    eveningWeekend: false,
  },
  {
    id: "279",
    name: "Cgl Tower Hamlets",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y06535",
    services: ["Physiotherapy Referrals", "ECG Testing", "Men's Health", "Health Screenings", "Postnatal Care", "New Patient Registration"],
    borough: "London",
    postcode: "E1 1DN",
    address: "183 Whitechapel Road, Tower Hamlets, London, E1 1DN",
    phone: "020 38899510",
    acceptingNewPatients: "yes",
    description: "Cgl Tower Hamlets is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Bengali", "Arabic", "Gujarati"],
    eveningWeekend: false,
  },
  {
    id: "280",
    name: "Health Locality Service",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y04690",
    services: ["Contraception & Family Planning", "Ear Syringing", "Asthma & COPD Management", "Vaccinations & Immunisations", "Mental Health Support", "Child Health & Development", "Physiotherapy Referrals", "Blood Tests & Pathology", "Minor Surgery"],
    borough: "London",
    postcode: "SE22 8EY",
    address: "Tessa Jowell Health Ctr, 72H East Dulwich Grove, London, SE22 8EY",
    phone: "020 30497600",
    acceptingNewPatients: "yes",
    description: "Serving the London community, Health Locality Service provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese", "Urdu", "Somali", "Mandarin"],
    eveningWeekend: true,
  },
  {
    id: "281",
    name: "Cockfosters Medical Ctre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F85016",
    services: ["Child Health & Development", "Mental Health Support", "Vaccinations & Immunisations", "Wound Care", "Smoking Cessation", "ECG Testing", "Weight Management", "Diabetes Management"],
    borough: "London",
    postcode: "EN4 9NB",
    address: "Cockfosters Medical Centre, Heddon Court Avenue, Cockfosters, Barnet, EN4 9NB",
    phone: "020 84417008",
    acceptingNewPatients: "yes",
    description: "Cockfosters Medical Ctre is a well-established GP practice in London, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Mandarin", "Romanian", "Polish", "Somali"],
    eveningWeekend: false,
  },
  
  {
    id: "284",
    name: "The Mill Hill Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E85107",
    services: ["Asthma & COPD Management", "ECG Testing", "Diabetes Management", "Wound Care", "Health Screenings", "Smoking Cessation", "Travel Vaccinations", "Vaccinations & Immunisations"],
    borough: "London",
    postcode: "W3 8QH",
    address: "111 Avenue Road, Acton, London, W3 8QH",
    phone: "020 89929955",
    acceptingNewPatients: "yes",
    description: "The Mill Hill Surgery offers friendly and professional GP services in London, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Mandarin", "Urdu", "Portuguese", "Spanish"],
    eveningWeekend: true,
  },
  {
    id: "285",
    name: "The Slieve Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85145",
    services: ["Ear Syringing", "Smoking Cessation", "Cervical Screening", "Diabetes Management", "Weight Management", "Blood Pressure Monitoring", "Health Screenings"],
    borough: "Birmingham",
    postcode: "B20 2NR",
    address: "2 The Slieve, Handsworth Wood, Birmingham, B20 2NR",
    phone: "0121 5541812",
    acceptingNewPatients: "yes",
    description: "The Slieve Surgery provides high-quality NHS healthcare services in Birmingham, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Bengali"],
    eveningWeekend: false,
  },
  
  {
    id: "290",
    name: "The Dove Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85081",
    services: ["Cervical Screening", "Men's Health", "Weight Management", "Repeat Prescriptions", "Ear Syringing", "Mental Health Support", "Child Health & Development", "Postnatal Care", "ECG Testing"],
    borough: "Birmingham",
    postcode: "B23 5DD",
    address: "60 Dovedale Road, , Birmingham, B23 5DD",
    phone: "0121 4655739",
    acceptingNewPatients: "yes",
    description: "The Dove Medical Practice is a well-established GP practice in Birmingham, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Cantonese", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "291",
    name: "Hodge Hill Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02567",
    services: ["Blood Tests & Pathology", "New Patient Registration", "ECG Testing", "Postnatal Care", "Blood Pressure Monitoring", "Smoking Cessation", "Vaccinations & Immunisations", "Health Screenings", "Ear Syringing"],
    borough: "Birmingham",
    postcode: "B36 8GH",
    address: "1St Floor Hodge Hill Pcc, Roughlea Avenue, Hodge Hill, Birmingham, B36 8GH",
    phone: "0121 6616961",
    acceptingNewPatients: "yes",
    description: "Hodge Hill Family Practice provides high-quality NHS healthcare services in Birmingham, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French", "Gujarati", "Bengali"],
    eveningWeekend: false,
  },
  
  
  {
    id: "296",
    name: "Springfield Medical Pract",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M85756",
    services: ["Minor Surgery", "Diabetes Management", "Weight Management", "Chronic Disease Management", "Blood Tests & Pathology", "General Consultations", "Health Screenings", "Men's Health", "ECG Testing"],
    borough: "Birmingham",
    postcode: "B11 4DG",
    address: "739-741 Stratford Road, Sparkhill, Birmingham, B11 4DG",
    phone: "0121 7784321",
    acceptingNewPatients: "yes",
    description: "Springfield Medical Pract is a patient-focused GP surgery in Birmingham, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese", "Bengali"],
    eveningWeekend: false,
  },
  
  {
    id: "298",
    name: "Bosworth Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M89008",
    services: ["Health Screenings", "Wound Care", "Diabetes Management", "New Patient Registration", "Asthma & COPD Management", "Blood Pressure Monitoring", "Chronic Disease Management", "ECG Testing", "Blood Tests & Pathology"],
    borough: "Birmingham",
    postcode: "B37 5BU",
    address: "Chelmsley Wood Pcc, Crabtree Drive, Chelmsley Wood, Birmingham, B37 5BU",
    phone: "0121 7704484",
    acceptingNewPatients: "yes",
    description: "Bosworth Medical Group provides high-quality NHS healthcare services in Birmingham, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Mandarin", "Turkish"],
    eveningWeekend: false,
  },
  
  {
    id: "300",
    name: "Care Homes Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02625",
    services: ["Chronic Disease Management", "Weight Management", "Antenatal Care", "Cervical Screening", "Mental Health Support", "ECG Testing", "Travel Vaccinations"],
    borough: "Manchester",
    postcode: "M6 5FW",
    address: "Mezzanine Floor, St James'S House, Pendleton Way, Salford, M6 5FW",
    phone: "0161 2125840",
    acceptingNewPatients: "yes",
    description: "Care Homes Medical Practice offers friendly and professional GP services in Manchester, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese", "Punjabi", "Bengali"],
    eveningWeekend: false,
  },
  {
    id: "301",
    name: "Millgate Healthcare Partnership",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P89015",
    services: ["Physiotherapy Referrals", "Women's Health", "Blood Pressure Monitoring", "Diabetes Management", "Chronic Disease Management", "General Consultations", "Postnatal Care"],
    borough: "Manchester",
    postcode: "M34 2AJ",
    address: "Ann Street, Denton, Manchester, M34 2AJ",
    phone: "0161 3203131",
    acceptingNewPatients: "yes",
    description: "Millgate Healthcare Partnership is a patient-focused GP surgery in Manchester, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "302",
    name: "Cleggs Lane Medical Practice/129",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P87613",
    services: ["ECG Testing", "Smoking Cessation", "Blood Tests & Pathology", "Diabetes Management", "Repeat Prescriptions", "Postnatal Care", "Travel Vaccinations", "Mental Health Support"],
    borough: "Manchester",
    postcode: "M38 9RS",
    address: "129-131 Cleggs Lane, Little Hulton, Manchester, M38 9RS",
    phone: "0161 7994001",
    acceptingNewPatients: "yes",
    description: "Cleggs Lane Medical Practice/129 is a patient-focused GP surgery in Manchester, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Mandarin", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "303",
    name: "Pes At The Village Opticians Bury",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y06920",
    services: ["Physiotherapy Referrals", "Child Health & Development", "Diabetes Management", "Asthma & COPD Management", "Cervical Screening", "Vaccinations & Immunisations", "ECG Testing", "Men's Health", "Blood Pressure Monitoring", "Smoking Cessation"],
    borough: "Manchester",
    postcode: "M25 1AX",
    address: "470 Bury New Road, Prestwich, Manchester, M25 1AX",
    phone: "01617 730069",
    acceptingNewPatients: "yes",
    description: "Pes At The Village Opticians Bury is a patient-focused GP surgery in Manchester, offering both routine and specialist primary care services.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "304",
    name: "1/Monton Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P87620",
    services: ["Child Health & Development", "Blood Pressure Monitoring", "Men's Health", "Health Screenings", "Blood Tests & Pathology", "Smoking Cessation", "Wound Care"],
    borough: "Manchester",
    postcode: "M30 8AR",
    address: "Monton Medical Centre, Canal Side, Eccles, Manchester, M30 8AR",
    phone: "0161 7890011",
    acceptingNewPatients: "yes",
    description: "1/Monton Medical Practice is a patient-focused GP surgery in Manchester, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish", "Romanian", "Gujarati", "Polish"],
    eveningWeekend: false,
  },
  
  {
    id: "306",
    name: "Orchard Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P87627",
    services: ["Joint Injections", "Repeat Prescriptions", "Antenatal Care", "Weight Management", "Chronic Disease Management", "Child Health & Development", "Smoking Cessation", "Minor Surgery", "Diabetes Management", "Women's Health"],
    borough: "Manchester",
    postcode: "M28 1LZ",
    address: "10 Leigh Road, Worsley, Boothstown, Manchester, M28 1LZ",
    phone: "0161 7028579",
    acceptingNewPatients: "yes",
    description: "Serving the Manchester community, Orchard Medical Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese"],
    eveningWeekend: false,
  },
  
  {
    id: "309",
    name: "Chorlton Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P84068",
    services: ["Men's Health", "Diabetes Management", "Repeat Prescriptions", "New Patient Registration", "Blood Tests & Pathology", "Joint Injections"],
    borough: "Manchester",
    postcode: "M21 9NJ",
    address: "Chorlton Health Centre, 1 Nicolas Road, Chorlton Cum Hardy, Manchester, M21 9NJ",
    phone: "0161 8814545",
    acceptingNewPatients: "yes",
    description: "Chorlton Family Practice provides high-quality NHS healthcare services in Manchester, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Tamil", "Gujarati"],
    eveningWeekend: false,
  },
  {
    id: "310",
    name: "Princess Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P84056",
    services: ["Blood Tests & Pathology", "General Consultations", "Weight Management", "Health Screenings", "Physiotherapy Referrals", "Ear Syringing", "Women's Health"],
    borough: "Manchester",
    postcode: "M20 1BH",
    address: "471-475 Princess Road, Withington, Manchester, M20 1BH",
    phone: "0161 4457805",
    acceptingNewPatients: "yes",
    description: "Princess Road Surgery is a patient-focused GP surgery in Manchester, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Polish"],
    eveningWeekend: false,
  },
  {
    id: "311",
    name: "Urmston Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "P91006",
    services: ["Contraception & Family Planning", "Blood Tests & Pathology", "Physiotherapy Referrals", "Minor Surgery", "Diabetes Management", "Blood Pressure Monitoring", "Mental Health Support", "New Patient Registration", "Smoking Cessation", "Asthma & COPD Management"],
    borough: "Manchester",
    postcode: "M41 8AA",
    address: "6 Woodsend Crescent Road, Urmston, Manchester, M41 8AA",
    phone: "0161 7559870",
    acceptingNewPatients: "yes",
    description: "Urmston Group Practice is a well-established GP practice in Manchester, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali"],
    eveningWeekend: false,
  },
  {
    id: "312",
    name: "Speke Hc - Dr Singh & Dr Bicha",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N82650",
    services: ["Ear Syringing", "Joint Injections", "General Consultations", "New Patient Registration", "Weight Management", "Mental Health Support", "Physiotherapy Referrals", "Men's Health", "Blood Pressure Monitoring", "Child Health & Development"],
    borough: "Liverpool",
    postcode: "L24 2SF",
    address: "Speke Neighbourhood Health Centre, 75 South Parade, Speke, Liverpool, L24 2SF",
    phone: "0151 2958810",
    acceptingNewPatients: "yes",
    description: "Speke Hc - Dr Singh & Dr Bicha is a well-established GP practice in Liverpool, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French", "Urdu", "Portuguese"],
    eveningWeekend: false,
  },
  {
    id: "313",
    name: "Minor Surgery St James",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y03210",
    services: ["Minor Surgery", "Women's Health", "Men's Health", "Diabetes Management", "Weight Management", "Wound Care", "Blood Pressure Monitoring", "New Patient Registration", "Travel Vaccinations"],
    borough: "Liverpool",
    postcode: "L1 5DZ",
    address: "Medical & Community Centre, 29 Great George Square, Liverpool, L1 5DZ",
    phone: "0151 7091120",
    acceptingNewPatients: "yes",
    description: "Located in Liverpool, Minor Surgery St James is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Cantonese", "Hindi", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "314",
    name: "Rocky Lane M.C",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y03099",
    services: ["Vaccinations & Immunisations", "Asthma & COPD Management", "Blood Tests & Pathology", "Diabetes Management", "Contraception & Family Planning", "Wound Care"],
    borough: "Liverpool",
    postcode: "L16 1JD",
    address: "80 Rocky Lane, Childwall, Liverpool, L16 1JD",
    phone: "0151 2953965",
    acceptingNewPatients: "yes",
    description: "Rocky Lane M.C is a patient-focused GP surgery in Liverpool, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Spanish", "Arabic", "French", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "315",
    name: "Concept House Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N84038",
    services: ["Joint Injections", "Cervical Screening", "Women's Health", "Diabetes Management", "Weight Management", "Men's Health"],
    borough: "Liverpool",
    postcode: "L20 3BG",
    address: "17 Merton Road, , Bootle, L20 3BG",
    phone: "0151 4767962",
    acceptingNewPatients: "yes",
    description: "Concept House Surgery provides high-quality NHS healthcare services in Liverpool, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Cantonese", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "318",
    name: "Dingle Park Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N82033",
    services: ["Child Health & Development", "Travel Vaccinations", "Vaccinations & Immunisations", "Contraception & Family Planning", "Physiotherapy Referrals", "Blood Tests & Pathology", "Smoking Cessation", "Diabetes Management"],
    borough: "Liverpool",
    postcode: "L8 6QP",
    address: "Park Street, Toxteth, Liverpool, L8 6QP",
    phone: "0151 2959228",
    acceptingNewPatients: "yes",
    description: "Serving the Liverpool community, Dingle Park Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish", "Punjabi"],
    eveningWeekend: false,
  },
  {
    id: "319",
    name: "Greenbank Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N82079",
    services: ["Travel Vaccinations", "Postnatal Care", "Wound Care", "Chronic Disease Management", "Blood Tests & Pathology", "Diabetes Management", "Ear Syringing", "General Consultations"],
    borough: "Liverpool",
    postcode: "L18 1HG",
    address: "1B Greenbank Road, , Liverpool, L18 1HG",
    phone: "0151 7333224",
    acceptingNewPatients: "yes",
    description: "Greenbank Road Surgery provides high-quality NHS healthcare services in Liverpool, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Romanian"],
    eveningWeekend: true,
  },
  {
    id: "320",
    name: "Seaforth Village Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "N84043",
    services: ["Vaccinations & Immunisations", "Mental Health Support", "Wound Care", "Diabetes Management", "Child Health & Development", "General Consultations", "New Patient Registration", "Men's Health", "Women's Health", "Blood Pressure Monitoring"],
    borough: "Liverpool",
    postcode: "L21 3TA",
    address: "20 Seaforth Road, , Liverpool, L21 3TA",
    phone: "0151 9491717",
    acceptingNewPatients: "yes",
    description: "Seaforth Village Surgery provides high-quality NHS healthcare services in Liverpool, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Cantonese"],
    eveningWeekend: false,
  },
  {
    id: "321",
    name: "Adult Adhd Cheshire",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y06765",
    services: ["Cervical Screening", "Physiotherapy Referrals", "Child Health & Development", "Ear Syringing", "Smoking Cessation", "Asthma & COPD Management", "Women's Health", "General Consultations", "Health Screenings", "Mental Health Support"],
    borough: "Liverpool",
    postcode: "L31 2HB",
    address: "Adhd & Psychiatry Service, Maghull Business Centre, 1 Liverpool Road North, Liverpool, L31 2HB",
    phone: "07305 963230",
    acceptingNewPatients: "yes",
    description: "Serving the Liverpool community, Adult Adhd Cheshire provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "Romanian", "French", "Spanish"],
    eveningWeekend: false,
  },
  {
    id: "322",
    name: "Dr G Lees & Partners",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86003",
    services: ["Contraception & Family Planning", "Men's Health", "Minor Surgery", "Blood Pressure Monitoring", "Asthma & COPD Management", "New Patient Registration"],
    borough: "Leeds",
    postcode: "LS12 3HD",
    address: "Armley Moor Medical Centre, 95 Town Street, Leeds, LS12 3HD",
    phone: "0113 4677499",
    acceptingNewPatients: "yes",
    description: "Dr G Lees & Partners provides high-quality NHS healthcare services in Leeds, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Portuguese", "Bengali", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "323",
    name: "Abbey Grange Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86068",
    services: ["Physiotherapy Referrals", "Weight Management", "Joint Injections", "General Consultations", "Mental Health Support", "Travel Vaccinations", "Asthma & COPD Management"],
    borough: "Leeds",
    postcode: "LS5 3JN",
    address: "Abbey Medical Centre, Norman Street, Leeds, LS5 3JN",
    phone: "0113 2951844",
    acceptingNewPatients: "yes",
    description: "Serving the Leeds community, Abbey Grange Medical Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Turkish", "Portuguese", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "324",
    name: "Lch - Home Ward (Frailty)",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08908",
    services: ["Women's Health", "Repeat Prescriptions", "New Patient Registration", "Blood Pressure Monitoring", "Contraception & Family Planning", "Physiotherapy Referrals", "Postnatal Care", "Mental Health Support", "Joint Injections"],
    borough: "Leeds",
    postcode: "LS11 0DL",
    address: "3 White Rose Office Park, Millshaw Park Lane, Leeds, LS11 0DL",
    phone: "01132 208500",
    acceptingNewPatients: "yes",
    description: "Lch - Home Ward (Frailty) is a patient-focused GP surgery in Leeds, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "326",
    name: "Crossley Street Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86033",
    services: ["Physiotherapy Referrals", "Repeat Prescriptions", "Travel Vaccinations", "Diabetes Management", "Asthma & COPD Management", "Contraception & Family Planning", "Men's Health", "Child Health & Development", "Vaccinations & Immunisations", "Smoking Cessation"],
    borough: "Leeds",
    postcode: "LS22 6RT",
    address: "Crossley Street, , Wetherby, LS22 6RT",
    phone: "01937 543200",
    acceptingNewPatients: "yes",
    description: "Crossley Street Surgery is a patient-focused GP surgery in Leeds, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "327",
    name: "Craven Road Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86017",
    services: ["Blood Pressure Monitoring", "ECG Testing", "Women's Health", "Blood Tests & Pathology", "Travel Vaccinations", "Weight Management", "Physiotherapy Referrals"],
    borough: "Leeds",
    postcode: "LS6 2RX",
    address: "60 Craven Road, , Leeds, LS6 2RX",
    phone: "0113 2953530",
    acceptingNewPatients: "yes",
    description: "Serving the Leeds community, Craven Road Medical Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Arabic", "Punjabi", "French"],
    eveningWeekend: false,
  },
  {
    id: "328",
    name: "Leeds Eating Disorder Service",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y06098",
    services: ["General Consultations", "ECG Testing", "Vaccinations & Immunisations", "New Patient Registration", "Chronic Disease Management", "Physiotherapy Referrals", "Wound Care", "Health Screenings", "Antenatal Care"],
    borough: "Leeds",
    postcode: "LS7 3EX",
    address: "Reginald Centre, 263 Chapeltown Road, Leeds, LS7 3EX",
    phone: "0113 8434317",
    acceptingNewPatients: "yes",
    description: "Leeds Eating Disorder Service is a patient-focused GP surgery in Leeds, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "French"],
    eveningWeekend: false,
  },
  {
    id: "329",
    name: "Windsor House Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B86057",
    services: ["New Patient Registration", "Smoking Cessation", "Health Screenings", "Blood Pressure Monitoring", "Child Health & Development", "Postnatal Care", "Mental Health Support", "ECG Testing"],
    borough: "Leeds",
    postcode: "LS27 9NB",
    address: "2 Corporation Street, Morley, Leeds, LS27 9NB",
    phone: "0333 242 7777",
    acceptingNewPatients: "yes",
    description: "Windsor House Group Practice offers friendly and professional GP services in Leeds, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Portuguese", "Gujarati", "Polish", "Bengali"],
    eveningWeekend: true,
  },
  {
    id: "331",
    name: "Leeds North West Pct Vasectomy Service",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y00291",
    services: ["Child Health & Development", "Minor Surgery", "Health Screenings", "Mental Health Support", "Cervical Screening", "Antenatal Care", "Blood Tests & Pathology", "Asthma & COPD Management", "Travel Vaccinations", "New Patient Registration"],
    borough: "Leeds",
    postcode: "LS16 6QG",
    address: "North West House, Ring Road, West Park, Leeds, LS16 6QG",
    phone: "0113 3057120",
    acceptingNewPatients: "yes",
    description: "Serving the Leeds community, Leeds North West Pct Vasectomy Service provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Hindi", "Cantonese"],
    eveningWeekend: false,
  },
  {
    id: "332",
    name: "The Milton Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81058",
    services: ["Chronic Disease Management", "Repeat Prescriptions", "Blood Pressure Monitoring", "ECG Testing", "Women's Health", "Contraception & Family Planning", "New Patient Registration", "Men's Health", "Joint Injections"],
    borough: "Bristol",
    postcode: "BS22 8AG",
    address: "232-234 Milton Road, , Weston-Super-Mare, BS22 8AG",
    phone: "01934 249554",
    acceptingNewPatients: "yes",
    description: "The Milton Surgery is a well-established GP practice in Bristol, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Cantonese", "Urdu", "Punjabi"],
    eveningWeekend: true,
  },
  {
    id: "333",
    name: "Fireclay Health",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81062",
    services: ["Ear Syringing", "Vaccinations & Immunisations", "ECG Testing", "Weight Management", "Mental Health Support", "Physiotherapy Referrals", "Antenatal Care", "Smoking Cessation"],
    borough: "Bristol",
    postcode: "BS5 7PH",
    address: "Bellevue Road, St George, Bristol, BS5 7PH",
    phone: "01179 612161",
    acceptingNewPatients: "yes",
    description: "Fireclay Health is a patient-focused GP surgery in Bristol, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese"],
    eveningWeekend: false,
  },
  {
    id: "334",
    name: "Skylark Rehabilitation Unit",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y05975",
    services: ["Mental Health Support", "Cervical Screening", "Child Health & Development", "Wound Care", "Chronic Disease Management", "ECG Testing"],
    borough: "Bristol",
    postcode: "BS37 5DW",
    address: "The Meadows, Cranleigh Court Road, Yate, Bristol, BS37 5DW",
    phone: "0300 1245490",
    acceptingNewPatients: "yes",
    description: "Skylark Rehabilitation Unit provides high-quality NHS healthcare services in Bristol, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic"],
    eveningWeekend: false,
  },
  {
    id: "335",
    name: "St Mary Street Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81103",
    services: ["Physiotherapy Referrals", "Child Health & Development", "Minor Surgery", "Chronic Disease Management", "Men's Health", "Smoking Cessation", "Blood Tests & Pathology", "Cervical Screening", "Women's Health"],
    borough: "Bristol",
    postcode: "BS35 2AT",
    address: "24 St. Mary Street, Thornbury, Bristol, BS35 2AT",
    phone: "01454 413691",
    acceptingNewPatients: "yes",
    description: "St Mary Street Surgery is a well-established GP practice in Bristol, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Bengali", "Turkish", "Hindi"],
    eveningWeekend: false,
  },
  {
    id: "336",
    name: "Courtside Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81024",
    services: ["Postnatal Care", "Cervical Screening", "Smoking Cessation", "Diabetes Management", "Chronic Disease Management", "Ear Syringing", "Blood Tests & Pathology", "Vaccinations & Immunisations", "Child Health & Development"],
    borough: "Bristol",
    postcode: "BS37 4DQ",
    address: "Kennedy Way, Yate, Bristol, BS37 4DQ",
    phone: "01454 313874",
    acceptingNewPatients: "yes",
    description: "Courtside Surgery provides high-quality NHS healthcare services in Bristol, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "337",
    name: "Mendip Vale Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81086",
    services: ["Contraception & Family Planning", "Diabetes Management", "Wound Care", "New Patient Registration", "Antenatal Care", "Asthma & COPD Management"],
    borough: "Bristol",
    postcode: "BS40 5EL",
    address: "Langford Surgery, Pudding Pie Lane, Langford, Bristol, BS40 5EL",
    phone: "01934 839820",
    acceptingNewPatients: "yes",
    description: "Mendip Vale Medical Practice is a patient-focused GP surgery in Bristol, offering both routine and specialist primary care services.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Cantonese", "Somali"],
    eveningWeekend: false,
  },
  {
    id: "338",
    name: "Heywood Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L81085",
    services: ["Contraception & Family Planning", "Minor Surgery", "Physiotherapy Referrals", "Men's Health", "Wound Care", "Women's Health", "ECG Testing"],
    borough: "Bristol",
    postcode: "BS20 0DL",
    address: "Lodway Gardens, Pill, Bristol, BS20 0DL",
    phone: "01275 372105",
    acceptingNewPatients: "yes",
    description: "Heywood Family Practice is a well-established GP practice in Bristol, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish"],
    eveningWeekend: false,
  },
  
  {
    id: "340",
    name: "Mayfield Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A88004",
    services: ["Health Screenings", "Minor Surgery", "Blood Tests & Pathology", "Mental Health Support", "Vaccinations & Immunisations", "Joint Injections"],
    borough: "Newcastle",
    postcode: "NE32 5SE",
    address: "Park Road, , Jarrow, NE32 5SE",
    phone: "0191 4897183",
    acceptingNewPatients: "yes",
    description: "Mayfield Medical Group provides high-quality NHS healthcare services in Newcastle, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Portuguese", "Romanian", "Polish"],
    eveningWeekend: false,
  },
  {
    id: "341",
    name: "Cedars Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A83038",
    services: ["Contraception & Family Planning", "Cervical Screening", "Men's Health", "Minor Surgery", "Child Health & Development", "Health Screenings"],
    borough: "Newcastle",
    postcode: "NE16 6HU",
    address: "Cedar Crescent, Burnopfield, Newcastle Upon Tyne, NE16 6HU",
    phone: "01207 272272",
    acceptingNewPatients: "yes",
    description: "Serving the Newcastle community, Cedars Medical Group provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Gujarati", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "342",
    name: "Benfield Park Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A86023",
    services: ["Blood Pressure Monitoring", "Contraception & Family Planning", "Asthma & COPD Management", "Travel Vaccinations", "Mental Health Support", "New Patient Registration", "Vaccinations & Immunisations", "Joint Injections", "Health Screenings", "Men's Health"],
    borough: "Newcastle",
    postcode: "NE6 4QD",
    address: "Benfield Road, , Newcastle Upon Tyne, NE6 4QD",
    phone: "0191 2821010",
    acceptingNewPatients: "yes",
    description: "Benfield Park Medical Group is a well-established GP practice in Newcastle, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "French", "Spanish", "Portuguese", "Hindi"],
    eveningWeekend: false,
  },
  {
    id: "343",
    name: "Milehouse Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M83697",
    services: ["Men's Health", "Contraception & Family Planning", "ECG Testing", "Wound Care", "Blood Pressure Monitoring", "Vaccinations & Immunisations", "Minor Surgery"],
    borough: "Newcastle",
    postcode: "ST5 9GA",
    address: "Milehouse Primary Care Centre, Lymebrook Way, Milehouse, Newcastle, ST5 9GA",
    phone: "01782 663830",
    acceptingNewPatients: "yes",
    description: "Serving the Newcastle community, Milehouse Medical Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Punjabi", "Polish", "Portuguese"],
    eveningWeekend: false,
  },
  {
    id: "344",
    name: "Collingwood Health Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A87004",
    services: ["Physiotherapy Referrals", "Repeat Prescriptions", "Minor Surgery", "Smoking Cessation", "Blood Pressure Monitoring", "Diabetes Management", "Health Screenings", "Cervical Screening"],
    borough: "Newcastle",
    postcode: "NE29 0SF",
    address: "Hawkeys Lane, , North Shields, NE29 0SF",
    phone: "0191 2571779",
    acceptingNewPatients: "yes",
    description: "Located in Newcastle, Collingwood Health Group is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish"],
    eveningWeekend: false,
  },
  
  {
    id: "347",
    name: "Wooler Health",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A84604",
    services: ["Child Health & Development", "Women's Health", "Joint Injections", "Blood Tests & Pathology", "New Patient Registration", "Cervical Screening", "Chronic Disease Management", "General Consultations", "Weight Management"],
    borough: "Newcastle",
    postcode: "NE71 6BL",
    address: "Cheviot Primary Care Centre, Padgepool Place, Wooler, NE71 6BL",
    phone: "01668 263852",
    acceptingNewPatients: "yes",
    description: "Wooler Health offers friendly and professional GP services in Newcastle, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Hindi"],
    eveningWeekend: false,
  },
  {
    id: "348",
    name: "Ecclesfield Group Pract",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C88039",
    services: ["Vaccinations & Immunisations", "Asthma & COPD Management", "ECG Testing", "Physiotherapy Referrals", "Blood Tests & Pathology", "Postnatal Care"],
    borough: "Sheffield",
    postcode: "S35 9XQ",
    address: "96A Mill Road, Ecclesfield, Sheffield, S35 9XQ",
    phone: "0114 2469030",
    acceptingNewPatients: "yes",
    description: "Ecclesfield Group Pract is a well-established GP practice in Sheffield, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Arabic", "Mandarin", "Spanish"],
    eveningWeekend: false,
  },
  {
    id: "349",
    name: "High Street Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C87018",
    services: ["Weight Management", "Asthma & COPD Management", "Health Screenings", "Chronic Disease Management", "Antenatal Care", "Blood Pressure Monitoring", "New Patient Registration", "Minor Surgery", "Contraception & Family Planning", "General Consultations"],
    borough: "Sheffield",
    postcode: "S62 6LW",
    address: "High Street, Rawmarsh, Rotherham, S62 6LW",
    phone: "01709 522022",
    acceptingNewPatients: "yes",
    description: "Serving the Sheffield community, High Street Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Romanian", "Gujarati", "Tamil"],
    eveningWeekend: true,
  },
  {
    id: "350",
    name: "Dr Raolu'S Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C87031",
    services: ["Diabetes Management", "General Consultations", "Asthma & COPD Management", "Joint Injections", "ECG Testing", "Postnatal Care", "Ear Syringing"],
    borough: "Sheffield",
    postcode: "S66 8JE",
    address: "Braithwell Road, Maltby, Rotherham, S66 8JE",
    phone: "0845 1228589",
    acceptingNewPatients: "yes",
    description: "Serving the Sheffield community, Dr Raolu'S Practice provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Portuguese", "Hindi"],
    eveningWeekend: false,
  },
  {
    id: "351",
    name: "The Dove Valley Pms Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C85007",
    services: ["Physiotherapy Referrals", "Antenatal Care", "Minor Surgery", "Postnatal Care", "Ear Syringing", "Blood Pressure Monitoring", "Travel Vaccinations", "Cervical Screening", "Wound Care", "New Patient Registration"],
    borough: "Sheffield",
    postcode: "S70 5NZ",
    address: "Worsbrough Healthcare Centre, Powell Street, Worsbrough, Barnsley, S70 5NZ",
    phone: "01226 648150",
    acceptingNewPatients: "yes",
    description: "The Dove Valley Pms Practice offers friendly and professional GP services in Sheffield, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "French", "Portuguese", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "352",
    name: "Woodhouse Ea",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y07735",
    services: ["Weight Management", "Minor Surgery", "Women's Health", "Blood Pressure Monitoring", "Asthma & COPD Management", "Health Screenings", "Vaccinations & Immunisations"],
    borough: "Sheffield",
    postcode: "S13 7LY",
    address: "5-9 Skelton Lane, Woodhouse, Sheffield, S13 7LY",
    phone: "01142 293090",
    acceptingNewPatients: "yes",
    description: "Located in Sheffield, Woodhouse Ea is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Hindi", "Turkish", "Spanish"],
    eveningWeekend: false,
  },
  {
    id: "353",
    name: "Aldine House Sch",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y06022",
    services: ["General Consultations", "Asthma & COPD Management", "Health Screenings", "Vaccinations & Immunisations", "New Patient Registration", "Physiotherapy Referrals", "Men's Health", "Smoking Cessation"],
    borough: "Sheffield",
    postcode: "S17 3ES",
    address: "75 Limb Lane, Dore, Sheffield, S17 3ES",
    phone: "0114 2621160",
    acceptingNewPatients: "yes",
    description: "Serving the Sheffield community, Aldine House Sch provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
    eveningWeekend: false,
  },
  {
    id: "354",
    name: "The Brimington Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C81058",
    services: ["ECG Testing", "Men's Health", "Repeat Prescriptions", "Mental Health Support", "Health Screenings", "Asthma & COPD Management", "Vaccinations & Immunisations"],
    borough: "Sheffield",
    postcode: "S43 1JG",
    address: "Church Street, Brimington, Chesterfield, S43 1JG",
    phone: "01246 273224",
    acceptingNewPatients: "yes",
    description: "Serving the Sheffield community, The Brimington Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Polish", "Portuguese", "Cantonese"],
    eveningWeekend: false,
  },
  {
    id: "355",
    name: "Welbeck Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C84664",
    services: ["Blood Tests & Pathology", "Minor Surgery", "Vaccinations & Immunisations", "ECG Testing", "Travel Vaccinations", "Blood Pressure Monitoring", "Smoking Cessation", "Joint Injections"],
    borough: "Nottingham",
    postcode: "NG5 2JJ",
    address: "481-491 Mansfield Road, Sherwood, Nottingham, NG5 2JJ",
    phone: "0115 9620932",
    acceptingNewPatients: "yes",
    description: "Welbeck Surgery is a well-established GP practice in Nottingham, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "French", "Hindi"],
    eveningWeekend: false,
  },
  {
    id: "356",
    name: "Erewash Electronic Referral Module Le",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y05366",
    services: ["Blood Tests & Pathology", "Minor Surgery", "Vaccinations & Immunisations", "Men's Health", "Asthma & COPD Management", "Women's Health", "Travel Vaccinations", "Joint Injections"],
    borough: "Nottingham",
    postcode: "NG10 1RY",
    address: "Long Eaton Health Centre, Midland Street, Long Eaton, Nottingham, NG10 1RY",
    phone: "0115 8554000",
    acceptingNewPatients: "yes",
    description: "Erewash Electronic Referral Module Le offers friendly and professional GP services in Nottingham, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "357",
    name: "Hounsfield Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C84660",
    services: ["Vaccinations & Immunisations", "Postnatal Care", "Mental Health Support", "Repeat Prescriptions", "Travel Vaccinations", "Physiotherapy Referrals", "Contraception & Family Planning", "Antenatal Care", "New Patient Registration"],
    borough: "Nottingham",
    postcode: "NG23 6PX",
    address: "Hounsfield Way, Sutton-On-Trent, Newark, NG23 6PX",
    phone: "01636 821023",
    acceptingNewPatients: "yes",
    description: "Hounsfield Surgery offers friendly and professional GP services in Nottingham, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish", "Cantonese"],
    eveningWeekend: false,
  },
  {
    id: "358",
    name: "Rise Park Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C84129",
    services: ["Postnatal Care", "Wound Care", "Antenatal Care", "Joint Injections", "Physiotherapy Referrals", "Smoking Cessation", "Asthma & COPD Management", "Vaccinations & Immunisations", "Contraception & Family Planning"],
    borough: "Nottingham",
    postcode: "NG5 5EB",
    address: "Off Revelstoke Way, , Nottingham, NG5 5EB",
    phone: "0115 9272525",
    acceptingNewPatients: "yes",
    description: "Rise Park Surgery is a patient-focused GP surgery in Nottingham, offering both routine and specialist primary care services.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Gujarati", "Bengali", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "359",
    name: "Jubilee Park Medical Partnership",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C84613",
    services: ["Blood Pressure Monitoring", "Cervical Screening", "Travel Vaccinations", "Asthma & COPD Management", "Repeat Prescriptions", "Women's Health", "Minor Surgery", "Contraception & Family Planning", "Vaccinations & Immunisations", "Health Screenings"],
    borough: "Nottingham",
    postcode: "NG4 3DQ",
    address: "61 Burton Road, Carlton, Nottingham, NG4 3DQ",
    phone: "01159 404333",
    acceptingNewPatients: "yes",
    description: "Jubilee Park Medical Partnership is a patient-focused GP surgery in Nottingham, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
    eveningWeekend: false,
  },
  {
    id: "360",
    name: "Ihss",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y01097",
    services: ["Blood Pressure Monitoring", "Cervical Screening", "Chronic Disease Management", "Ear Syringing", "Mental Health Support", "Diabetes Management", "ECG Testing"],
    borough: "Nottingham",
    postcode: "NG19 7JZ",
    address: "Bull Frm Pcc,Concorde Way, Millennium Business Park, Mansfield, NG19 7JZ",
    phone: "01623 621059",
    acceptingNewPatients: "yes",
    description: "Ihss is a well-established GP practice in Nottingham, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Mandarin"],
    eveningWeekend: true,
  },
  {
    id: "361",
    name: "Willowbrook Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C84012",
    services: ["Blood Tests & Pathology", "Joint Injections", "Chronic Disease Management", "Antenatal Care", "Minor Surgery", "Child Health & Development"],
    borough: "Nottingham",
    postcode: "NG17 1ES",
    address: "Brook Street, , Sutton-In-Ashfield, NG17 1ES",
    phone: "01623 440018",
    acceptingNewPatients: "yes",
    description: "Willowbrook Medical Practice provides high-quality NHS healthcare services in Nottingham, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French"],
    eveningWeekend: false,
  },
  {
    id: "362",
    name: "Turning Point Leicestershre County",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y05282",
    services: ["Weight Management", "Blood Pressure Monitoring", "Minor Surgery", "Joint Injections", "Contraception & Family Planning", "Diabetes Management", "Antenatal Care"],
    borough: "Leicester",
    postcode: "LE1 5RA",
    address: "38 Friar Lane, , Leicester, LE1 5RA",
    phone: "07909 232520",
    acceptingNewPatients: "yes",
    description: "Turning Point Leicestershre County provides high-quality NHS healthcare services in Leicester, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali", "Turkish", "Urdu", "Punjabi"],
    eveningWeekend: false,
  },
  {
    id: "364",
    name: "Village Health Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C84005",
    services: ["Physiotherapy Referrals", "Weight Management", "Repeat Prescriptions", "Health Screenings", "Joint Injections", "Contraception & Family Planning", "Cervical Screening", "Child Health & Development", "Blood Tests & Pathology"],
    borough: "Leicester",
    postcode: "LE12 6JG",
    address: "East Leake Health Centre, Gotham Road, East Leake, Loughborough, LE12 6JG",
    phone: "01509 852181",
    acceptingNewPatients: "yes",
    description: "Village Health Group provides high-quality NHS healthcare services in Leicester, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish"],
    eveningWeekend: false,
  },
  
  
  {
    id: "367",
    name: "Health Suite",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y07253",
    services: ["Smoking Cessation", "Health Screenings", "ECG Testing", "Antenatal Care", "Physiotherapy Referrals", "Mental Health Support"],
    borough: "Leicester",
    postcode: "LE7 9QB",
    address: "624A Uppingham Road, Thurnby, Leicester, LE7 9QB",
    phone: "07732 434496",
    acceptingNewPatients: "yes",
    description: "Health Suite is a patient-focused GP surgery in Leicester, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Spanish", "Portuguese", "Punjabi"],
    eveningWeekend: false,
  },
  {
    id: "368",
    name: "Bushloe Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C82013",
    services: ["New Patient Registration", "Joint Injections", "Minor Surgery", "Child Health & Development", "ECG Testing", "Blood Tests & Pathology", "Repeat Prescriptions", "Weight Management", "Asthma & COPD Management", "General Consultations"],
    borough: "Leicester",
    postcode: "LE18 2EW",
    address: "Two Steeples Med Centre, Abington Close, Wigston, LE18 2EW",
    phone: "0116 3440233",
    acceptingNewPatients: "yes",
    description: "Bushloe Surgery offers friendly and professional GP services in Leicester, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali"],
    eveningWeekend: false,
  },
  
  
  {
    id: "372",
    name: "Chapel End Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M84005",
    services: ["Cervical Screening", "Blood Pressure Monitoring", "Contraception & Family Planning", "Joint Injections", "New Patient Registration", "Chronic Disease Management", "Repeat Prescriptions", "Minor Surgery", "Weight Management", "Smoking Cessation"],
    borough: "Coventry",
    postcode: "CV10 0JH",
    address: "10 Camp Hill Road, , Nuneaton, CV10 0JH",
    phone: "024 76393388",
    acceptingNewPatients: "yes",
    description: "Chapel End Surgery provides high-quality NHS healthcare services in Coventry, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese"],
    eveningWeekend: false,
  },
  {
    id: "373",
    name: "Eating Disorders",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02233",
    services: ["Diabetes Management", "Health Screenings", "Wound Care", "Child Health & Development", "Repeat Prescriptions", "Antenatal Care", "Minor Surgery", "Weight Management"],
    borough: "Coventry",
    postcode: "CV34 5BW",
    address: "Aspen Centre, Lakin Road, Warwick, CV34 5BW",
    phone: "01926 410281",
    acceptingNewPatients: "yes",
    description: "Eating Disorders is a patient-focused GP surgery in Coventry, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "French", "Gujarati"],
    eveningWeekend: false,
  },
  
  {
    id: "375",
    name: "The Saltaire & Windhill Medical P'Ship",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B83039",
    services: ["Mental Health Support", "Blood Tests & Pathology", "Men's Health", "Repeat Prescriptions", "Minor Surgery", "Antenatal Care", "Weight Management"],
    borough: "Bradford",
    postcode: "BD18 1QB",
    address: "Windhill Green Medic Prac, 2 Thackley Old Road, Shipley, BD18 1QB",
    phone: "01274 584223",
    acceptingNewPatients: "yes",
    description: "The Saltaire & Windhill Medical P'Ship is a well-established GP practice in Bradford, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish"],
    eveningWeekend: false,
  },
  {
    id: "381",
    name: "Carden Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G81014",
    services: ["Child Health & Development", "Women's Health", "Mental Health Support", "Wound Care", "Men's Health", "Vaccinations & Immunisations"],
    borough: "Brighton",
    postcode: "BN1 8DD",
    address: "County Oak Medical Centre, Carden Hill, Brighton, BN1 8DD",
    phone: "01273 500155",
    acceptingNewPatients: "yes",
    description: "Carden Surgery is a patient-focused GP surgery in Brighton, offering both routine and specialist primary care services.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu"],
    eveningWeekend: false,
  },
  
  {
    id: "383",
    name: "Com Nursing Cissbury Integrated Care",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08136",
    services: ["Contraception & Family Planning", "Chronic Disease Management", "New Patient Registration", "Minor Surgery", "Mental Health Support", "Wound Care", "Men's Health"],
    borough: "Brighton",
    postcode: "BN13 2RX",
    address: "Durrington Health Centre, Durrington Lane, Worthing, BN13 2RX",
    phone: "01273 265870",
    acceptingNewPatients: "yes",
    description: "Com Nursing Cissbury Integrated Care is a patient-focused GP surgery in Brighton, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish", "Mandarin", "Arabic", "Cantonese"],
    eveningWeekend: true,
  },
  {
    id: "384",
    name: "Broadway Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G81669",
    services: ["Postnatal Care", "Blood Tests & Pathology", "Contraception & Family Planning", "Travel Vaccinations", "Joint Injections", "Men's Health", "Wound Care", "Minor Surgery", "Chronic Disease Management", "Mental Health Support"],
    borough: "Brighton",
    postcode: "BN2 5FL",
    address: "Wellsbourne Health Centre, 179 Whitehawk Road, Brighton, BN2 5FL",
    phone: "01273 600888",
    acceptingNewPatients: "yes",
    description: "Serving the Brighton community, Broadway Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Spanish", "Punjabi", "Arabic"],
    eveningWeekend: false,
  },
  {
    id: "385",
    name: "Lansdowne Sch",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y06988",
    services: ["Postnatal Care", "ECG Testing", "Women's Health", "Ear Syringing", "Contraception & Family Planning", "Cervical Screening"],
    borough: "Brighton",
    postcode: "BN27 1NP",
    address: "Hawks Road, , Hailsham, BN27 1NP",
    phone: "01323 466270",
    acceptingNewPatients: "yes",
    description: "Lansdowne Sch is a well-established GP practice in Brighton, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Bengali", "Spanish", "Cantonese"],
    eveningWeekend: false,
  },
  
  
  {
    id: "388",
    name: "Old Bridge Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L82022",
    services: ["Contraception & Family Planning", "Asthma & COPD Management", "Postnatal Care", "Blood Pressure Monitoring", "Minor Surgery", "Diabetes Management", "Ear Syringing", "Wound Care", "Child Health & Development"],
    borough: "Plymouth",
    postcode: "PL13 1HA",
    address: "The Looe Health Centre, Station Road, East Looe, Looe, PL13 1HA",
    phone: "01503 266960",
    acceptingNewPatients: "yes",
    description: "Old Bridge Surgery is a well-established GP practice in Plymouth, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali", "Polish"],
    eveningWeekend: false,
  },
  {
    id: "389",
    name: "Church View Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L83064",
    services: ["Physiotherapy Referrals", "Smoking Cessation", "Postnatal Care", "Health Screenings", "Contraception & Family Planning", "Cervical Screening", "New Patient Registration", "Travel Vaccinations", "General Consultations"],
    borough: "Plymouth",
    postcode: "PL9 9BN",
    address: "30 Holland Road, Plymstock, Plymouth, PL9 9BN",
    phone: "01752 403206",
    acceptingNewPatients: "yes",
    description: "Church View Surgery provides high-quality NHS healthcare services in Plymouth, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "390",
    name: "Stoke Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L83071",
    services: ["Postnatal Care", "Travel Vaccinations", "New Patient Registration", "Contraception & Family Planning", "ECG Testing", "Antenatal Care", "Vaccinations & Immunisations"],
    borough: "Plymouth",
    postcode: "PL3 4DP",
    address: "Belmont Villas, Stoke, Plymouth, PL3 4DP",
    phone: "01752 562569",
    acceptingNewPatients: "yes",
    description: "Serving the Plymouth community, Stoke Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "391",
    name: "Kccg-Little Harbour",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y04179",
    services: ["Smoking Cessation", "Postnatal Care", "Health Screenings", "Cervical Screening", "ECG Testing", "Joint Injections", "Vaccinations & Immunisations", "Chronic Disease Management", "Physiotherapy Referrals", "Mental Health Support"],
    borough: "Plymouth",
    postcode: "PL26 6AZ",
    address: "Little Harbour, Porthpean Rd, Porthpean, St Austell, PL26 6AZ",
    phone: "01726 627953",
    acceptingNewPatients: "yes",
    description: "Located in Plymouth, Kccg-Little Harbour is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "French", "Hindi", "Arabic"],
    eveningWeekend: false,
  },
  {
    id: "392",
    name: "Balmore Park Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "K81014",
    services: ["Women's Health", "Men's Health", "Wound Care", "Diabetes Management", "Health Screenings", "Weight Management", "Joint Injections", "General Consultations"],
    borough: "Reading",
    postcode: "RG4 7SS",
    address: "59A Hemdean Road, Caversham, Reading, RG4 7SS",
    phone: "0118 9471455",
    acceptingNewPatients: "yes",
    description: "Balmore Park Surgery is a patient-focused GP surgery in Reading, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Mandarin", "Punjabi", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "393",
    name: "Via West Berkshire",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y07269",
    services: ["Weight Management", "ECG Testing", "Women's Health", "General Consultations", "Blood Tests & Pathology", "Physiotherapy Referrals", "Vaccinations & Immunisations"],
    borough: "Reading",
    postcode: "RG14 7LP",
    address: "1 Station Road, , Newbury, RG14 7LP",
    phone: "03003 034554",
    acceptingNewPatients: "yes",
    description: "Via West Berkshire is a well-established GP practice in Reading, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish", "Gujarati", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "394",
    name: "Camhs - Bracknell/M221001",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y04915",
    services: ["Child Health & Development", "Cervical Screening", "Postnatal Care", "Men's Health", "Blood Pressure Monitoring", "Women's Health", "Contraception & Family Planning"],
    borough: "Reading",
    postcode: "RG12 7FR",
    address: "Camhs - Churchill Hse, 51-52 Turing Drive, Bracknell, RG12 7FR",
    phone: "01344 823341",
    acceptingNewPatients: "yes",
    description: "Camhs - Bracknell/M221001 is a patient-focused GP surgery in Reading, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Tamil"],
    eveningWeekend: false,
  },
  
  {
    id: "396",
    name: "Grovelands Pharmacy",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08255",
    services: ["Chronic Disease Management", "Diabetes Management", "New Patient Registration", "Travel Vaccinations", "Weight Management", "Wound Care", "Vaccinations & Immunisations", "General Consultations", "Repeat Prescriptions"],
    borough: "Reading",
    postcode: "RG30 2NY",
    address: "2 Grovelands Road, , Reading, RG30 2NY",
    phone: "01189 572389",
    acceptingNewPatients: "yes",
    description: "Grovelands Pharmacy is a patient-focused GP surgery in Reading, offering both routine and specialist primary care services.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "French", "Mandarin", "Portuguese"],
    eveningWeekend: false,
  },
  {
    id: "398",
    name: "Luther Street Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "K84066",
    services: ["Postnatal Care", "ECG Testing", "Contraception & Family Planning", "Wound Care", "General Consultations", "Smoking Cessation", "Mental Health Support", "Vaccinations & Immunisations", "Weight Management"],
    borough: "Oxford",
    postcode: "OX1 1UL",
    address: "Luther Street, , Oxford, OX1 1UL",
    phone: "01865 726008",
    acceptingNewPatients: "yes",
    description: "Luther Street Medical Practice is a patient-focused GP surgery in Oxford, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "French", "Arabic"],
    eveningWeekend: true,
  },
  {
    id: "399",
    name: "I.C.B. (Oxford City)",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y01822",
    services: ["Men's Health", "Chronic Disease Management", "Vaccinations & Immunisations", "General Consultations", "Smoking Cessation", "Blood Tests & Pathology", "Repeat Prescriptions", "Blood Pressure Monitoring"],
    borough: "Oxford",
    postcode: "OX28 6JQ",
    address: "C/O Nuffield Health Centre, Windrush Way, Witney, OX28 6JQ",
    phone: "01993 703641",
    acceptingNewPatients: "yes",
    description: "I.C.B. (Oxford City) is a well-established GP practice in Oxford, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati"],
    eveningWeekend: false,
  },
  
  {
    id: "402",
    name: "Ccs-Contraception & Sexual Hlth Service",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y01778",
    services: ["Repeat Prescriptions", "General Consultations", "Chronic Disease Management", "Weight Management", "Mental Health Support", "Women's Health", "ECG Testing", "Blood Tests & Pathology", "Joint Injections", "Minor Surgery"],
    borough: "Cambridge",
    postcode: "CB1 3DF",
    address: "Lime Tree Clinic, 351 Mill Road, Cambridge, CB1 3DF",
    phone: "0300 3003030",
    acceptingNewPatients: "yes",
    description: "Ccs-Contraception & Sexual Hlth Service is a well-established GP practice in Cambridge, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Somali", "Gujarati", "Tamil"],
    eveningWeekend: true,
  },
  {
    id: "403",
    name: "Harston Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "D81058",
    services: ["Women's Health", "Antenatal Care", "Asthma & COPD Management", "Child Health & Development", "Blood Tests & Pathology", "Joint Injections", "Chronic Disease Management", "Ear Syringing", "Mental Health Support", "Smoking Cessation"],
    borough: "Cambridge",
    postcode: "CB22 7NP",
    address: "11 Church Street, Harston, Cambridge, CB22 7NP",
    phone: "01223 870250",
    acceptingNewPatients: "yes",
    description: "Serving the Cambridge community, Harston Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Mandarin", "Hindi", "Urdu", "Gujarati"],
    eveningWeekend: false,
  },
  {
    id: "404",
    name: "Haddenham Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "D81062",
    services: ["Wound Care", "ECG Testing", "Child Health & Development", "Travel Vaccinations", "Diabetes Management", "Minor Surgery", "Men's Health", "Antenatal Care", "Repeat Prescriptions"],
    borough: "Cambridge",
    postcode: "CB6 3TA",
    address: "The Green, Haddenham, Ely, CB6 3TA",
    phone: "01353 740205",
    acceptingNewPatients: "yes",
    description: "Haddenham Surgery offers friendly and professional GP services in Cambridge, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese"],
    eveningWeekend: true,
  },
  {
    id: "405",
    name: "Newport Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F81034",
    services: ["Blood Tests & Pathology", "New Patient Registration", "Vaccinations & Immunisations", "Mental Health Support", "Health Screenings", "Child Health & Development"],
    borough: "Cambridge",
    postcode: "CB11 3PY",
    address: "Frambury Lane, Newport, Saffron Walden, CB11 3PY",
    phone: "01799 540570",
    acceptingNewPatients: "yes",
    description: "Serving the Cambridge community, Newport Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese", "Hindi", "Punjabi"],
    eveningWeekend: false,
  },
  {
    id: "406",
    name: "Bottisham Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "D81055",
    services: ["Antenatal Care", "Chronic Disease Management", "Minor Surgery", "Smoking Cessation", "Travel Vaccinations", "Diabetes Management", "Health Screenings"],
    borough: "Cambridge",
    postcode: "CB25 9DU",
    address: "Tunbridge Lane, Bottisham, Cambridge, CB25 9DU",
    phone: "01223 810030",
    acceptingNewPatients: "yes",
    description: "Bottisham Medical Practice is a patient-focused GP surgery in Cambridge, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Gujarati", "Bengali", "Turkish", "Hindi"],
    eveningWeekend: true,
  },
  {
    id: "407",
    name: "Ny Horizons Selby",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B82634",
    services: ["Child Health & Development", "Chronic Disease Management", "Contraception & Family Planning", "Smoking Cessation", "Minor Surgery", "Vaccinations & Immunisations", "Mental Health Support"],
    borough: "York",
    postcode: "YO8 4ET",
    address: "74-76 Gowthorpe, , Selby, YO8 4ET",
    phone: "01723 330730",
    acceptingNewPatients: "yes",
    description: "Located in York, Ny Horizons Selby is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: true,
    languages: ["English", "Somali", "Portuguese"],
    eveningWeekend: true,
  },
  {
    id: "408",
    name: "Westhill Pharmacy",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08280",
    services: ["Contraception & Family Planning", "Vaccinations & Immunisations", "Weight Management", "Asthma & COPD Management", "Ear Syringing", "Child Health & Development", "Diabetes Management"],
    borough: "York",
    postcode: "YO16 4RB",
    address: "29 Bessingby Gate, , Bridlington, YO16 4RB",
    phone: "01262 603999",
    acceptingNewPatients: "yes",
    description: "Westhill Pharmacy is a well-established GP practice in York, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Portuguese"],
    eveningWeekend: true,
  },
  {
    id: "409",
    name: "Jorvik Gillygate Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B82098",
    services: ["Health Screenings", "Postnatal Care", "Joint Injections", "Repeat Prescriptions", "New Patient Registration", "Mental Health Support", "Asthma & COPD Management", "Travel Vaccinations"],
    borough: "York",
    postcode: "YO1 7NP",
    address: "Woolpack House, The Stonebow, York, YO1 7NP",
    phone: "01904 405487",
    acceptingNewPatients: "yes",
    description: "Jorvik Gillygate Practice provides high-quality NHS healthcare services in York, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic"],
    eveningWeekend: false,
  },
  {
    id: "410",
    name: "Yorkshire Doctors Ucc Ooh",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y04950",
    services: ["Health Screenings", "Joint Injections", "Weight Management", "Physiotherapy Referrals", "Child Health & Development", "New Patient Registration", "Travel Vaccinations", "Smoking Cessation", "Vaccinations & Immunisations"],
    borough: "York",
    postcode: "YO30 4RB",
    address: "Unit 1, Horizon Court, Audax Close, York, YO30 4RB",
    phone: "0300 1231780",
    acceptingNewPatients: "yes",
    description: "Located in York, Yorkshire Doctors Ucc Ooh is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Urdu", "Turkish", "Hindi"],
    eveningWeekend: true,
  },
  {
    id: "411",
    name: "Drs Reddy And Nunn",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "B81069",
    services: ["Weight Management", "Repeat Prescriptions", "Antenatal Care", "Minor Surgery", "Cervical Screening", "Women's Health", "Men's Health"],
    borough: "York",
    postcode: "YO16 4LZ",
    address: "Bridlington Medical Centre, Station Avenue, Bridlington, YO16 4LZ",
    phone: "01262 670686",
    acceptingNewPatients: "yes",
    description: "Drs Reddy And Nunn is a well-established GP practice in York, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "412",
    name: "Stockbridge Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "J82016",
    services: ["Women's Health", "Chronic Disease Management", "Smoking Cessation", "Child Health & Development", "Blood Pressure Monitoring", "Joint Injections", "Minor Surgery"],
    borough: "Southampton",
    postcode: "SO20 6HG",
    address: "New Street, , Stockbridge, SO20 6HG",
    phone: "01264 810524",
    acceptingNewPatients: "yes",
    description: "Stockbridge Surgery is a well-established GP practice in Southampton, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Turkish"],
    eveningWeekend: false,
  },
  {
    id: "413",
    name: "Bassett Pharmacy",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08234",
    services: ["Diabetes Management", "Travel Vaccinations", "Health Screenings", "Joint Injections", "Asthma & COPD Management", "Mental Health Support", "Ear Syringing", "Physiotherapy Referrals", "General Consultations", "New Patient Registration"],
    borough: "Southampton",
    postcode: "SO16 7AP",
    address: "19 Burgess Road, Bassett, Southampton, SO16 7AP",
    phone: "02380 790654",
    acceptingNewPatients: "yes",
    description: "Bassett Pharmacy is a well-established GP practice in Southampton, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati"],
    eveningWeekend: true,
  },
  {
    id: "414",
    name: "Gratton Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "J82106",
    services: ["Repeat Prescriptions", "Blood Pressure Monitoring", "New Patient Registration", "Physiotherapy Referrals", "Antenatal Care", "Postnatal Care", "Diabetes Management", "Chronic Disease Management"],
    borough: "Southampton",
    postcode: "SO21 3LE",
    address: "Sutton Scotney, East Stratton, Winchester, SO21 3LE",
    phone: "01962 760394",
    acceptingNewPatients: "yes",
    description: "Gratton Surgery offers friendly and professional GP services in Southampton, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese", "Somali", "Polish"],
    eveningWeekend: false,
  },
  {
    id: "415",
    name: "Brook Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "J82216",
    services: ["New Patient Registration", "General Consultations", "Diabetes Management", "Asthma & COPD Management", "Minor Surgery", "Chronic Disease Management"],
    borough: "Southampton",
    postcode: "SO31 7DQ",
    address: "233A Brook Lane, Sarisbury Green, Southampton, SO31 7DQ",
    phone: "01489 575191",
    acceptingNewPatients: "yes",
    description: "Brook Lane Surgery is a well-established GP practice in Southampton, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Gujarati"],
    eveningWeekend: false,
  },
  
  {
    id: "417",
    name: "Esplanade Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "J84005",
    services: ["Minor Surgery", "Health Screenings", "Blood Tests & Pathology", "Child Health & Development", "Travel Vaccinations", "Contraception & Family Planning", "Cervical Screening", "Joint Injections", "Asthma & COPD Management", "Chronic Disease Management"],
    borough: "Portsmouth",
    postcode: "PO33 2EH",
    address: "19 Esplanade, , Ryde, PO33 2EH",
    phone: "01983 618388",
    acceptingNewPatients: "yes",
    description: "Esplanade Surgery is a patient-focused GP surgery in Portsmouth, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Cantonese", "Tamil", "Polish"],
    eveningWeekend: false,
  },
  {
    id: "418",
    name: "West Meads Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "H82099",
    services: ["General Consultations", "Travel Vaccinations", "Women's Health", "Mental Health Support", "Smoking Cessation", "Chronic Disease Management", "Weight Management"],
    borough: "Portsmouth",
    postcode: "PO21 5SB",
    address: "2-6 The Precinct, West Meads, Bognor Regis, PO21 5SB",
    phone: "01243 837980",
    acceptingNewPatients: "yes",
    description: "West Meads Surgery is a patient-focused GP surgery in Portsmouth, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali"],
    eveningWeekend: true,
  },
  
  {
    id: "420",
    name: "The Elms Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "J82210",
    services: ["ECG Testing", "Wound Care", "Antenatal Care", "Child Health & Development", "Diabetes Management", "Mental Health Support"],
    borough: "Portsmouth",
    postcode: "PO11 9AP",
    address: "Elm Grove, Mengham, Hayling Island, PO11 9AP",
    phone: "023 92466216",
    acceptingNewPatients: "yes",
    description: "Located in Portsmouth, The Elms Practice is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Arabic", "Spanish"],
    eveningWeekend: false,
  },
  {
    id: "421",
    name: "Audiology Service Inm",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A91143",
    services: ["Health Screenings", "Chronic Disease Management", "Mental Health Support", "New Patient Registration", "General Consultations", "Diabetes Management", "Minor Surgery", "Vaccinations & Immunisations", "Women's Health"],
    borough: "Portsmouth",
    postcode: "PO12 2DL",
    address: "Crescent Road, Alverstoke, Gosport, PO12 2DL",
    phone: "0000 000 0000",
    acceptingNewPatients: "yes",
    description: "Located in Portsmouth, Audiology Service Inm is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Turkish", "Hindi", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "422",
    name: "New Silksworth Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A89032",
    services: ["Joint Injections", "Repeat Prescriptions", "Mental Health Support", "ECG Testing", "Ear Syringing", "New Patient Registration", "Blood Tests & Pathology", "Wound Care"],
    borough: "Sunderland",
    postcode: "SR3 2AN",
    address: "Silksworth Health Centre, Silksworth Road, Sunderland, SR3 2AN",
    phone: "0191 5210252",
    acceptingNewPatients: "yes",
    description: "New Silksworth Medical Practice provides high-quality NHS healthcare services in Sunderland, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Spanish", "Urdu"],
    eveningWeekend: false,
  },
  {
    id: "423",
    name: "The New City Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A89013",
    services: ["Chronic Disease Management", "Blood Pressure Monitoring", "Cervical Screening", "Mental Health Support", "New Patient Registration", "General Consultations"],
    borough: "Sunderland",
    postcode: "SR1 2QB",
    address: "Tatham Street, , Sunderland, SR1 2QB",
    phone: "0191 5675571",
    acceptingNewPatients: "yes",
    description: "Serving the Sunderland community, The New City Medical Group provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-17:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Mandarin", "Cantonese"],
    eveningWeekend: false,
  },
  {
    id: "424",
    name: "Recovery At Home - Leechmere",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y05437",
    services: ["Women's Health", "Ear Syringing", "Asthma & COPD Management", "Diabetes Management", "Repeat Prescriptions", "New Patient Registration"],
    borough: "Sunderland",
    postcode: "SR2 9TS",
    address: "Recovery At Home Service, Claymere Road, Sunderland, SR2 9TS",
    phone: "0191 5616666",
    acceptingNewPatients: "yes",
    description: "Located in Sunderland, Recovery At Home - Leechmere is committed to delivering excellent patient care through a range of medical services.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Somali"],
    eveningWeekend: true,
  },
  {
    id: "425",
    name: "Hylton Medical Group",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "A89031",
    services: ["Women's Health", "Asthma & COPD Management", "Antenatal Care", "Postnatal Care", "ECG Testing", "Wound Care", "Blood Pressure Monitoring", "New Patient Registration", "General Consultations", "Vaccinations & Immunisations"],
    borough: "Sunderland",
    postcode: "SR4 7XF",
    address: "Pallion H.C, Hylton Road, Sunderland, SR4 7XF",
    phone: "0191 5658598",
    acceptingNewPatients: "yes",
    description: "Hylton Medical Group offers friendly and professional GP services in Sunderland, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "French", "Arabic", "Tamil"],
    eveningWeekend: false,
  },
  {
    id: "426",
    name: "Dr Sinha & Tahir",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M92013",
    services: ["Diabetes Management", "Women's Health", "Child Health & Development", "Travel Vaccinations", "Blood Tests & Pathology", "Wound Care", "Ear Syringing"],
    borough: "Wolverhampton",
    postcode: "WV10 0BD",
    address: "Woden Road, , Wolverhampton, WV10 0BD",
    phone: "01902 454242",
    acceptingNewPatients: "yes",
    description: "Dr Sinha & Tahir provides high-quality NHS healthcare services in Wolverhampton, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali", "Cantonese", "Punjabi", "Urdu"],
    eveningWeekend: true,
  },
  {
    id: "427",
    name: "The Keys Family Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y02626",
    services: ["Diabetes Management", "Antenatal Care", "Wound Care", "Child Health & Development", "Travel Vaccinations", "Cervical Screening", "Minor Surgery", "Health Screenings", "Smoking Cessation", "Men's Health"],
    borough: "Wolverhampton",
    postcode: "WV13 2NY",
    address: "Willenhall Medical Centre, Field Street, Willenhall, WV13 2NY",
    phone: "01922 211272",
    acceptingNewPatients: "yes",
    description: "The Keys Family Practice provides high-quality NHS healthcare services in Wolverhampton, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Mandarin", "Romanian"],
    eveningWeekend: false,
  },
  {
    id: "428",
    name: "Newbridge Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "M92029",
    services: ["Joint Injections", "Repeat Prescriptions", "Antenatal Care", "Vaccinations & Immunisations", "New Patient Registration", "Health Screenings", "Chronic Disease Management", "Travel Vaccinations", "Child Health & Development"],
    borough: "Wolverhampton",
    postcode: "WV6 0DE",
    address: "255 Tettenhall Road, , Wolverhampton, WV6 0DE",
    phone: "01902 751420",
    acceptingNewPatients: "yes",
    description: "Newbridge Surgery is a well-established GP practice in Wolverhampton, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Punjabi", "Hindi", "Polish", "Bengali"],
    eveningWeekend: true,
  },
  {
    id: "429",
    name: "Pes@Taylor Biddle Wombourne Bc Icb",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y07961",
    services: ["Joint Injections", "Women's Health", "Weight Management", "Physiotherapy Referrals", "Blood Pressure Monitoring", "New Patient Registration", "Diabetes Management"],
    borough: "Wolverhampton",
    postcode: "WV5 9JD",
    address: "34 Windmill Bank, Wombourne, Wolverhampton, WV5 9JD",
    phone: "01902 895909",
    acceptingNewPatients: "yes",
    description: "Pes@Taylor Biddle Wombourne Bc Icb provides high-quality NHS healthcare services in Wolverhampton, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "Urdu", "Polish", "Portuguese"],
    eveningWeekend: true,
  },
  {
    id: "430",
    name: "Mickleover Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C81113",
    services: ["Blood Tests & Pathology", "Asthma & COPD Management", "Minor Surgery", "Smoking Cessation", "Cervical Screening", "Repeat Prescriptions", "General Consultations", "Joint Injections", "Antenatal Care"],
    borough: "Derby",
    postcode: "DE3 9BJ",
    address: "10 Cavendish Way, Mickleover, Derby, DE3 9BJ",
    phone: "01332 519160",
    acceptingNewPatients: "yes",
    description: "Mickleover Surgery offers friendly and professional GP services in Derby, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "Romanian", "Cantonese"],
    eveningWeekend: false,
  },
  {
    id: "431",
    name: "Lister House Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C81072",
    services: ["Diabetes Management", "Child Health & Development", "Cervical Screening", "Men's Health", "Women's Health", "Wound Care", "New Patient Registration", "Travel Vaccinations", "Health Screenings", "Physiotherapy Referrals"],
    borough: "Derby",
    postcode: "DE23 8RJ",
    address: "207 St. Thomas Road, , Derby, DE23 8RJ",
    phone: "01332 271212",
    acceptingNewPatients: "yes",
    description: "Serving the Derby community, Lister House Surgery provides a full range of NHS primary care services in a welcoming environment.",
    openingHours: "Mon 07:30-18:30, Tue 07:30-18:30, Wed 07:30-18:30, Thu 07:30-18:30, Fri 07:30-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Portuguese", "Romanian", "Urdu"],
    eveningWeekend: false,
  },
  
  {
    id: "433",
    name: "Castle Donington Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "C82007",
    services: ["Diabetes Management", "Ear Syringing", "Asthma & COPD Management", "Smoking Cessation", "Wound Care", "New Patient Registration", "ECG Testing", "Women's Health", "Chronic Disease Management"],
    borough: "Derby",
    postcode: "DE74 2LB",
    address: "53 Borough Street, Castle Donington, Derby, DE74 2LB",
    phone: "01332 856050",
    acceptingNewPatients: "yes",
    description: "Castle Donington Surgery offers friendly and professional GP services in Derby, supporting the health and wellbeing of the local community.",
    openingHours: "Mon 08:00-20:00, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-20:00, Fri 08:00-18:30, Sat 09:00-12:00, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Tamil", "Gujarati", "Mandarin"],
    eveningWeekend: true,
  },
  
  
  
  {
    id: "438",
    name: "Lewis Pharmacy",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08324",
    services: ["Repeat Prescriptions", "Blood Tests & Pathology", "Men's Health", "Health Screenings", "Vaccinations & Immunisations", "Wound Care", "Child Health & Development", "Chronic Disease Management"],
    borough: "Exeter",
    postcode: "EX8 1PY",
    address: "70 Exeter Road, , Exmouth, EX8 1PY",
    phone: "01395 272714",
    acceptingNewPatients: "yes",
    description: "Lewis Pharmacy is a well-established GP practice in Exeter, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Romanian", "Mandarin"],
    eveningWeekend: false,
  },
  {
    id: "439",
    name: "Ide Lane Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L83079",
    services: ["Child Health & Development", "New Patient Registration", "ECG Testing", "Postnatal Care", "Physiotherapy Referrals", "General Consultations", "Wound Care", "Antenatal Care"],
    borough: "Exeter",
    postcode: "EX2 8UP",
    address: "Ide Lane, Alphington, Exeter, EX2 8UP",
    phone: "01392 428900",
    acceptingNewPatients: "yes",
    description: "Ide Lane Surgery provides high-quality NHS healthcare services in Exeter, with a dedicated team of GPs and healthcare professionals.",
    openingHours: "Mon 08:00-18:00, Tue 08:00-18:00, Wed 08:00-18:00, Thu 08:00-18:00, Fri 08:00-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Hindi", "Portuguese", "Spanish", "Somali"],
    eveningWeekend: false,
  },
  {
    id: "440",
    name: "Bramblehaies Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "L83128",
    services: ["Blood Pressure Monitoring", "Women's Health", "Diabetes Management", "Wound Care", "Antenatal Care", "Weight Management", "Mental Health Support", "ECG Testing"],
    borough: "Exeter",
    postcode: "EX15 1TZ",
    address: "College Road, , Cullompton, EX15 1TZ",
    phone: "01884 33536",
    acceptingNewPatients: "yes",
    description: "Bramblehaies Surgery is a patient-focused GP surgery in Exeter, offering both routine and specialist primary care services.",
    openingHours: "Mon 08:30-18:00, Tue 08:30-18:00, Wed 08:30-18:00, Thu 08:30-18:00, Fri 08:30-18:00, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: true,
    languages: ["English", "Urdu", "Somali"],
    eveningWeekend: false,
  },
  {
    id: "441",
    name: "Boots",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y08325",
    services: ["Contraception & Family Planning", "Diabetes Management", "Postnatal Care", "Smoking Cessation", "Ear Syringing", "Blood Tests & Pathology", "Men's Health"],
    borough: "Exeter",
    postcode: "EX4 3QD",
    address: "251 High Street, , Exeter, EX4 3QD",
    phone: "0000 000 0000",
    acceptingNewPatients: "yes",
    description: "Boots is a well-established GP practice in Exeter, offering comprehensive primary care services to patients of all ages.",
    openingHours: "Mon 08:00-18:30, Tue 08:00-18:30, Wed 08:00-18:30, Thu 08:00-18:30, Fri 08:00-18:30, Sat Closed, Sun Closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    wheelchairAccessible: true,
    outOfAreaRegistrations: false,
    languages: ["English", "Somali", "Arabic"],
    eveningWeekend: false,
  },
  {
    id: "600",
    name: "The Neaman Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84640",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "City of London",
    postcode: "EC1A 7HF",
    address: "15 Half Moon Court, London, Greater London, EC1A 7HF",
    phone: "020 7600 9740",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "601",
    name: "Clerkenwell Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83624",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "City of London",
    postcode: "EC1R 0LP",
    address: "Finsbury Health Centre, 17 Pine Street, London, Greater London, EC1R 0LP",
    phone: "020 7833 5906",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "602",
    name: "Dr Segarajasinghe",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83678",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "City of London",
    postcode: "EC1R 0LP",
    address: "Finsbury Health Centre, Pine Street, London, Greater London, EC1R 0LP",
    phone: "020 7713 5256",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "603",
    name: "New North Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83034",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 7AA",
    address: "287-293 New North Road, London, Greater London, N1 7AA",
    phone: "020 7226 9090",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "604",
    name: "Kings College Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87768",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC2B 4PJ",
    address: "3Rd Floor, Bush House, South East Wing, 300 Strand, London, WC2B 4PJ",
    phone: "020 7848 2613",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
    restricted: true,
  },
  {
    id: "605",
    name: "The Mitchison Road Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83056",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 3NG",
    address: "2 Mitchison Road, London, Greater London, N1 3NG",
    phone: "020 7226 6016",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "606",
    name: "Soho Square General Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87714",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "W1D 3HZ",
    address: "1St Floor, 1 Frith Street, London, Greater London, W1D 3HZ",
    phone: "020 3405 6570",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "607",
    name: "The Goodinge Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83008",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "London",
    postcode: "N7 9EW",
    address: "Goodinge Health Centre, Goodinge Close, 20 North Road, London, Greater London, N7 9EW",
    phone: "020 7619 6670",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "608",
    name: "GP Pathfinder Clinics - Battersea",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "London",
    postcode: "SW8 4EX",
    address: "25 Patcham Terrace, London, SW8 4EX",
    phone: "020 8902 4792",
    acceptingNewPatients: "no",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "609",
    name: "GP Pathfinder Clinics - New Cross",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Southwark",
    postcode: "SE14 6BL",
    address: "Unit B-C, Ewen Henderson Court, 40 Goodwood Road, London, SE14 6BL",
    phone: "020 8902 4792",
    acceptingNewPatients: "no",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "610",
    name: "Half Penny Steps Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
        services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "W10 4RE",
    address: "427-429 Harrow Road, London, Greater London, W10 4RE",
    phone: "020 8962 8700",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "611",
    name: "Kings Road Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87063",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW10 0UD",
    address: "529 Kings Road, Chelsea, London, Greater London, SW10 0UD",
    phone: "0844 815 1870",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "612",
    name: "GP Pathfinder Clinics - Queens Park (Chamberlayne)",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Brent",
    postcode: "NW10 3JP",
    address: "Chamberlayne Road Surgery, 124 Chamberlayne Road, London, NW10 3JP",
    phone: "020 8206 6590",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "613",
    name: "The Practice at 188",
    type: "GP Practice",
    nhsStatus: "nhs",
        services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Barnet",
    postcode: "NW11 9AY",
    address: "188 Golders Green Road, Golders Green, London, Greater London, NW11 9AY",
    phone: "020 3474 6498",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "614",
    name: "GP Pathfinder Clinics - Willeseden",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Brent",
    postcode: "NW10 2PB",
    address: "Utopia House, 206 High Road, Willesden, London, NW10 2PB",
    phone: "020 8902 4792",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "615",
    name: "GP Pathfinder Clinics - Wandsworth",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW18 4GP",
    address: "12 Buckhold Road, London, SW18 4GP",
    phone: "020 8902 4792",
    acceptingNewPatients: "no",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "616",
    name: "GP Pathfinder Clinics - Neasden (Crest)",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "London",
    postcode: "NW2 7NA",
    address: "Crest Medical Centre, 157 Crest Road, London, NW2 7NA",
    phone: "020 8452 5155",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "617",
    name: "Amwell Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
        services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC1X 0GB",
    address: "4 Naoroji Street, Islington, London, Greater London, WC1X 0GB",
    phone: "020 7837 2020",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "618",
    name: "The Hoxton Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84692",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 5DR",
    address: "12 Rushton Street, Hoxton, London, Greater London, N1 5DR",
    phone: "020 3487 0200",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "619",
    name: "SHOREDITCH PARK SURGERY",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84635",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 5DR",
    address: "6-10 Rushton Street, London, Greater London, N1 5DR",
    phone: "020 7739 8525",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "620",
    name: "St Peter's Street Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83032",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 8JG",
    address: "16 1/2 St Peter's Street, Islington, London, Greater London, N1 8JG",
    phone: "020 7288 9320",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "621",
    name: "Holborn Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83058",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC1N 3NA",
    address: "64-66 Lambs Conduit Street, London, Greater London, WC1N 3NA",
    phone: "020 3077 0044",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "622",
    name: "STROUTS PLACE MEDICAL CENTRE",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84051",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Tower Hamlets",
    postcode: "E2 7QU",
    address: "3 Strouts Place, Pelter Street, London, Greater London, E2 7QU",
    phone: "020 8187 2000",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "623",
    name: "St Philips Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83672",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC2A 2AZ",
    address: "Tower 3, 2Nd Floor Clements Inn, London, Greater London, WC2A 2AZ",
    phone: "020 7611 5131",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "624",
    name: "Elizabeth Avenue Group Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83012",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 3BS",
    address: "2 Elizabeth Avenue, Islington, London, Greater London, N1 3BS",
    phone: "020 7226 6363",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "625",
    name: "Health E1",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84733",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Tower Hamlets",
    postcode: "E1 6PU",
    address: "9-11 Brick Lane, London, E1 6PU",
    phone: "020 7247 0090",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "626",
    name: "The Bloomsbury Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83044",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC1N 1PD",
    address: "1 Handel Street, London, Greater London, WC1N 1PD",
    phone: "020 7837 8559",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "627",
    name: "Brunswick Medical Centre Uhpc",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83048",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC1N 1AF",
    address: "39 Brunswick Centre, London, Greater London, WC1N 1AF",
    phone: "020 7837 3057",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "628",
    name: "The Spitalfields Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84081",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Tower Hamlets",
    postcode: "E1 5PB",
    address: "20 Old Montague Street, London, Greater London, E1 5PB",
    phone: "020 7247 7070",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "629",
    name: "River Place Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83002",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 2DE",
    address: "River Place, Essex Road, London, Greater London, N1 2DE",
    phone: "020 7704 2832",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "630",
    name: "Killick Street Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83063",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 9RH",
    address: "Killick Street Health Ctr, 75 Killick Street, London, Greater London, N1 9RH",
    phone: "020 7833 9939",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "631",
    name: "DE BEAUVOIR SURGERY",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84072",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Islington",
    postcode: "N1 5QT",
    address: "30 Hertford Road, London, Greater London, N1 5QT",
    phone: "020 7923 3684",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "632",
    name: "Blackfriars Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85642",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Southwark",
    postcode: "SE1 8EE",
    address: "Blackfriars Medical Pract, 45 Colombo Street, London, Greater London, SE1 8EE",
    phone: "020 7928 6216",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "633",
    name: "Museum Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83061",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC1B 3BA",
    address: "58 Great Russell Street, London, Greater London, WC1B 3BA",
    phone: "020 7405 2739",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "634",
    name: "The Blithehale Med.Ctr.",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84718",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Tower Hamlets",
    postcode: "E2 6JA",
    address: "10-22 Dunbridge Street, London, Greater London, E2 6JA",
    phone: "020 7739 5497",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "635",
    name: "Covent Garden Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87045",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC2H 9AA",
    address: "47 Shorts Gardens, London, Greater London, WC2H 9AA",
    phone: "020 7379 7209",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "636",
    name: "Goodman's Field Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84039",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Tower Hamlets",
    postcode: "E1 8ZF",
    address: "11 Stable Walk, London, E1 8ZF",
    phone: "020 7702 2036",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "637",
    name: "Bethnal Green Health Ctr.",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F84083",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Tower Hamlets",
    postcode: "E2 6LL",
    address: "60 Florida Street, London, Greater London, E2 6LL",
    phone: "020 7739 6677",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "638",
    name: "The Dr Hickey Surgery For The Homeless",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87740",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1P 2BG",
    address: "3-7 Arneway Street, London, Greater London, SW1P 2BG",
    phone: "020 7222 8593",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
    restricted: true,
  },
  {
    id: "639",
    name: "Victoria Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87002",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1V 1SN",
    address: "29 Upper Tachbrook Street, London, Greater London, SW1V 1SN",
    phone: "020 7834 2298",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "640",
    name: "Millbank Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87739",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1P 4EN",
    address: "Medical Centre Building, 20 Page Street, London, Greater London, SW1P 4EN",
    phone: "020 7834 5502",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "641",
    name: "Belgrave Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87753",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1W 8NA",
    address: "The Surgery, 13 Pimlico Road, London, Greater London, SW1W 8NA",
    phone: "020 7730 5171",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "642",
    name: "Lees Place Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "W1K 6LN",
    address: "11 Lees Place, London, Greater London, W1K 6LN",
    phone: "020 7036 6060",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "643",
    name: "Great Chapel Street Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
        services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "W1D 3HZ",
    address: "4th Floor, Soho Centre For Health, 1 Frith Street, London, W1D 3HZ",
    phone: "020 7437 9360",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "644",
    name: "Connaught Square Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87037",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "London",
    postcode: "W2 2HL",
    address: "41 Connaught Square, London, Greater London, W2 2HL",
    phone: "020 7402 4026",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "645",
    name: "Kings Cross Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83635",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "NW1 1HY",
    address: "77-83 Chalton Street, London, NW1 1HY",
    phone: "020 7278 9074",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "646",
    name: "Earls Court Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87750",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "London",
    postcode: "SW5 9JA",
    address: "269 Old Brompton Road, Earls Court, London, Greater London, SW5 9JA",
    phone: "020 7370 2643",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "648",
    name: "Barlby Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "Y01011",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "W10 6DZ",
    address: "St. Charles Hospital, Exmoor Street, London, Greater London, W10 6DZ",
    phone: "020 7062 6310",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "649",
    name: "GP at Hand - Heron House",
    type: "GP Practice",
    nhsStatus: "nhs",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1H 0DX",
    address: "Heron House, 10 Dean Farrar Street, London, SW1H 0DX",
    phone: "0330 303 8000",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
    restricted: true,
  },
  {
    id: "650",
    name: "Belgravia Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
        services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1W 9PY",
    address: "24 Eccleston Street, Belgravia, London, Greater London, SW1W 9PY",
    phone: "020 7590 8000",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "651",
    name: "Knightsbridge Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87738",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1X 0ET",
    address: "71-75 Pavilion Road, London, Greater London, SW1X 0ET",
    phone: "020 3961 5260",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "652",
    name: "Pimlico Health At The Marven Surgery",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87034",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "SW1V 3EB",
    address: "46-50 Lupus Street, Westminster, London, Greater London, SW1V 3EB",
    phone: "020 7834 1160",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "653",
    name: "Mayfair Medical Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87648",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "W1K 5LS",
    address: "3-5 Weighhouse Street, London, Greater London, W1K 5LS",
    phone: "020 7493 1647",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "654",
    name: "Soho Centre For Health And Care",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87069",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Westminster",
    postcode: "W1D 3QS",
    address: "1St Floor, 30 Soho Square, London, Greater London, W1D 3QS",
    phone: "020 7534 6570",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "655",
    name: "Waterloo Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85136",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Southwark",
    postcode: "SE1 7AE",
    address: "114-118 Lower Marsh, Waterloo, London, SE1 7AE",
    phone: "020 7928 4049",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "656",
    name: "Gray's Inn Medical Group Vauxhall",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "G85102",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Southwark",
    postcode: "SE11 5NH",
    address: "8 Jonathan Street, Kennington, London, Greater London, SE11 5NH",
    phone: "020 7735 1971",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  {
    id: "657",
    name: "Riverside Medical Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
        services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "London",
    postcode: "SW8 2JB",
    address: "Hobart House, St Georges Wharf, Wandsworth Road, London, Greater London, SW8 2JB",
    phone: "020 3049 4504",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "658",
    name: "Imperial College Health Centre",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "E87677",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "London",
    postcode: "SW7 1LY",
    address: "40 Princes Gardens, London, Greater London, SW7 1LY",
    phone: "020 7584 6301",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
    restricted: true,
  },
  {
    id: "659",
    name: "Ridgmount Practice",
    type: "GP Practice",
    nhsStatus: "nhs",
    odsCode: "F83043",
    services: ["General Practice", "Chronic Disease Management", "Vaccinations", "Health Screenings"],
    borough: "Camden",
    postcode: "WC1E 7AA",
    address: "8 Ridgmount Street, London, Greater London, WC1E 7AA",
    phone: "020 7387 6306",
    acceptingNewPatients: "yes",
    description: "NHS GP practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: true,
    languages: ["English"],
  },
  // ─── DENTAL PRACTICES (150) ───
  {
    id: "442",
    name: "Covent Garden Dental Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "WC2N 4HS",
    address: "48a Chandos Place, London, WC2N 4HS",
    phone: "020 7836 1847",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 115,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "443",
    name: "Soho Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "W1D 3HZ",
    address: "2nd Floor, 1 Frith Street, Soho Square, London, W1D 3HZ",
    phone: "020 7534 6622",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-19:00, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 118,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "444",
    name: "Centre Point Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "WC2H 0LA",
    address: "Flat 4, Shaldon Mansions, 132 Charing Cross Road, London, WC2H 0LA",
    phone: "020 7836 9259",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "445",
    name: "EGO Dental Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "SW1P 2HY",
    address: "11-13 Strutton Ground, London, SW1P 2HY",
    phone: "020 7222 8442",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-18:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 13,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "446",
    name: "Westminster House Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "SW1P 2AH",
    address: "11-13 Horseferry Road, London, SW1P 2AH",
    phone: "020 7976 5251",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-20:00, Sun closed",
    rating: 4.2,
    reviews: 16,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "447",
    name: "Esthetique Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "W1T 6NA",
    address: "74 Cleveland Street, Fitzrovia, London, W1T 6NA",
    phone: "020 7631 4444",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-19:00, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 19,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "448",
    name: "The London Dental Studio",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "SW1V 1QT",
    address: "27-29 Warwick Way, London, SW1V 1QT",
    phone: "020 7630 0782",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-17:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "449",
    name: "McKennell Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "SW1V 2LY",
    address: "21 Churton Street, London, SW1V 2LY",
    phone: "020 7834 8802",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 25,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "450",
    name: "Bond Dental London (Bloomsbury)",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "WC1N 1AG",
    address: "96 Marchmont Street, London, WC1N 1AG",
    phone: "020 3432 8620",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-19:00, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 28,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "451",
    name: "Confi-Dent Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "NW1 2NT",
    address: "66 Hampstead Road, London, NW1 2NT",
    phone: "020 7388 8556",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 31,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "452",
    name: "Raval Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "WC1H 8DG",
    address: "86 Cromer Street, Kings Cross, London, WC1H 8DG",
    phone: "020 7837 3478",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 34,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Urdu"],
  },
  {
    id: "453",
    name: "Gandhi & Chan Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "WC1X 8RH",
    address: "231 Grays Inn Road, London, WC1X 8RH",
    phone: "020 7837 0903",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 37,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "454",
    name: "Borough Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Southwark",
    postcode: "SE1 1JH",
    address: "305 Borough High Street, London, SE1 1JH",
    phone: "020 7407 3883",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Southwark offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-18:00, Sun closed",
    rating: 5.0,
    reviews: 40,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "455",
    name: "Chalton Street Dental",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "NW1 1JB",
    address: "30-32 Chalton Street, London, NW1 1JB",
    phone: "020 3757 5272",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-20:00, Sun closed",
    rating: 3.8,
    reviews: 43,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "456",
    name: "Lupus Street Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "SW1V 3HD",
    address: "141-145 Lupus Street, Pimlico, London, SW1V 3HD",
    phone: "020 7834 8081",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 46,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "457",
    name: "Arrow Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Camden",
    postcode: "NW1 3QT",
    address: "89a Robert Street, London, NW1 3QT",
    phone: "020 7387 8143",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Camden offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 49,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "458",
    name: "Dr GK Ooi & Associates",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Lambeth",
    postcode: "SE11 4LD",
    address: "302 Kennington Road, London, SE11 4LD",
    phone: "020 7582 1668",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Lambeth offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 52,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "459",
    name: "Wytes Elephant & Castle",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Southwark",
    postcode: "SE1 6SW",
    address: "94-96 Walworth Road, London, SE1 6SW",
    phone: "020 7703 2524",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Southwark offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-20:00, Sun closed",
    rating: 4.2,
    reviews: 55,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "460",
    name: "James Street Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "W1U 1EQ",
    address: "18 James Street, London, W1U 1EQ",
    phone: "020 7629 2164",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 58,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "461",
    name: "Weiss Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Westminster",
    postcode: "W1G 8GG",
    address: "21 Wimpole Street, London, W1G 8GG",
    phone: "020 7637 4421",
    acceptingNewPatients: "limited",
    description: "NHS dental practice in Westminster offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 61,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "462",
    name: "Whitechapel Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Tower Hamlets",
    postcode: "E1 1BB",
    address: "260 Whitechapel Road, London, E1 1BB",
    phone: "020 7247 1152",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Tower Hamlets offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.5,
    reviews: 64,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali", "Urdu"],
  },
  {
    id: "463",
    name: "Bethnal Green Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Tower Hamlets",
    postcode: "E2 0AN",
    address: "340 Bethnal Green Road, London, E2 0AN",
    phone: "020 7739 2478",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Tower Hamlets offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 67,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Bengali"],
  },
  {
    id: "464",
    name: "Peckham Dental Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Southwark",
    postcode: "SE15 5EW",
    address: "62 Peckham High Street, London, SE15 5EW",
    phone: "020 7703 0095",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Southwark offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 70,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "465",
    name: "Brixton Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Lambeth",
    postcode: "SW9 8HH",
    address: "37 Brixton Road, London, SW9 8HH",
    phone: "020 7274 3923",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Lambeth offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 73,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "466",
    name: "Streatham Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Lambeth",
    postcode: "SW16 1BW",
    address: "98 Streatham High Road, London, SW16 1BW",
    phone: "020 8769 3436",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Lambeth offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-17:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 76,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "467",
    name: "Tooting Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Wandsworth",
    postcode: "SW17 0RL",
    address: "141 Upper Tooting Road, London, SW17 0RL",
    phone: "020 8682 3344",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Wandsworth offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 5.0,
    reviews: 79,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "468",
    name: "Lewisham Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Lewisham",
    postcode: "SE13 6JE",
    address: "49 Lewisham High Street, London, SE13 6JE",
    phone: "020 8852 1095",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Lewisham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 82,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "469",
    name: "Catford Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Lewisham",
    postcode: "SE6 4AA",
    address: "193 Rushey Green, London, SE6 4AA",
    phone: "020 8697 2043",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Lewisham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 85,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "470",
    name: "Holloway Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Islington",
    postcode: "N7 6PA",
    address: "178 Holloway Road, London, N7 6PA",
    phone: "020 7607 3855",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Islington offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 88,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "471",
    name: "Finsbury Park Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Haringey",
    postcode: "N4 2NX",
    address: "106 Stroud Green Road, London, N4 2NX",
    phone: "020 7272 5421",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Haringey offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 91,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "472",
    name: "Wood Green Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Haringey",
    postcode: "N22 6YQ",
    address: "230 High Road, London, N22 6YQ",
    phone: "020 8888 6281",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Haringey offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-13:00, Sun closed",
    rating: 4.2,
    reviews: 94,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "473",
    name: "Edmonton Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Enfield",
    postcode: "N9 0HJ",
    address: "35 Fore Street, London, N9 0HJ",
    phone: "020 8807 1478",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Enfield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 97,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "474",
    name: "Walthamstow Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Waltham Forest",
    postcode: "E17 7AA",
    address: "180 Hoe Street, London, E17 7AA",
    phone: "020 8520 4433",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Waltham Forest offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 100,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Polish", "Urdu"],
  },
  {
    id: "475",
    name: "Stratford Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newham",
    postcode: "E15 1JA",
    address: "42 Broadway, London, E15 1JA",
    phone: "020 8534 2143",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 103,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "476",
    name: "Ilford Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Redbridge",
    postcode: "IG1 2PG",
    address: "88 Cranbrook Road, London, IG1 2PG",
    phone: "020 8478 3833",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Redbridge offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-14:00, Sun closed",
    rating: 4.6,
    reviews: 106,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Bengali", "Gujarati"],
  },
  {
    id: "477",
    name: "Barking Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Barking",
    postcode: "IG11 8HE",
    address: "54 East Street, London, IG11 8HE",
    phone: "020 8594 3622",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Barking offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 109,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "478",
    name: "Ealing Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Ealing",
    postcode: "W5 5JY",
    address: "4 The Mall, London, W5 5JY",
    phone: "020 8567 5588",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Ealing offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 112,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "479",
    name: "Acton Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Ealing",
    postcode: "W3 6RS",
    address: "149 High Street, London, W3 6RS",
    phone: "020 8992 4478",
    acceptingNewPatients: "limited",
    description: "NHS dental practice in Ealing offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 115,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "480",
    name: "Hammersmith Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Hammersmith",
    postcode: "W6 7AH",
    address: "45 King Street, London, W6 7AH",
    phone: "020 8748 2626",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Hammersmith offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 118,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "481",
    name: "Kilburn Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Brent",
    postcode: "NW6 7HJ",
    address: "153 Kilburn High Road, London, NW6 7HJ",
    phone: "020 7624 2614",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Brent offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 10,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "482",
    name: "Harlesden Dental Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Brent",
    postcode: "NW10 4RA",
    address: "89 High Street, London, NW10 4RA",
    phone: "020 8965 2431",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Brent offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 13,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Gujarati", "Urdu"],
  },
  {
    id: "483",
    name: "Croydon Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Croydon",
    postcode: "CR0 1NF",
    address: "49 North End, London, CR0 1NF",
    phone: "020 8688 3818",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Croydon offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat 09:00-14:00, Sun closed",
    rating: 4.0,
    reviews: 16,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "484",
    name: "Woolwich Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Greenwich",
    postcode: "SE18 6AB",
    address: "88 Powis Street, London, SE18 6AB",
    phone: "020 8854 2266",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Greenwich offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 19,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "485",
    name: "Deptford Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Lewisham",
    postcode: "SE8 4NS",
    address: "122 Deptford High Street, London, SE8 4NS",
    phone: "020 8692 1413",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Lewisham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "486",
    name: "Hackney Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Hackney",
    postcode: "E8 1EJ",
    address: "224 Mare Street, London, E8 1EJ",
    phone: "020 8985 7607",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Hackney offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 25,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Turkish"],
  },
  {
    id: "487",
    name: "Dalston Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Hackney",
    postcode: "E8 2NS",
    address: "80 Kingsland High Street, London, E8 2NS",
    phone: "020 7254 9666",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Hackney offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 28,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "488",
    name: "Tottenham Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Haringey",
    postcode: "N17 8AS",
    address: "615 High Road, London, N17 8AS",
    phone: "020 8808 1166",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Haringey offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:00, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 31,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "489",
    name: "Enfield Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Enfield",
    postcode: "EN2 6LE",
    address: "48 Church Street, London, EN2 6LE",
    phone: "020 8363 2332",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Enfield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 34,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "490",
    name: "Greenwich Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Greenwich",
    postcode: "SE10 9JB",
    address: "163 Trafalgar Road, London, SE10 9JB",
    phone: "020 8858 2212",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Greenwich offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 37,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "491",
    name: "Fulham Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Hammersmith",
    postcode: "SW6 1NW",
    address: "675 Fulham Road, London, SW6 1NW",
    phone: "020 7385 7766",
    acceptingNewPatients: "limited",
    description: "NHS dental practice in Hammersmith offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 40,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "492",
    name: "Priory Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B4 7ET",
    address: "Vision Science Building, Coleshill Street, Aston University, Birmingham, B4 7ET",
    phone: "0121 204 4310",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 43,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "493",
    name: "Bhandal Dental Practice Cherry Street",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B2 5AR",
    address: "12 Cherry Street, City Centre, Birmingham, B2 5AR",
    phone: "0121 643 5279",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 46,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "494",
    name: "Newtown Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B19 2ER",
    address: "15 Wheeler Street Precinct, Newtown, Birmingham, B19 2ER",
    phone: "0121 554 3406",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 49,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "495",
    name: "Farm Street Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B19 2TZ",
    address: "357a Farm Street, Hockley, Birmingham, B19 2TZ",
    phone: "0121 554 9324",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 52,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "496",
    name: "Family Dental Care Lozells",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B19 2TJ",
    address: "54b Lozells Road, Lozells, Birmingham, B19 2TJ",
    phone: "0121 554 3064",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 4.0,
    reviews: 55,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "497",
    name: "Dental Surgery Birchfield Road",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B19 1SU",
    address: "19 Birchfield Road, Handsworth, Birmingham, B19 1SU",
    phone: "0121 554 1065",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 58,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "498",
    name: "Lozells Road Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B19 2SX",
    address: "176 Lozells Road, Lozells, Birmingham, B19 2SX",
    phone: "0121 554 2314",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 61,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "499",
    name: "Broadway Dental Suite",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B18 7BA",
    address: "Cope Street, Ladywood, Birmingham, B18 7BA",
    phone: "0121 792 0479",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 64,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "500",
    name: "Bhandal Dental Practice Five Ways",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B15 1LD",
    address: "Edgbaston, Birmingham, B15 1LD",
    phone: "0121 454 7172",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-19:00, Sun closed",
    rating: 4.4,
    reviews: 67,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "501",
    name: "Birchfield Road Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B19 1LL",
    address: "195-197 The Parade, Birchfield Road, Perry Barr, Birmingham, B19 1LL",
    phone: "0121 554 1066",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 4.5,
    reviews: 70,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "502",
    name: "Heathfield Road Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B19 1JD",
    address: "191 Heathfield Road, Handsworth, Birmingham, B19 1JD",
    phone: "0121 523 3749",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 73,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "503",
    name: "Keep Smiling Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B9 5DN",
    address: "306-308 Green Lane, Small Heath, Birmingham, B9 5DN",
    phone: "0121 772 3121",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 4.7,
    reviews: 76,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "504",
    name: "Soho Road Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B21 9BN",
    address: "78 Soho Road, Handsworth, Birmingham, B21 9BN",
    phone: "0121 523 8231",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 4.8,
    reviews: 79,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "505",
    name: "488 Smile Avenue",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B9 5PA",
    address: "488 Bordesley Green, Birmingham, B9 5PA",
    phone: "0121 772 1148",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-19:00, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 82,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "506",
    name: "Cromer Road Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B12 9QP",
    address: "3 Cromer Road, Birmingham, B12 9QP",
    phone: "0121 440 4621",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 85,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "507",
    name: "Smiles 4U Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B23 7JY",
    address: "62 Gravelly Hill, Erdington, Birmingham, B23 7JY",
    phone: "0121 327 2358",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 88,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "508",
    name: "Poplar Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B11 1UH",
    address: "1 Poplar Road, Sparkhill, Birmingham, B11 1UH",
    phone: "0121 771 3720",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 91,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "509",
    name: "Solanki Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B12 8LH",
    address: "115 Ladypool Road, Balsall Heath, Birmingham, B12 8LH",
    phone: "0121 440 1668",
    acceptingNewPatients: "limited",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-19:00, Sun closed",
    rating: 4.0,
    reviews: 94,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Punjabi"],
  },
  {
    id: "510",
    name: "Bhandal Dental Smallheath",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B10 0DP",
    address: "103a Golden Hillock Road, Smallheath, Birmingham, B10 0DP",
    phone: "0121 772 0266",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-19:00, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 97,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "511",
    name: "Bhandal Dental Monument Road",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Birmingham",
    postcode: "B16 8XP",
    address: "280 Monument Road, Edgbaston, Birmingham, B16 8XP",
    phone: "0121 454 8571",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Birmingham offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-19:00, Sun closed",
    rating: 4.2,
    reviews: 100,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "512",
    name: "Manchester DP Ltd",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M2 7HA",
    address: "2-4 Exchange Street, Manchester, M2 7HA",
    phone: "0161 833 0704",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 103,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "513",
    name: "207 Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M3 3NW",
    address: "Onward Buildings, 207 Deansgate, Manchester, M3 3NW",
    phone: "0161 834 0606",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 106,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "514",
    name: "mydentist Stevenson Square",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M1 1FN",
    address: "43 Lever Street, Manchester, M1 1FN",
    phone: "0161 228 1952",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 109,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "515",
    name: "New Cross Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M4 5EE",
    address: "50-52 Oldham Road, Ancoats, Manchester, M4 5EE",
    phone: "0161 236 0458",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 112,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "516",
    name: "Revive Dental Care Bexley",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Salford",
    postcode: "M3 6BZ",
    address: "2 Bexley Square, Salford, Manchester, M3 6BZ",
    phone: "0161 834 5166",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Salford offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 115,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "517",
    name: "Revive Dental Care Ancoats",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M4 6EE",
    address: "Ancoats Primary Care Centre, Old Mill Street, Ancoats, Manchester, M4 6EE",
    phone: "0161 529 6831",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:00-19:00, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 118,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "518",
    name: "The Vallance Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M13 9UJ",
    address: "The Vallance Health Centre, Brunswick Street, Manchester, M13 9UJ",
    phone: "0161 273 5998",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-18:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 10,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "519",
    name: "Deane Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Salford",
    postcode: "M7 1US",
    address: "The Vibe, 175 Broughton Lane, Salford, M7 1US",
    phone: "0161 478 2980",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Salford offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 13,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "520",
    name: "Parsonage Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M15 5WD",
    address: "The Parsonage, 1B Parsonage Street, Hulme, Manchester, M15 5WD",
    phone: "0161 227 7570",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "521",
    name: "Windsor Dental Practice Salford",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Salford",
    postcode: "M6 5EQ",
    address: "Belvedere Road, Salford, Manchester, M6 5EQ",
    phone: "0161 736 1397",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Salford offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 19,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "522",
    name: "Brooks Bar Dental",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M16 7WW",
    address: "176 Chorlton Road, Old Trafford, Manchester, M16 7WW",
    phone: "0161 226 2841",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "523",
    name: "Queens Road Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M8 0JL",
    address: "337 Queens Road, Manchester, M8 0JL",
    phone: "0161 205 1113",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-20:00, Sun closed",
    rating: 4.1,
    reviews: 25,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "524",
    name: "Collegiate Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M8 0AB",
    address: "76 Brideoak Street, Cheetham Hill, Manchester, M8 0AB",
    phone: "0161 205 1000",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 28,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "525",
    name: "Rusholme Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M14 4LP",
    address: "300 Great Western Street, Rusholme, Manchester, M14 4LP",
    phone: "0161 226 2548",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 31,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "526",
    name: "Family Dental Practice Moss Side",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M14 4RL",
    address: "177a Princess Road, Moss Side, Manchester, M14 4RL",
    phone: "0161 226 8432",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 34,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "527",
    name: "151 Dental Ltd",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M16 9WR",
    address: "151 Ayres Road, Old Trafford, Manchester, M16 9WR",
    phone: "0161 872 1695",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 37,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "528",
    name: "End D Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Salford",
    postcode: "M7 4UF",
    address: "263 Great Cheetham Street East, Salford, Manchester, M7 4UF",
    phone: "0161 792 1960",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Salford offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 40,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "529",
    name: "The Griffin Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Salford",
    postcode: "M7 2JB",
    address: "1-3 Great Cheetham Street West, Salford, Manchester, M7 2JB",
    phone: "0161 792 2363",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Salford offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 43,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "530",
    name: "Archway Dental Practice Hulme",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Manchester",
    postcode: "M15 5NR",
    address: "Denhill House, 2 Denhill Road, Manchester, M15 5NR",
    phone: "0161 232 8403",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Manchester offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 46,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "532",
    name: "Whitecross Dental Care Leeds",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS1 5PL",
    address: "1 Park Cross Street, Leeds, LS1 5PL",
    phone: "0113 262 2303",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-17:30, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 52,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "533",
    name: "Dr Stefan Andronovici",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS11 9NS",
    address: "73 Domestic Street, Leeds, LS11 9NS",
    phone: "0113 244 8259",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 55,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "534",
    name: "Genix Healthcare Beeston",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS11 6AJ",
    address: "2 Lady Pit Lane, Beeston, Leeds, LS11 6AJ",
    phone: "0113 245 7672",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 58,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "535",
    name: "Genix Healthcare East Park",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS9 9JD",
    address: "5-7 East Park Road, Leeds, LS9 9JD",
    phone: "0113 248 0589",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 61,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "536",
    name: "Armley Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS12 1XB",
    address: "4 Hall Road, Leeds, LS12 1XB",
    phone: "0113 263 8357",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 64,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "537",
    name: "Hunslet Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS10 2AR",
    address: "Fraternity House, 50 Church Street, Hunslet, Leeds, LS10 2AR",
    phone: "0113 271 6623",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 67,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "538",
    name: "Deu Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS6 2AD",
    address: "10 Otley Road, Headingley, Leeds, LS6 2AD",
    phone: "0113 278 4000",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 70,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "539",
    name: "Stainbeck Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS7 2PW",
    address: "58 Stainbeck Road, Leeds, LS7 2PW",
    phone: "0113 230 4010",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 4.4,
    reviews: 73,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "540",
    name: "Street Lane Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS17 6HQ",
    address: "457 Street Lane, Leeds, LS17 6HQ",
    phone: "0113 268 9696",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 76,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "541",
    name: "Whitecross Dental Bramley",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS13 2ET",
    address: "Units 26-29, Bramley Shopping Centre, Leeds, LS13 2ET",
    phone: "0113 256 5513",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-20:00, Sun closed",
    rating: 4.6,
    reviews: 79,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "542",
    name: "Fountain Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS27 9EN",
    address: "Little Fountain Street, Morley, Leeds, LS27 9EN",
    phone: "0113 532 8111",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 82,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "543",
    name: "Perfect Smile Clinic Oakwood",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS8 3ES",
    address: "252-254 Easterly Road, Oakwood, Leeds, LS8 3ES",
    phone: "0113 248 6777",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 85,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "544",
    name: "Montreal Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS7 4LF",
    address: "8 Montreal Avenue, Chapel Allerton, Leeds, LS7 4LF",
    phone: "0113 262 1663",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 88,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "545",
    name: "LS10 Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS10 2EU",
    address: "20 Pepper Road, Leeds, LS10 2EU",
    phone: "0113 277 6606",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 91,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "546",
    name: "Xen Smiles Limited",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Leeds",
    postcode: "LS13 4AJ",
    address: "241 Green Hill Road, Bramley, Leeds, LS13 4AJ",
    phone: "0113 397 2640",
    acceptingNewPatients: "limited",
    description: "NHS dental practice in Leeds offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 94,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "547",
    name: "mydentist London Road Sheffield",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S2 4LR",
    address: "83-87 London Road, Sheffield, S2 4LR",
    phone: "0114 255 0444",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 97,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "548",
    name: "Abbeydale Road Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S7 1FD",
    address: "4-6 Abbeydale Road, Highfields, Sheffield, S7 1FD",
    phone: "0114 255 4433",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 100,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "549",
    name: "Castle Dental Practice Sheffield",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S2 5HJ",
    address: "309 City Road, Sheffield, S2 5HJ",
    phone: "0114 272 1909",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 103,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "550",
    name: "Thompson & Thomas",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S6 2UB",
    address: "76 Langsett Road, Hillsborough, Sheffield, S6 2UB",
    phone: "0114 234 3346",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 106,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "551",
    name: "Taptonville House Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S10 5BQ",
    address: "1 Taptonville Road, Broomhill, Sheffield, S10 5BQ",
    phone: "0114 399 0888",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 109,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "552",
    name: "mydentist South Road Walkley",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S6 3TA",
    address: "263-265 South Road, Walkley, Sheffield, S6 3TA",
    phone: "0114 233 5332",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 4.4,
    reviews: 112,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "553",
    name: "Hillsborough Family Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S6 2SA",
    address: "1 Victor Street, Hillsborough, Sheffield, S6 2SA",
    phone: "0114 234 4810",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 115,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "554",
    name: "LWT Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S11 8TG",
    address: "719 Ecclesall Road, Ecclesall, Sheffield, S11 8TG",
    phone: "0114 266 2853",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 118,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "555",
    name: "765 Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S7 2BG",
    address: "765 Abbeydale Road, Sheffield, S7 2BG",
    phone: "0114 258 8549",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-19:00, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 10,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "556",
    name: "Manor Park Family Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S2 1RA",
    address: "1a Motehall Road, Manor Park, Sheffield, S2 1RA",
    phone: "0114 239 2985",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 13,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "557",
    name: "D&K Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S5 8TQ",
    address: "528 Herries Road, Shirecliffe, Sheffield, S5 8TQ",
    phone: "0114 232 5938",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 16,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "558",
    name: "Collins & Ball Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S11 9PF",
    address: "60 Ecclesall Road South, Sheffield, S11 9PF",
    phone: "0114 266 2212",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:30, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 19,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "559",
    name: "Darnall Dental Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S9 4LP",
    address: "652-654 Staniforth Road, Darnall, Sheffield, S9 4LP",
    phone: "0114 244 1731",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 22,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English", "Urdu", "Arabic"],
  },
  {
    id: "560",
    name: "Richmond Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S13 8LU",
    address: "427-429 Richmond Road, Sheffield, S13 8LU",
    phone: "0114 269 4746",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 25,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "561",
    name: "mydentist Shiregreen Lane",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Sheffield",
    postcode: "S5 6AB",
    address: "73-75 Shiregreen Lane, Wincobank, Sheffield, S5 6AB",
    phone: "0114 256 1511",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Sheffield offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 28,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "562",
    name: "Sheil Road Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "L6 3AF",
    address: "74 Sheil Road, Kensington, Liverpool, L6 3AF",
    phone: "0151 263 3088",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 4.1,
    reviews: 31,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "563",
    name: "mydentist Park Road Liverpool",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "L8 4PF",
    address: "Park Dental Centre, 323 Park Road, Toxteth, Liverpool, L8 4PF",
    phone: "0151 727 1301",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-20:00, Sun closed",
    rating: 4.2,
    reviews: 34,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "564",
    name: "mydentist Priory Road Anfield",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "L4 2RT",
    address: "7 Priory Road, Anfield, Liverpool, L4 2RT",
    phone: "0151 260 6414",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 37,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "565",
    name: "mydentist Picton Road Liverpool",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "L15 4LL",
    address: "200 Picton Road, Wavertree, Liverpool, L15 4LL",
    phone: "0151 735 0050",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 40,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "566",
    name: "mydentist Stanley Road Liverpool",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "L20 3EN",
    address: "200 Stanley Road, Bootle, Liverpool, L20 3EN",
    phone: "0151 922 1652",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 43,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "567",
    name: "Menlove Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "L18 1LS",
    address: "1 Menlove Avenue, Liverpool, L18 1LS",
    phone: "0151 733 8449",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 46,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "568",
    name: "Devonshire Park Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "CH42 7LX",
    address: "62 Greenway Road, Birkenhead, Liverpool, CH42 7LX",
    phone: "0151 643 8433",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 49,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "569",
    name: "Woodlands Dental Practice Liverpool",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "CH42 4NG",
    address: "493 Old Chester Road, Rock Ferry, Wirral, CH42 4NG",
    phone: "0151 645 3967",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 52,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "570",
    name: "Church Road Dental Practice Liverpool",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "CH63 3EB",
    address: "66 Church Road, Bebington, Wirral, CH63 3EB",
    phone: "0151 645 4101",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 55,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "571",
    name: "mydentist Crosby Road South",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Liverpool",
    postcode: "L22 1RG",
    address: "Waterloo Dental Centre, 11 Crosby Road South, Liverpool, L22 1RG",
    phone: "0151 928 2420",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Liverpool offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:30, Sat closed, Sun closed",
    rating: 5.0,
    reviews: 58,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "572",
    name: "Your Dentist Ltd",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS1 4AZ",
    address: "1 Telephone Avenue, Marsh Street, Bristol, BS1 4AZ",
    phone: "0117 954 4666",
    acceptingNewPatients: "limited",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 61,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "573",
    name: "East Street Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS3 4EY",
    address: "68/70 East Street, Bedminster, Bristol, BS3 4EY",
    phone: "0117 966 3298",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:30, Sat closed, Sun closed",
    rating: 3.9,
    reviews: 64,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "574",
    name: "mydentist Filton Road Horfield",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS7 0PD",
    address: "94-96 Filton Road, Horfield, Bristol, BS7 0PD",
    phone: "0117 969 2373",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.0,
    reviews: 67,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "575",
    name: "Lodge Causeway Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS16 3PL",
    address: "352-354 Lodge Causeway, Bristol, BS16 3PL",
    phone: "0117 958 6878",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 70,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "576",
    name: "Filton Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS34 7PF",
    address: "114 Gloucester Road North, Filton, Bristol, BS34 7PF",
    phone: "0117 969 2230",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 73,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "577",
    name: "Dentalcare Winterbourne",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS36 1JN",
    address: "16 High Street, Winterbourne, Bristol, BS36 1JN",
    phone: "01454 568989",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-19:00, Sun closed",
    rating: 4.3,
    reviews: 76,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "578",
    name: "Halton House Dental Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS39 7LH",
    address: "Hallatrow Road, Paulton, Bristol, BS39 7LH",
    phone: "01761 411166",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 79,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "579",
    name: "Queen Square Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bath",
    postcode: "BA1 2HA",
    address: "1a Queen Square, Bath, BA1 2HA",
    phone: "01225 424336",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Bath offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.5,
    reviews: 82,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "580",
    name: "Bank Cottage Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bristol",
    postcode: "BS35 1HB",
    address: "14 Castle Street, Thornbury, Bristol, BS35 1HB",
    phone: "01454 412053",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Bristol offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.6,
    reviews: 85,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "581",
    name: "Bathampton Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Bath",
    postcode: "BA2 6UL",
    address: "29 Holcombe Lane, Bathampton, Bath, BA2 6UL",
    phone: "01225 485303",
    acceptingNewPatients: "no",
    description: "NHS dental practice in Bath offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:00, Sat closed, Sun closed",
    rating: 4.7,
    reviews: 88,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "582",
    name: "Mrs L K Cave & Associates",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE1 4PG",
    address: "50 Leazes Park Road, Newcastle Upon Tyne, NE1 4PG",
    phone: "0191 222 0725",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:30-18:30, Sat closed, Sun closed",
    rating: 4.8,
    reviews: 91,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "583",
    name: "Newcastle Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE1 8JQ",
    address: "11 Ridley Place, Newcastle Upon Tyne, NE1 8JQ",
    phone: "0191 232 4284",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.9,
    reviews: 94,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "584",
    name: "Perfect Smile Jesmond",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE2 2DJ",
    address: "44 Acorn Road, Jesmond, Newcastle Upon Tyne, NE2 2DJ",
    phone: "0191 281 4444",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-19:00, Sun closed",
    rating: 5.0,
    reviews: 97,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "585",
    name: "Grainger Park Road Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE4 8DP",
    address: "3 Grainger Park Road, Benwell, Newcastle Upon Tyne, NE4 8DP",
    phone: "0191 273 9635",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 3.8,
    reviews: 100,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "586",
    name: "Mr A A Waugh Dental",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE6 5JT",
    address: "13 Jesmond Vale Terrace, Heaton, Newcastle Upon Tyne, NE6 5JT",
    phone: "0191 265 5414",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Sat 09:00-17:30, Sun closed",
    rating: 3.9,
    reviews: 103,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "587",
    name: "Perfect Smile Gosforth",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE3 4AA",
    address: "67 High Street, Gosforth, Newcastle Upon Tyne, NE3 4AA",
    phone: "0191 213 2001",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Sat 08:00-19:00, Sun closed",
    rating: 4.0,
    reviews: 106,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: false,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "588",
    name: "Bensham Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Gateshead",
    postcode: "NE8 4XE",
    address: "24 Saltwell Road, Gateshead, NE8 4XE",
    phone: "0191 477 1981",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Gateshead offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-18:30, Sat closed, Sun closed",
    rating: 4.1,
    reviews: 109,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "589",
    name: "The Dental Care Clinic Westerhope",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE5 2LH",
    address: "369 Stamfordham Road, Westerhope, Newcastle, NE5 2LH",
    phone: "0191 286 9156",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.2,
    reviews: 112,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "590",
    name: "Crescent Dental Care Dunston",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Gateshead",
    postcode: "NE11 9SJ",
    address: "3/4 The Crescent, Dunston, Gateshead, NE11 9SJ",
    phone: "0191 460 4182",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Gateshead offering general and preventive dental care.",
    openingHours: "Mon-Fri 09:00-17:30, Sat closed, Sun closed",
    rating: 4.3,
    reviews: 115,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "591",
    name: "The Killingworth Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["Check-ups", "Fillings", "Extractions", "Root Canal", "Dental Hygiene", "Crowns & Bridges"],
    borough: "Newcastle",
    postcode: "NE12 6YT",
    address: "Unit 20-21, The Killingworth Centre, Newcastle Upon Tyne, NE12 6YT",
    phone: "0191 268 6502",
    acceptingNewPatients: "yes",
    description: "NHS dental practice in Newcastle offering general and preventive dental care.",
    openingHours: "Mon-Fri 08:00-18:30, Sat closed, Sun closed",
    rating: 4.4,
    reviews: 118,
    distance: 0,
    eveningWeekend: true,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "660",
    name: "NEWHAM FAMILY DENTAL CARE LTD",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "City of London",
    postcode: "EC1M 7AA",
    address: "16-18, Goswell Road, London, EC1M 7AA",
    phone: "020 7253 3232",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "661",
    name: "Malmin Orthodontic Group Ltd",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "City of London",
    postcode: "EC1A 4JQ",
    address: "Malmin Barbican, 131 Aldersgate Street, London, EC1A 4JQ",
    phone: "020 7251 2222",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "662",
    name: "Barbican Orthodontic Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "City of London",
    postcode: "EC1A 9ET",
    address: "84-85 Long Lane, Barbican, London, EC1A 9ET",
    phone: "020 7726 8282",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "663",
    name: "Nile Street Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Islington",
    postcode: "N1 7RD",
    address: "77 Nile Street, Hackney, London, N1 7RD",
    phone: "020 7253 6454",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "664",
    name: "Dent Essentials",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "City of London",
    postcode: "EC2A 3BS",
    address: "75 Curtain Road, Shoreditch, London, EC2A 3BS",
    phone: "020 7613 3388",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "665",
    name: "EC1 Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "City of London",
    postcode: "EC1V 9DS",
    address: "EC1 Dental Centre, 344 Old Street, London, EC1V 9DS",
    phone: "020 7739 2553",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "666",
    name: "Charles Landau Dentistry",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Islington",
    postcode: "N1 8PT",
    address: "Charles Landau Dentistry, 6 Prebend Street, Islington, London, N1 8PT",
    phone: "020 7226 3424",
    acceptingNewPatients: "no",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "667",
    name: "Conduit Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Camden",
    postcode: "WC1N 3NG",
    address: "41 Lambs Conduit Street, Holborn, London, WC1N 3NG",
    phone: "020 7405 4030",
    acceptingNewPatients: "limited",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "668",
    name: "Cosmo Clinic Ltd.",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E2 8AL",
    address: "Cosmo Clinic Ltd, 167-169 Kingsland Road, London, E2 8AL",
    phone: "020 7739 6574",
    acceptingNewPatients: "limited",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "669",
    name: "Alba Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E1 7NE",
    address: "32 Toynbee Street, London, E1 7NE",
    phone: "020 7377 6886",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "670",
    name: "MRS O SULAIMAN",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E1 7NE",
    address: "40/42 Toynbee Street, London, London, London, E1 7NE",
    phone: "020 3216 0077",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "671",
    name: "Pickering Dental Surgeries",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Islington",
    postcode: "N1 1QN",
    address: "103 Upper Street, Islington, London, London, N1 1QN",
    phone: "020 7359 6103",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "672",
    name: "Angle House Orthodontics Islington",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Islington",
    postcode: "N1 0JH",
    address: "51-55 Copenhagen Street, Islington, London, London, N1 0JH",
    phone: "020 3096 2009",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "673",
    name: "Dentalmark",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Islington",
    postcode: "N1 0SQ",
    address: "192 Caledonian Road, London, N1 0SQ",
    phone: "020 7278 1628",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "674",
    name: "AP Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E2 0AA",
    address: "1st Floor, 238-240 Bethnal Green Road, London, E2 0AA",
    phone: "020 7729 7319",
    acceptingNewPatients: "no",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "675",
    name: "Dental Beauty Dalston",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Hackney",
    postcode: "E8 4FD",
    address: "333 Kingsland Road, London, E8 4FD",
    phone: "020 7254 0898",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "676",
    name: "The Whitechapel Dental Centre",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E1 1JE",
    address: "132-134 Whitechapel Road, Whitechapel, London, E1 1JE",
    phone: "020 7247 4600",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "677",
    name: "DentalShine Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Islington",
    postcode: "N1 3PF",
    address: "372 Essex Road, Islington, London, N1 3PF",
    phone: "020 7226 9797",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "678",
    name: "New Road Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E1 1HE",
    address: "29 New Road, London, E1 1HE",
    phone: "020 7247 3737",
    acceptingNewPatients: "no",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "679",
    name: "Care Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E1 2LP",
    address: "44-56 Hessel Street, Stepney, London, E1 2LP",
    phone: "020 7488 0608",
    acceptingNewPatients: "no",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "680",
    name: "E2 Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E2 0AN",
    address: "427A Bethnal Green Road, London, Greater London, E2 0AN",
    phone: "020 7739 7009",
    acceptingNewPatients: "limited",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "681",
    name: "Soho Dental",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Westminster",
    postcode: "W1D 3HZ",
    address: "2nd Floor, 1 Frith Street, Soho Square, London, W1D 3HZ",
    phone: "020 7534 6622",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "682",
    name: "Soho Orthodontic Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Westminster",
    postcode: "W1D 3QS",
    address: "29-30 Soho Square, London, W1D 3QS",
    phone: "020 7434 2626",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "683",
    name: "Leicester Square Dental Clinic",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Camden",
    postcode: "WC2H 7BA",
    address: "24b Lisle Street, London, London, WC2H 7BA",
    phone: "020 7437 8520",
    acceptingNewPatients: "no",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "684",
    name: "Aldgate House Dental Care",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E1 2JT",
    address: "202 Commercial Road, London, E1 2JT",
    phone: "020 7247 5378",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "685",
    name: "Abbey Dental Practice",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E1 1BJ",
    address: "224 Whitechapel Road, Whitechapel, London, E1 1BJ",
    phone: "020 7247 0964",
    acceptingNewPatients: "no",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "686",
    name: "Waterloo Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Southwark",
    postcode: "SE1 8XH",
    address: "223 Waterloo Road, London, SE1 8XH",
    phone: "020 7928 5844",
    acceptingNewPatients: "limited",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "687",
    name: "Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Hackney",
    postcode: "E8 4AR",
    address: "537 Kingsland Road, Hackney, London, E8 4AR",
    phone: "020 7254 7520",
    acceptingNewPatients: "limited",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "688",
    name: "Mornington Dental Surgery",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Camden",
    postcode: "NW1 1TU",
    address: "11 Crowndale Road, Camden, London, NW1 1TU",
    phone: "020 7387 6590",
    acceptingNewPatients: "limited",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "689",
    name: "City Smile",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Hackney",
    postcode: "E8 4RP",
    address: "37-39 Mare Street, London, E8 4RP",
    phone: "020 7739 4275",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },
  {
    id: "690",
    name: "Al Attar & Associates",
    type: "Dentist",
    nhsStatus: "mixed",
    services: ["NHS Dentistry", "Check-ups", "Fillings", "Extractions"],
    borough: "Tower Hamlets",
    postcode: "E2 9PJ",
    address: "55 Old Ford Road, Bethnal Green, London, E2 9PJ",
    phone: "020 8980 3252",
    acceptingNewPatients: "yes",
    description: "NHS dental practice providing comprehensive healthcare services to the local community.",
    openingHours: "Mon-Fri 08:30-18:00, Sat closed, Sun closed",
    rating: 0,
    reviews: 0,
    distance: 0,
    eveningWeekend: false,
    wheelchairAccessible: true,
    privatePay: false,
    outOfAreaRegistrations: false,
    languages: ["English"],
  },

];

// ─── Filter Configuration ───
export const DISTANCE_OPTIONS = [
  { label: "1 mile", value: 1 },
  { label: "3 miles", value: 3 },
  { label: "5 miles", value: 5 },
  { label: "10+ miles", value: 10 },
];

export const PATIENT_STATUS_OPTIONS = [
  { label: "Yes", value: "yes" as AcceptingStatus },
  { label: "Limited", value: "limited" as AcceptingStatus },
  { label: "No", value: "no" as AcceptingStatus },
];

export const SERVICE_OPTIONS = [
  "GP Practice",
  "Dentist",
  "Pharmacy",
];

export const NHS_STATUS_OPTIONS: { label: string; value: NhsStatus }[] = [
  { label: "NHS", value: "nhs" },
  { label: "Private", value: "private" },
  { label: "NHS & Private", value: "mixed" },
];
